/**
 * AIAgentPanel — Wrangle-pattern full-page open-source AI Agent.
 *
 * Layout:
 *
 *   Desktop (no job results):
 *   ┌──────────────────────────────────────────────┐
 *   │ TopBar                                        │
 *   ├──────────────┬───────────────────────────────┤
 *   │ HistorySide  │  Messages / EmptyState         │
 *   │ (persistent) │  Composer                      │
 *   └──────────────┴───────────────────────────────┘
 *
 *   Desktop (job_search action detected — Wrangle 55.png pattern):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ TopBar                                                │
 *   ├──────────────┬────────────────┬──────────────────────┤
 *   │ HistorySide  │  Chat (40%)    │  Job results (60%)   │
 *   │              │  Composer      │  job cards list       │
 *   └──────────────┴────────────────┴──────────────────────┘
 *
 *   Mobile: overlay drawer for history; jobs panel stacks below chat.
 */

import {
  Bookmark,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Copy,
  Database,
  Globe,
  KeyRound,
  Paperclip,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { getAgentResponseLanguage, getDocumentResponseLanguage } from './utils/agentLocale';
import { optimizeUserMessage, startingAnalysisMessage } from './utils/agentWorkflowI18n';
import {
  ASK_OPTIMIZE_JD_REPLY,
  REMIND_OPTIMIZE_JD_REPLY,
  detectExplicitCanvasOpenIntent,
  detectOptimizeResumeIntent,
  lastAssistantAwaitingOptimizeJd,
  looksLikeJobDescription,
  parseJobMetaFromText,
} from './utils/optimizeIntent';
import {
  createdLetterLeadLine,
  draftingApplicationLetterMessage,
  draftingApplicationNoteMessage,
  draftingCoverLetterMessage,
  letterRefusalErrorMessage,
} from './utils/agentEngagementI18n';
import { supabase } from '../../lib/supabase';
import { explainMatch } from '../recommendations/engine';
import type { RecommendationResult } from '../recommendations/types';
import { roleAlignmentScore } from '../recommendations/engine/scoring/dimensionScores';
import { useUserProfile } from '../dashboard/hooks/useUserProfile';
import { useProfileSkills } from '../dashboard/hooks/useProfileSkills';
import { useUserSkills } from '../dashboard/hooks/useUserSkills';
import { getTopJobsForUser } from '../recommendations/engine/recommendationEngine';
import {
  companionPageToPath,
  mapCompanionNavigatePage,
  resolveCompanionAction,
  type CompanionDashboardPage,
} from '../dashboard/utils/companionActions';

import {
  appendMessage,
  classifyCanvasToolCall,
  CANVAS_SECTION_TITLE_MAP,
  canvasSectionDisplayTitle,
  CLOUD_MODEL_PREFIX,
  DEFAULT_AGENT_CLOUD_MODEL,
  formatAdminCloudModelShort,
  formatPublicAgentModelLabel,
  GEMINI_MODEL_PREFIX,
  isCloudGeminiModel,
  migrateCloudModelSelection,
  countMessages,
  createSession,
  deriveSessionTitle,
  isGenericSessionTitle,
  listMessagesBefore,
  listMessages,
  listMessagesRecent,
  probeOllama,
  markOllamaAvailable,
  listRecentSessions,
  performWebSearch,
  renameSession,
  streamCompletion,
  updateMessageContent,
  WEB_MODEL_PREFIX,
  type WebSearchResult,
} from './agentSession';
import {
  getLocalizedStarterPrompt,
  type AgentCapabilityPreset,
} from './capabilityPrompts';
import AgentComposer, { type AgentComposerHandle } from './components/AgentComposer';
import SuggestedPromptChips from './components/SuggestedPromptChips';
import AgentJobsPanel, { type AgentJob, type Collection } from './components/AgentJobsPanel';
import JobDetailDrawer from '../dashboard/components/Jobs/JobDetailDrawer';
import { type Job as JobCardType } from '../dashboard/components/JobCard';
import CompanyLogo from '../../components/CompanyLogo';
import AgentMessage from './components/AgentMessage';
import VideoSuggestions from './components/VideoSuggestions';
import HistorySidebar from './components/HistorySidebar';
import { type DownloadFormat } from './components/OptimizeCanvas';
import InlineOptimizeWidget from './components/InlineOptimizeWidget';
import {
  CanvasProvider,
  useCanvas,
  RightPanelContainer,
  type AgentActivity,
  useAgentStateMachine,
  profileToResumeData,
  resumePlainTextToResumeData,
  type AgentMessage as CanvasAgentMessage,
  type AgentAction,
} from './canvas';
import { waitForCanvasTypewriter } from './canvas/useCanvasTypewriter';
import {
  buildEngagementContent,
  buildCapabilityEngagementActions,
  buildGeneralEngagementActions,
  buildGeneralEngagementFooter,
  buildWorkflowActions,
  getCanvasWorkflowSnapshot,
  hasEngagementFooter,
  UNIVERSAL_ENGAGEMENT_PROMPT,
  type CapabilityId,
  type EngagementOptions,
  type GeneralEngagementHandlers,
} from './canvas/canvasWorkflowFollowUp';
import {
  applySectionReorder,
  buildDummySectionContent,
  buildSectionSeedFromProfile,
  canonicalSectionTitle,
  findSectionIndexByKey,
  parseCanvasSectionKeyFromText,
  parseAnchorSectionKeyFromInstruction,
  parseMovedSectionKeyFromText,
  parsePlacementFromUserText,
  dedupeCanvasSections,
  sanitizeSectionContentForCanvas,
  sectionContentHasPlaceholders,
  wantsDummyOrSampleFill,
  wantsPreserveAndExpandContent,
  employersMissingFromRewrite,
  parseRemoveSectionKeyFromText,
  removeSectionByKey,
  prepareHydratedCanvasSections,
  finalizeAgenticCanvasSections,
  applyOptimizedCanvasSections,
  prepareSectionsForExport,
  normalizeUserCommandTypos,
  buildPersonalSectionContent,
  wantsSectionFormattingEmphasis,
  applyBoldEmphasisToSectionContent,
} from './canvas/canvasSectionUtils';
import { sortSectionsByCanonicalOrder } from '../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import {
  buildJdFromJob,
  resolveJobListingUrl,
  type OptimizeJobContext,
  type OptimizeResult,
} from './agentOptimize';
import { generateLetter, letterLooksLikeSafetyRefusal, buildFallbackLetter, type LetterRequest } from './agentLetters';
import { optimizeSection } from '../../services/sectionOptimizationService';
import { TEMPLATE_LIBRARY } from '../dashboard/components/AIResumeOptimizer/ResultsStep';
import { useAgenticOptimize } from './hooks/useAgenticOptimize';
import { processResumeWithGemini } from '../resume/services/geminiResumeService';
import { canonicalResumeToCanvasSections } from '../resume/adapters/canonicalToCanvas';
import { mergeOptimizedSectionIntoCanonical, canvasSectionsToCanonicalResume } from '../resume/adapters/canvasSectionsToCanonical';
import { resumeDataToCanonical } from '../resume/adapters/resumeDataToCanonical';
import type { CanonicalResume } from '../resume/schemas/resumeSchema';
import { useAppliedJobsMap } from './hooks/useAppliedJobsMap';
import { useApplyReturnNudge } from './hooks/useApplyReturnNudge';
import { buildResumePlainTextFromUserProfile, getBestResumePlainTextForAgent } from '../dashboard/utils/profileResumeForOptimizer';
import { loadExternalCreds } from '../dashboard/components/DeveloperSettings';
import { saveAgentArtifacts } from '../../services/applicationTrackingService';
import { extractResumeText } from '../../utils/extractResumeText';
import { pushUserNotification } from '../../services/objectivesTasksService';
import type { NotificationType } from '../../types/objectivesTasks';
import { safeClipboardWrite } from '../../utils/clipboard';
import { getCurrentUser } from '../../utils/authUtils';
import { isOwnerAdminEmail } from '../dashboard/components/Jobs/helpers';
import { consumeAgentOptimizeHandoff, hasAgentOptimizeHandoff } from '../dashboard/components/Jobs/agentOptimizeHandoff';
import { saveArtifact } from './agentArtifacts';
import { saveComprehensiveOptimization } from '../../services/optimizationService';
import {
  buildUnifiedRecommendedFeedCore,
  jobsToAgentJobs,
  RECOMMENDED_POOL_LIMIT,
} from '../../services/unifiedRecommendedFeedBuilder';
import {
  buildJobMatchingSystemHint,
  buildProfileAwareJobQuery,
  filterJobsByProfileRelevance,
  jobMeetsSeniorityFloor,
  refineAgentJobResults,
  seniorityQueryTokens,
} from './utils/jobProfileMatch';
import {
  AGENT_CHAT_PROCESSING_STEPS,
  JOB_SEARCH_PROCESSING_STEPS,
  mergeStatusSteps,
  RESUME_OPTIMIZE_PROCESSING_STEPS,
  useAgentProcessingSteps,
} from './hooks/useAgentProcessingSteps';
import { extractStatusTags } from './utils/agentStatusTokens';
import { generateResumePDF, generateResumeDOCX, type TemplateId } from '../../utils/resumeDocumentGenerator';
import { normalizeResumeText } from '../../utils/pdfTextSanitizer';
import { generateCompanyBrief } from './agentResearch';
import type {
  AgentHistoryItem,
  AgentMessage as AgentMessageT,
  AgentSession,
} from './types';
import { usePlanStatus } from '../../hooks/usePlanStatus';
import { useCreditSystem } from '../../hooks/useCreditSystem';
import { getCreditCost } from '../../config/creditCosts';
import CreditGateModal from '../../components/CreditSystem/CreditGateModal';
import UtilityCluster from '../shell/components/UtilityCluster';

// ─────────────────────────────────────────────────────────────────────────────
// State types
// ─────────────────────────────────────────────────────────────────────────────

export interface AIAgentPanelProps {
  onSignOut?: () => void | Promise<void>;
  onBackToWorkspace?: () => void;
  /** When true the panel is embedded inside WorkspaceShell — uses h-full,
   *  hides the GGJLogo back-button and Dashboard button (shell handles nav). */
  embedded?: boolean;
  /** AgentHubPage layout: chat-only in main column; history lives in hub sub-nav. */
  hubLayout?: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// thinkingMs per message id map
// ─────────────────────────────────────────────────────────────────────────────
type ThinkingMap = Record<string, number>;
type ModelMap    = Record<string, string>;  // messageId → model label

interface WorkspaceLetterArtifact {
  mode: 'cover' | 'application';
  title: string;
  subtitle: string;
  body: string;
}

const AGENT_PLACEHOLDER_KEYS = [
  'agent.composer.placeholder1',
  'agent.composer.placeholder2',
  'agent.composer.placeholder3',
  'agent.composer.placeholder4',
  'agent.composer.placeholder5',
] as const;

const AGENT_PLACEHOLDER_FALLBACKS = [
  'What kind of jobs are you looking for?',
  'Paste a job URL to optimize my resume…',
  'Find roles that match my skills and experience…',
  'Help me prep for an upcoming interview…',
  'Draft a cover letter for a role I am targeting…',
];

const AGENT_SEARCH_SOURCE_KEY = 'ggj:agent:search-source';
const AGENT_DEEP_SEARCH_KEY = 'ggj:agent:deep-search';
const AGENT_LAST_SESSION_KEY = 'ggj:agent:last-session';

function spaNavigate(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function executeAgentNavigation(
  page: CompanionDashboardPage,
  opts?: { agentPrompt?: string; delayMs?: number },
) {
  if (opts?.agentPrompt) {
    try {
      sessionStorage.setItem('agent_pending_prompt', opts.agentPrompt);
    } catch { /* noop */ }
  }
  const path = companionPageToPath(page);
  window.setTimeout(() => spaNavigate(path), opts?.delayMs ?? 450);
}

/** Pages that mean "open agent resume canvas", not leave /agent. */
function isAgentCanvasPage(page: string): boolean {
  const p = page.trim().toLowerCase();
  return p === 'agent' || p === 'single-optimize' || p === 'resume-builder';
}

function persistLastSessionId(sessionId: string) {
  try {
    localStorage.setItem(AGENT_LAST_SESSION_KEY, sessionId);
  } catch { /* noop */ }
}

function isProfileConfigured(
  p: { currentRole?: string | null; skills?: string[] | null; headline?: string | null } | null | undefined,
): boolean {
  if (!p) return false;
  return Boolean(
    p.currentRole?.trim() ||
    (p.skills && p.skills.length > 0) ||
    p.headline?.trim(),
  );
}

/** Profile review/assessment — stay in agent chat; never redirect to settings. */
function isProfileAssessmentIntent(userText: string): boolean {
  const t = userText.toLowerCase().trim();
  return (
    /\b(about|assess|assessment|review|analyze|analyse|evaluate|tell me about)\b.{0,48}\b(profile|my background|my experience)\b/.test(t)
    || /\b(profile|my background)\b.{0,48}\b(strengths?|gaps?|assessment|review|focus on next|market positioning)\b/.test(t)
    || /\b(profile assessment|assess my profile|about my profile|detailed assessment of my profile)\b/.test(t)
    || /\bgive me\b.{0,60}\b(profile|my background)\b/.test(t)
  );
}

function readAgentSearchSource(): 'internal' | 'external' {
  try {
    return localStorage.getItem(AGENT_SEARCH_SOURCE_KEY) === 'external' ? 'external' : 'internal';
  } catch {
    return 'internal';
  }
}

function readAgentDeepSearch(): boolean {
  try {
    return localStorage.getItem(AGENT_DEEP_SEARCH_KEY) === '1';
  } catch {
    return false;
  }
}

function persistAgentSearchPrefs(source: 'internal' | 'external', deep: boolean) {
  try {
    localStorage.setItem(AGENT_SEARCH_SOURCE_KEY, source);
    localStorage.setItem(AGENT_DEEP_SEARCH_KEY, deep ? '1' : '0');
  } catch {
    /* noop */
  }
}

function sessionSearchPrefsKey(sessionId: string) {
  return `ggj:agent:sq:${sessionId}`;
}

function persistSessionSearchPrefs(
  sessionId: string,
  source: 'internal' | 'external',
  deep: boolean,
) {
  persistAgentSearchPrefs(source, deep);
  try {
    localStorage.setItem(sessionSearchPrefsKey(sessionId), JSON.stringify({ source, deep }));
  } catch {
    /* noop */
  }
}

function getPersistedSessionSearchPrefs(
  sessionId: string,
): { source: 'internal' | 'external'; deep: boolean } | null {
  try {
    const raw = localStorage.getItem(sessionSearchPrefsKey(sessionId));
    if (!raw) return null;
    const p = JSON.parse(raw) as { source?: string; deep?: boolean };
    return {
      source: p.source === 'external' ? 'external' : 'internal',
      deep: Boolean(p.deep),
    };
  } catch {
    return null;
  }
}

function sessionJobsPanelKey(sessionId: string) {
  return `ggj:agent:jobs-panel:${sessionId}`;
}

function persistSessionJobsPanelOpen(sessionId: string, open: boolean) {
  try {
    if (open) localStorage.setItem(sessionJobsPanelKey(sessionId), '1');
    else localStorage.removeItem(sessionJobsPanelKey(sessionId));
  } catch {
    /* noop */
  }
}

function getPersistedJobsPanelOpen(sessionId: string): boolean {
  try {
    return localStorage.getItem(sessionJobsPanelKey(sessionId)) === '1';
  } catch {
    return false;
  }
}

/** True when chat history references the jobs side panel (used to restore after remount). */
function messagesIndicateJobPanel(messages: Array<{ role: string; content: string }>): boolean {
  return messages.some(
    (m) =>
      m.role === 'assistant' &&
      (/panel on the right/i.test(m.content) ||
        /Results are shown in the panel/i.test(m.content) ||
        /Found \*\*\d+\*\* matching jobs/i.test(m.content) ||
        /Searching for matching jobs/i.test(m.content)),
  );
}

const AIAgentPanel: React.FC<AIAgentPanelProps> = ({
  onSignOut,
  onBackToWorkspace,
  embedded = false,
  hubLayout = false,
}) => {
  const { t, i18n } = useTranslation();
  const planStatus = usePlanStatus();
  const creditSystem = useCreditSystem();
  const { profile, save: saveProfile } = useUserProfile();

  // ── Credit gate modal state ────────────────────────────────────────────────
  const [creditGate, setCreditGate] = useState<{
    open: boolean;
    action: string;
    cost: number;
  }>({ open: false, action: '', cost: 0 });

  const openCreditGate = useCallback((action: string, cost: number) => {
    setCreditGate({ open: true, action, cost });
  }, []);

  const closeCreditGate = useCallback(() => {
    setCreditGate({ open: false, action: '', cost: 0 });
  }, []);

  const handleGetCredits = useCallback(() => {
    closeCreditGate();
    window.location.href = '/checkout';
  }, [closeCreditGate]);

  const [isSignedIn, setIsSignedIn] = useState(false);
  const [isOwnerAdmin, setIsOwnerAdmin] = useState(false);
  const [authEmail, setAuthEmail] = useState<string | undefined>(undefined);
  const [authPhone, setAuthPhone] = useState<string | undefined>(undefined);

  const placeholderPrompts = useMemo(
    () => AGENT_PLACEHOLDER_KEYS.map((key, i) => t(key, { defaultValue: AGENT_PLACEHOLDER_FALLBACKS[i] })),
    [t],
  );

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!cancelled) {
        setIsSignedIn(!!session);
        setAuthEmail(session?.user?.email ?? undefined);
        setAuthPhone(session?.user?.phone ?? undefined);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const { ctx: resumeCtx, hasResume, loading: resumeSkillsLoading } = useUserSkills(isSignedIn);
  const {
    ctx: profileCtx,
    hasProfile,
    experienceYears: profileExperienceYears,
    currentJobTitle: profileSkillsJobTitle,
    loading: profileSkillsLoading,
  } = useProfileSkills(isSignedIn);

  const userCtx = useMemo(() => {
    if (!resumeCtx && !profileCtx) return null;
    const base = !resumeCtx ? profileCtx
      : !profileCtx ? resumeCtx
      : {
          skills: new Set([...resumeCtx.skills, ...profileCtx.skills]),
          titles: new Set([...resumeCtx.titles, ...profileCtx.titles]),
        };
    const headline = profile?.currentRole ?? profileSkillsJobTitle ?? undefined;
    return base ? { ...base, experienceYears: profileExperienceYears, headline } : null;
  }, [resumeCtx, profileCtx, profileExperienceYears, profile?.currentRole, profileSkillsJobTitle]);

  const hasSkillContext = hasResume || hasProfile;
  const skillContextReady = isSignedIn && !profileSkillsLoading && !resumeSkillsLoading;

  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then((u) => {
        if (!cancelled) setIsOwnerAdmin(isOwnerAdminEmail(u?.email));
      })
      .catch(() => {
        if (!cancelled) setIsOwnerAdmin(false);
      });
    return () => { cancelled = true; };
  }, []);

  const firstName = useMemo(
    () => (profile?.displayName?.trim() || profile?.email?.split('@')[0] || 'there').split(' ')[0],
    [profile?.displayName, profile?.email],
  );

  /** Rich user context injected verbatim at the top of every Ollama/Groq system prompt. */
  const userContext = useMemo(() => {
    const lines: string[] = ['=== LOGGED-IN USER PROFILE ==='];
    if (!profile) {
      lines.push('Profile: Not loaded yet — ask the user what kind of jobs they are looking for.');
      lines.push('=== END OF USER PROFILE ===');
      return lines.join('\n');
    }
    if (profile.displayName)          lines.push(`Name: ${profile.displayName}`);
    if (profile.email)                lines.push(`Email: ${profile.email}`);
    if (profile.headline)             lines.push(`Headline: ${profile.headline}`);
    if (profile.currentRole)          lines.push(`Current Role: ${profile.currentRole}`);
    if (profile.experienceYears != null) lines.push(`Total Years of Experience (across ALL roles, not just current): ${profile.experienceYears}`);
    if (profile.industry)             lines.push(`Industry: ${profile.industry}`);
    if (profile.location)             lines.push(`Location: ${profile.location}`);
    if (profile.skills?.length)       lines.push(`Skills: ${profile.skills.join(', ')}`);
    if (profile.preferredJobTypes?.length) lines.push(`Preferred Job Types: ${profile.preferredJobTypes.join(', ')}`);
    if (profile.salaryExpectationInr) lines.push(`Salary Expectation (INR): ${profile.salaryExpectationInr.toLocaleString('en-IN')}`);
    if (profile.jobSearchStatus)      lines.push(`Job Search Status: ${profile.jobSearchStatus}`);
    if (profile.linkedinUrl)          lines.push(`LinkedIn: ${profile.linkedinUrl}`);
    if (profile.githubUrl)            lines.push(`GitHub: ${profile.githubUrl}`);
    if (profile.portfolioUrl)         lines.push(`Portfolio: ${profile.portfolioUrl}`);

    const isProfileThin = !profile.currentRole && !profile.skills?.length && !profile.headline;
    if (isProfileThin) {
      lines.push('Note: Profile is incomplete — ask what kind of jobs they are looking for and use that as the search query.');
    } else if (!profile.onboardingCompleted) {
      lines.push('Note: Onboarding not yet completed — encourage them to finish their profile for better matches.');
    }
    lines.push('=== END OF USER PROFILE ===');
    return lines.join('\n');
  }, [profile]);

  // ── Proactively seed the Pinecone profile vector ──────────────────────────
  // `upsert-profile-vector` is only called on onboarding/resume-optimization
  // by default. Users who skip those flows have no Pinecone vector, causing
  // `match-jobs-for-user` to always return `no_profile_vector`. We auto-seed
  // here with a 1-hour localStorage cooldown so it runs once per session.
  useEffect(() => {
    if (!profile?.id) return;
    const KEY = `ggj_pvec_ts_${profile.id}`;
    const lastUpsert = parseInt(localStorage.getItem(KEY) ?? '0', 10);
    if (Date.now() - lastUpsert < 60 * 60 * 1_000) return;

    supabase.auth.getSession().then(({ data: { session: authSession } }) => {
      if (!authSession?.access_token) return;
      void supabase.functions.invoke('upsert-profile-vector', {
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      })
        .then(({ error }) => {
          if (error) {
            console.warn('[ai-agent] upsert-profile-vector failed:', error.message);
          } else {
            localStorage.setItem(KEY, String(Date.now()));
            console.info('[ai-agent] profile vector seeded for', profile.id.slice(0, 8));
          }
        })
        .catch(() => {});
    }).catch(() => {});
  }, [profile?.id]);

  // ── Pinecone profile vector metadata (TASK A-5) ──────────────────────────
  // Non-blocking fetch of the user's Pinecone profile-vector metadata.
  // Provides enrichment for profile assessment: skills the recommendation
  // engine actually sees, last update time, and any skill drift detection.
  const [pineconeMeta, setPineconeMeta] = useState<Record<string, unknown> | null>(null);
  useEffect(() => {
    if (!profile?.id) return;
    supabase.functions.invoke('ggj_b2c_pinecone', {
      body: { action: 'fetch', index: 'profiles', namespace: 'b2c-default', id: profile.id },
    }).then(({ data }) => {
      if (data && typeof data === 'object' && 'metadata' in data) {
        setPineconeMeta((data as { metadata: Record<string, unknown> }).metadata ?? null);
      }
    }).catch(() => { /* fire-and-forget — vector metadata is optional */ });
  }, [profile?.id]);

  /** Build profile vector context block for TASK A-5 */
  const profileVectorContext = useMemo(() => {
    if (!pineconeMeta) return '';
    const lines: string[] = ['=== PROFILE VECTOR (what the recommendation engine sees) ==='];

    const pcSkills = pineconeMeta.skills;
    if (Array.isArray(pcSkills) && pcSkills.length > 0) {
      lines.push(`Indexed Skills: ${pcSkills.join(', ')}`);
    }

    const expYears = pineconeMeta.experience_years;
    if (typeof expYears === 'number') {
      lines.push(`Experience Years (indexed): ${expYears}`);
    }

    const industry = pineconeMeta.industry;
    if (typeof industry === 'string' && industry) {
      lines.push(`Industry (indexed): ${industry}`);
    }

    const location = pineconeMeta.location;
    if (typeof location === 'string' && location) {
      lines.push(`Location (indexed): ${location}`);
    }

    const updatedAt = pineconeMeta.updated_at;
    if (typeof updatedAt === 'string' && updatedAt) {
      const updatedDate = new Date(updatedAt);
      const daysSinceUpdate = Math.floor((Date.now() - updatedDate.getTime()) / (1000 * 60 * 60 * 24));
      lines.push(`Vector last updated: ${updatedDate.toLocaleDateString('en-IN')} (${daysSinceUpdate} days ago)`);

      if (daysSinceUpdate > 14) {
        lines.push('Profile vector is stale (>14 days). Recommend user re-run a resume optimization to refresh.');
      }
    }

    // Detect skill drift: compare DB skills vs indexed skills
    if (profile?.skills?.length && Array.isArray(pcSkills)) {
      const dbSkillsLower = new Set(profile.skills.map(s => s.toLowerCase()));
      const pcSkillsLower = new Set(pcSkills.map((s: unknown) => typeof s === 'string' ? s.toLowerCase() : ''));
      const inDbNotPc = profile.skills.filter(s => !pcSkillsLower.has(s.toLowerCase()));
      if (inDbNotPc.length > 0) {
        lines.push(`Skills in DB but NOT in vector index: ${inDbNotPc.join(', ')} — consider re-indexing.`);
      }
    }

    lines.push('=== END PROFILE VECTOR ===');
    return lines.length > 2 ? lines.join('\n') : '';
  }, [pineconeMeta, profile?.skills]);

  /** Recent user activity fetched once on mount — enriches agent context. */
  const [recentActivity, setRecentActivity] = useState<string>('');
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!s?.user?.id) return;
      const uid = s.user.id;

      const optRes = await supabase
        .from('llm_optimization_level2')
        .select('created_at, match_score', { count: 'exact' })
        .eq('user_id', uid)
        .order('created_at', { ascending: false })
        .limit(3);

      const actLines: string[] = ['=== RECENT ACTIVITY ==='];

      if (!optRes.error) {
        const count = optRes.count ?? optRes.data?.length ?? 0;
        actLines.push(`Resume Optimizations: ${count} total`);
        if (optRes.data?.[0]) {
          const last = optRes.data[0];
          actLines.push(
            `Last Optimization: ${new Date(last.created_at).toLocaleDateString('en-IN')} — match score ${last.match_score ?? '?'}%`,
          );
        }
      }

      actLines.push('=== END RECENT ACTIVITY ===');
      if (actLines.length > 2) setRecentActivity(actLines.join('\n'));
    });
  // Run once on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeModel,      setActiveModel]      = useState<{ label: string; backend: 'ollama' | 'gemini' | 'groq' } | null>(null);
  const [session,          setSession]          = useState<AgentSession | null>(null);
  const [bootstrapError,   setBootstrapError]   = useState(false);
  const [messages,         setMessages]         = useState<AgentMessageT[]>([]);
  const [composerValue,    setComposerValue]    = useState('');
  const [isStreaming,      setIsStreaming]      = useState(false);
  const [optimizingMsgId, setOptimizingMsgId]  = useState<string | null>(null);
  const [processingMsgId, setProcessingMsgId]  = useState<string | null>(null);
  const [streamProcessingMsgId, setStreamProcessingMsgId] = useState<string | null>(null);
  const [streamStatusLabels, setStreamStatusLabels] = useState<string[]>([]);
  const [isLoadingMsgs,    setIsLoadingMsgs]    = useState(false);
  const [olderMsgsExist,   setOlderMsgsExist]   = useState(false);
  const [isLoadingOlder,   setIsLoadingOlder]   = useState(false);
  const [historyKey,       setHistoryKey]       = useState(0);
  const [activeCapability, setActiveCapability] = useState<AgentCapabilityPreset | null>(null);
  const [historyOpen,      setHistoryOpen]      = useState(!hubLayout);
  const [draftTitle,       setDraftTitle]       = useState('');
  const [titleEditing,     setTitleEditing]     = useState(false);

  // ── Collections (persisted to localStorage) ──
  const [collections,        setCollections]        = useState<Collection[]>(() => {
    try { return JSON.parse(localStorage.getItem('ggj_job_collections') ?? '[]'); } catch { return []; }
  });
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);

  const handleCollectionsChange = useCallback((next: Collection[]) => {
    setCollections(next);
    try { localStorage.setItem('ggj_job_collections', JSON.stringify(next)); } catch { /* ignore */ }
  }, []);
  const [isDesktop,        setIsDesktop]        = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024,
  );

  // Job results panel
  const [jobResults,     setJobResults]     = useState<AgentJob[] | null>(null);
  const [jobRecs,        setJobRecs]        = useState<Map<string, RecommendationResult>>(new Map());
  const [jobsLoading,    setJobsLoading]    = useState(false);
  const [jobQuery,       setJobQuery]       = useState<string>('');
  const [channelFetchPhase, setChannelFetchPhase] = useState<'idle' | 'channels' | 'scoring' | 'done'>('idle');

  // Track the most recently opened job in the detail drawer — used to inject
  // context for match_explain (TASK A-10).
  const [focusedJobId,   setFocusedJobId]   = useState<string | null>(null);

  // Job opened from chat links (agent:open-job event) — rendered in a standalone drawer
  const [chatSelectedJob, setChatSelectedJob] = useState<JobCardType | null>(null);

  // Deep search toggle (broader multi-field search)
  const [deepSearch, setDeepSearch] = useState(readAgentDeepSearch);
  // Unified search — always 'internal' (searches all sources simultaneously)
  const searchSource: 'internal' | 'external' = 'internal';
  const setSearchSource = (_v: 'internal' | 'external') => { /* no-op: unified search */ };

  // ── Lifted toolbar state (shared with AgentJobsPanel) ──────────────
  const [jobViewMode,   setJobViewMode]   = useState<'grid' | 'list'>('grid');
  const [shortlisted,   setShortlisted]   = useState<Set<string>>(new Set());
  const [chatVisible,   setChatVisible]   = useState(true);
  const [jobsPanelVisible, setJobsPanelVisible] = useState(false);
  const [mobileSplitTab, setMobileSplitTab] = useState<'chat' | 'jobs'>('chat');
  const [mobileAgentFollowUp, setMobileAgentFollowUp] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);

  // ── Focus & Auto-Scroll Controller Rule ──────────────────────────────────
  useEffect(() => {
    const handleCanvasFocus = () => {
      if (window.innerWidth < 1024) {
        setMobileSplitTab('jobs');
      }
      setJobsPanelVisible(true);
      requestAnimationFrame(() => {
        const root = document.querySelector('[data-agent-canvas-root]');
        root?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    };
    window.addEventListener('ggj:canvas-focus', handleCanvasFocus);
    return () => window.removeEventListener('ggj:canvas-focus', handleCanvasFocus);
  }, []);
  // Per-message thinking time and reasoning
  const [thinkingMap,    setThinkingMap]    = useState<ThinkingMap>({});
  const [modelMap,       setModelMap]       = useState<ModelMap>({});
  const [thinkingContent, setThinkingContent] = useState<Record<string, string>>({});

  const chatProcessingSteps = useAgentProcessingSteps(
    isStreaming && !!streamProcessingMsgId,
    AGENT_CHAT_PROCESSING_STEPS,
    950,
  );
  const jobSearchProcessingSteps = useAgentProcessingSteps(jobsLoading, JOB_SEARCH_PROCESSING_STEPS, 900);

  // Model selector
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [selectedModel,   setSelectedModel]   = useState<string | null>(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const modelPickerRef = useRef<HTMLDivElement>(null);

  // Web search results panel
  const [webResults,      setWebResults]     = useState<WebSearchResult[] | null>(null);
  const [webSearchQuery,  setWebSearchQuery] = useState<string>('');
  const [webLoading,      setWebLoading]     = useState(false);

  // Letter generation (cover letter / application letter)
  const [isGeneratingLetter, setIsGeneratingLetter] = useState(false);
  const [generatingLetterType, setGeneratingLetterType] = useState<'cover' | 'application'>('cover');
  const [workspaceLetter, setWorkspaceLetter] = useState<WorkspaceLetterArtifact | null>(null);
  const letterAbortRef = useRef<AbortController | null>(null);

  // Canvas panel: when active, takes priority over jobs/web panels (ChatGPT-style swap)
  const [canvasJob, setCanvasJob]           = useState<OptimizeJobContext | null>(null);
  const [canvasResume, setCanvasResume]     = useState<string>('');
  const [canvasTemplate, setCanvasTemplate] = useState<string>('clean');
  const [canvasSaving,  setCanvasSaving]    = useState(false);
  const [canvasSaved,   setCanvasSaved]     = useState(false);
  const [canvasSaveErr, setCanvasSaveErr]   = useState<string | null>(null);
  const [canvasApplied, setCanvasApplied]   = useState(false);
  const optimizationIdRef = useRef<string | null>(null);

  // ── Assisted Handoff: return-nudge for Apply ─────────────────────────────
  // The Apply button no longer marks the row as 'applied' immediately.
  // Instead it saves as 'in_progress', opens the portal, then this hook
  // waits for the user to come back (≥30s dwell on the portal tab) and
  // prompts them: "Did you finish applying?" — converting that into a
  // final 'applied' / 'not_applied' status update.
  const returnNudge = useApplyReturnNudge();

  /** All Agent feedback goes to the bell tray (no floating toasts — avoids header overlap). */
  const pushAgentBellNotification = useCallback(
    async (
      message: string,
      type: Extract<
        NotificationType,
        'apply_handoff' | 'apply_confirm_needed' | 'apply_recorded' | 'agent_info'
      > = 'agent_info',
      entityId?: string | null,
    ) => {
      try {
        const user = await getCurrentUser();
        if (!user?.id) return;
        await pushUserNotification(user.id, {
          type,
          entity_type: 'job_application',
          entity_id: entityId ?? null,
          message,
        });
      } catch (err) {
        console.warn('[ai-agent] pushAgentBellNotification failed:', err);
      }
    },
    [],
  );

  // Per-section agentic optimization driver
  const agentic = useAgenticOptimize();
  const resumeOptimizeProcessingSteps = useAgentProcessingSteps(
    agentic.phase !== 'idle' && agentic.phase !== 'done' && agentic.phase !== 'error',
    RESUME_OPTIMIZE_PROCESSING_STEPS,
    1000,
  );

  // Applied-jobs map — used by handleOptimizeForJob to detect duplicates.
  // We mirror the lookup function into a ref so callbacks don't need a
  // dependency on the (often-changing) lookup identity.
  const { lookup: appliedJobsLookup } = useAppliedJobsMap();
  const appliedJobsLookupRef = useRef(appliedJobsLookup);
  useEffect(() => { appliedJobsLookupRef.current = appliedJobsLookup; }, [appliedJobsLookup]);

  const canvasOpen = canvasJob !== null;

  // Canvas context for the new agentic flow
  const canvas = useCanvas();
  const canvasRef = useRef(canvas);
  canvasRef.current = canvas;

  // Agent state machine for 7-step optimization
  const agentStateMachine = useAgentStateMachine({
    onMessage: (msg: CanvasAgentMessage) => {
      // Use the always-current ref so this never drops messages due to a stale
      // `session` closure captured at mount time (session is set asynchronously
      // during bootstrap, long after this callback is first created).
      const currentSession = sessionRef.current;
      if (!currentSession) return;
      const newMsg: AgentMessageT = {
        id: msg.id,
        sessionId: currentSession.id,
        role: msg.role,
        content: msg.content,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, newMsg]);
      // Store actions for this message
      if (msg.actions) {
        setMessageActions((prev) => ({ ...prev, [msg.id]: msg.actions! }));
      }
    },
  });

  // Store action buttons per message ID
  const [messageActions, setMessageActions] = useState<Record<string, AgentAction[]>>({});
  // Store custom widget (e.g. job mini-cards) per message ID
  const [messageWidgets, setMessageWidgets] = useState<Record<string, React.ReactNode>>({});

  const handleLetterActionRef = useRef<
    (mode: 'cover' | 'application', data: { jobTitle?: string; company?: string; tone?: string; referredBy?: string }, replyId: string) => Promise<void>
  >(async () => {});
  const handleSubmitRef = useRef<(text: string) => Promise<void>>(async () => {});

  const composerRef   = useRef<AgentComposerHandle | null>(null);
  const abortRef      = useRef<AbortController | null>(null);
  const scrollRef     = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const scrollPositionsRef = useRef<Map<string, number>>(new Map());
  const pendingScrollRestoreRef = useRef<string | null>(null);
  const userPinnedToBottomRef = useRef(true);
  const isStreamingRef = useRef(false);

  // Chat accordion — collapsed hides message history, shows only composer
  const [chatCollapsed, setChatCollapsed] = useState(false);
  // Always up-to-date ref for session — prevents stale closures in async functions
  const sessionRef    = useRef<AgentSession | null>(null);
  const splitRef      = useRef<HTMLDivElement | null>(null);
  const isDragging    = useRef(false);
  // Always points to the latest fetchJobResults — prevents stale closure in memoized callbacks.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fetchJobResultsRef = useRef<(query: string, source?: 'internal' | 'external') => Promise<void>>(null as any);
  const jobResultsRef = useRef(jobResults);
  jobResultsRef.current = jobResults;
  const jobRecsRef = useRef(jobRecs);
  jobRecsRef.current = jobRecs;
  const handleAgentOptimizeFromJobsRef = useRef<(job: AgentJob) => void>(() => {});
  /** Bumps on each fetch; stale responses must not overwrite newer results. */
  const jobFetchGenRef = useRef(0);
  /** Dedupe concurrent identical fetches — callers await the same promise. */
  const jobFetchInflightRef = useRef<{ key: string; promise: Promise<void> } | null>(null);

  const [chatWidthPct, setChatWidthPct] = useState(45);
  const onDividerMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      if (!isDragging.current || !splitRef.current) return;
      const rect = splitRef.current.getBoundingClientRect();
      const pct  = ((ev.clientX - rect.left) / rect.width) * 100;
      setChatWidthPct(Math.min(62, Math.max(34, pct)));
    };
    const onUp = () => {
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // null = probe still running; true = OK; false = offline; 'cors' = running but CORS blocked
  const [ollamaReachable, setOllamaReachable] = useState<boolean | 'cors' | null>(null);

  // Non-admin: cloud by default; when VITE_USE_OLLAMA=true, probe and use local model.
  useEffect(() => {
    if (isOwnerAdmin) return;

    const useOllama = (import.meta.env.VITE_USE_OLLAMA as string | undefined) === 'true';
    if (!useOllama) {
      setSelectedModel(DEFAULT_AGENT_CLOUD_MODEL);
      setActiveModel({
        label: formatPublicAgentModelLabel(DEFAULT_AGENT_CLOUD_MODEL),
        backend: 'gemini',
      });
      return;
    }

    (async () => {
      const probe = await probeOllama();
      if (probe.status !== 'ok') {
        setOllamaReachable(probe.status === 'cors_blocked' ? 'cors' : false);
        setSelectedModel(DEFAULT_AGENT_CLOUD_MODEL);
        setActiveModel({
          label: formatPublicAgentModelLabel(DEFAULT_AGENT_CLOUD_MODEL),
          backend: 'gemini',
        });
        return;
      }

      setOllamaReachable(true);
      const preferred = import.meta.env.VITE_OLLAMA_MODEL ?? 'qwen2.5:latest';
      const base = preferred.split(':')[0];
      const matched =
        probe.models.find((n) => n === preferred) ??
        probe.models.find((n) => n.startsWith(base)) ??
        probe.models.find((n) => n.startsWith('llama3.2')) ??
        probe.models[0] ??
        preferred;

      markOllamaAvailable(true);
      setSelectedModel(matched);
      setActiveModel({ label: 'Local AI', backend: 'ollama' });
    })();
  }, [isOwnerAdmin]);

  // Probe Ollama on mount — two-phase: GET /api/tags (model list) + POST /api/chat (CORS check)
  useEffect(() => {
    if (!isOwnerAdmin) return;

    // Production never talks to local Ollama — it runs entirely on cloud AI.
    // Probing http://localhost:11434 from https://www.gograbjob.com only yields
    // CORS/connection errors in the admin console, so skip it outside dev.
    if (!import.meta.env.DEV) {
      setOllamaReachable(false);
      setActiveModel({
        label: formatPublicAgentModelLabel(DEFAULT_AGENT_CLOUD_MODEL),
        backend: 'gemini',
      });
      setSelectedModel((prev) => {
        if (prev && (isCloudGeminiModel(prev) || prev.startsWith(WEB_MODEL_PREFIX))) {
          return migrateCloudModelSelection(prev);
        }
        return prev ?? DEFAULT_AGENT_CLOUD_MODEL;
      });
      return;
    }

    (async () => {
      const probe = await probeOllama();

      if (probe.status === 'cors_blocked') {
        // Server is running but CORS not configured — show amber + specific fix message
        setOllamaReachable('cors');
        setAvailableModels([...FALLBACK_OLLAMA_MODELS]);
        setActiveModel({ label: FALLBACK_OLLAMA_MODELS[0], backend: 'gemini' });
        return;
      }

      const reachable = probe.status === 'ok';
      setOllamaReachable(reachable);

      const names  = probe.models;
      const models = reachable && names.length > 0 ? names : [...FALLBACK_OLLAMA_MODELS];
      setAvailableModels(models);

      const preferred = import.meta.env.VITE_OLLAMA_MODEL ?? 'qwen2.5:latest';
      const base      = preferred.split(':')[0];
      const matched   = models.find((n) => n.startsWith(base))
                     ?? models.find((n) => n.startsWith('llama3.2'))
                     ?? models[0]
                     ?? preferred;
      setActiveModel({ label: matched, backend: reachable ? 'ollama' : 'gemini' });
      setSelectedModel((prev) => {
        if (prev && (isCloudGeminiModel(prev) || prev.startsWith(WEB_MODEL_PREFIX))) {
          return migrateCloudModelSelection(prev);
        }
        return DEFAULT_AGENT_CLOUD_MODEL;
      });
    })();
  }, [isOwnerAdmin]);

  // Close model picker on outside click
  useEffect(() => {
    if (!modelPickerOpen) return;
    const h = (e: MouseEvent) => {
      if (modelPickerRef.current && !modelPickerRef.current.contains(e.target as Node)) setModelPickerOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [modelPickerOpen]);

  // Bootstrap — restore the user's last chat when they return; otherwise start fresh.
  const loadSessionRef = useRef<(s: AgentSession) => Promise<void>>(async () => {});

  const runBootstrap = useCallback(async () => {
    setBootstrapError(false);
    const fromJobsHandoff = hasAgentOptimizeHandoff();
    try {
    setJobResults(null);
    setJobRecs(new Map());
    setShortlisted(new Set());
    setJobsPanelVisible(false);
    setWebResults(null);
    setWebSearchQuery('');
    if (!fromJobsHandoff) {
        setCanvasJob(null);
      }
      setThinkingContent({});
      setComposerValue('');
      setActiveCapability(null);

      // Jobs → Optimize & Apply: start a clean session (no "recommended jobs" restore).
      if (fromJobsHandoff) {
        canvas.dispatch({ type: 'RESET_CANVAS' });
        const fresh = await createSession();
        setSession(fresh);
        setMessages([]);
        setHistoryKey((k) => k + 1);
        persistLastSessionId(fresh.id);
        window.dispatchEvent(new CustomEvent('ggj:agent-session-changed', { detail: { sessionId: fresh.id } }));
        window.dispatchEvent(new CustomEvent('ggj:agent-history-refresh'));
        return;
      }

      // Always start with a fresh session (clean slate).
      // Previous sessions are accessible via the history sidebar.
      canvas.dispatch({ type: 'RESET_CANVAS' });
      const fresh = await createSession();
      setSession(fresh);
      setMessages([]);
      setHistoryKey((k) => k + 1);
      persistLastSessionId(fresh.id);
      window.dispatchEvent(new CustomEvent('ggj:agent-session-changed', { detail: { sessionId: fresh.id } }));
      window.dispatchEvent(new CustomEvent('ggj:agent-history-refresh'));
    } catch (e) {
      console.error('[ai-agent] bootstrap failed:', e);
      setBootstrapError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void runBootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollChatToBottom = useCallback((opts?: { force?: boolean; behavior?: ScrollBehavior }) => {
    const el = scrollRef.current;
    if (!el) return;
    const force = opts?.force ?? false;
    if (!force && !userPinnedToBottomRef.current && !isStreamingRef.current) return;

    const run = () => {
      const top = el.scrollHeight;
      if (opts?.behavior === 'smooth') {
        el.scrollTo({ top, behavior: 'smooth' });
      } else {
        el.scrollTop = top;
      }
    };
    requestAnimationFrame(run);
  }, []);

  useEffect(() => {
    setDraftTitle(session?.title?.trim() ?? '');
    setTitleEditing(false);
  }, [session?.id, session?.title]);

  const sessionDisplayTitle = useMemo(() => {
    if (!isGenericSessionTitle(session?.title)) return session!.title!.trim();
    const firstUserMessage = messages.find((m) => m.role === 'user')?.content ?? '';
    return firstUserMessage ? deriveSessionTitle(firstUserMessage) : 'New search';
  }, [messages, session?.title]);

  // Broadcast static breadcrumb to WorkspaceShell (never raw message text)
  useEffect(() => {
    if (!embedded) return;
    window.dispatchEvent(new CustomEvent('ggj:agent-breadcrumb', { detail: { title: '' } }));
  }, [embedded]);

  // Clear breadcrumb on unmount (navigating away from agent)
  useEffect(() => {
    if (!embedded) return;
    return () => {
      window.dispatchEvent(new CustomEvent('ggj:agent-breadcrumb', { detail: { title: '' } }));
    };
  }, [embedded]);

  useEffect(() => {
    if (!historyOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setHistoryOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [historyOpen]);

  // ── Session job-query persistence helpers ────────────────────────────
  /** Key used to store the last job search query for a session in localStorage. */
  function sessionJobQueryKey(sessionId: string) { return `ggj:agent:jq:${sessionId}`; }

  function persistSessionJobQuery(sessionId: string, query: string) {
    try { localStorage.setItem(sessionJobQueryKey(sessionId), query); } catch { /* noop */ }
  }

  function getPersistedJobQuery(sessionId: string): string | null {
    try { return localStorage.getItem(sessionJobQueryKey(sessionId)); } catch { return null; }
  }

  // ── Sliding-window constants ─────────────────────────────────────────
  const MSG_WINDOW = 12; // messages loaded per page

  // ── Helpers ─────────────────────────────────────────────────────────
  async function loadSession(s: AgentSession) {
    if (session?.id && scrollRef.current) {
      scrollPositionsRef.current.set(session.id, scrollRef.current.scrollTop);
    }
    pendingScrollRestoreRef.current = s.id;
    persistLastSessionId(s.id);
    setSession(s);
    setMessageActions({});
    setIsLoadingMsgs(true);
    setActiveCapability(null);
    setJobResults(null);
    setJobsPanelVisible(false);
    setOlderMsgsExist(false);
    try {
      const [rows, total] = await Promise.all([
        listMessagesRecent(s.id, MSG_WINDOW),
        countMessages(s.id),
      ]);
      setMessages(rows);
      setOlderMsgsExist(total > rows.length);

      // Restore search prefs; do NOT auto-open jobs panel on session load.
      // Jobs only show when the user explicitly asks for them.
      const savedQuery = getPersistedJobQuery(s.id);
      if (savedQuery && !hasAgentOptimizeHandoff()) {
        const prefs =
          getPersistedSessionSearchPrefs(s.id) ?? {
            source: 'internal' as const,
            deep: readAgentDeepSearch(),
          };
        setDeepSearch(prefs.deep);
        setJobQuery(savedQuery);
      }
    } finally {
      setIsLoadingMsgs(false);
    }
  }
  loadSessionRef.current = loadSession;

  async function loadOlderMessages() {
    if (!session || isLoadingOlder || messages.length === 0) return;
    const oldest = messages[0];
    setIsLoadingOlder(true);
    const el = scrollRef.current;
    const prevScrollHeight = el?.scrollHeight ?? 0;
    try {
      const older = await listMessagesBefore(session.id, oldest.createdAt, MSG_WINDOW);
      if (older.length === 0) {
        setOlderMsgsExist(false);
        return;
      }
      setMessages(prev => [...older, ...prev]);
      const total = await countMessages(session.id);
      setOlderMsgsExist(total > messages.length + older.length);
      // Preserve scroll position so view doesn't jump
      requestAnimationFrame(() => {
        if (el) el.scrollTop = el.scrollHeight - prevScrollHeight;
      });
    } finally {
      setIsLoadingOlder(false);
    }
  }

  /**
   * Resolve job search parameters. Now always returns 'unified' source —
   * the fetch function searches ALL sources (DB + external portals) simultaneously.
   */
  function resolveJobSearchParams(query: string): { source: 'internal' | 'external'; query: string } {
    const profileCtx = profile
      ? {
          currentRole: profile.currentRole,
          headline: profile.headline,
          skills: profile.skills,
          experienceYears: profile.experienceYears,
          location: profile.location,
        }
      : null;

    const profileFallbackQuery = profileCtx
      ? buildProfileAwareJobQuery('', profileCtx)
      : 'software engineer';

    const trimmed = query.trim();
    const isPlaceholder = !trimmed || /^<[^>]+>$/.test(trimmed) || trimmed.length < 2;

    // Always return 'internal' — the fetch function now searches all sources
    const resolvedQuery = isPlaceholder
      ? (profileCtx ? profileFallbackQuery : 'software engineer')
      : (profileCtx ? buildProfileAwareJobQuery(trimmed, profileCtx) : trimmed);

    return { source: 'internal', query: resolvedQuery };
  }

  const PROFILE_JOB_INTENT_RE =
    /\b(my profile|match my profile|match my\b|based on my profile|recommended jobs?|jobs? for me|jobs? that match)\b/i;

  function isProfileIntentJobQuery(q: string, keywordCount: number): boolean {
    return PROFILE_JOB_INTENT_RE.test(q) || keywordCount === 0;
  }

  function buildExternalSearchKeywords(
    q: string,
    keywordTokens: string[],
  ): string {
    const role = profile?.currentRole?.trim();
    if (isProfileIntentJobQuery(q, keywordTokens.length) && role) {
      return buildProfileAwareJobQuery(role, {
        currentRole: profile?.currentRole,
        headline: profile?.headline,
        skills: profile?.skills,
        experienceYears: profile?.experienceYears,
        location: profile?.location,
      });
    }
    const seniority = seniorityQueryTokens(profile?.experienceYears, role);
    if (keywordTokens.length >= 2) {
      return [keywordTokens.join(' '), seniority].filter(Boolean).join(' ');
    }
    if (keywordTokens.length === 1) {
      return [keywordTokens[0], seniority].filter(Boolean).join(' ');
    }
    if (role) {
      return buildProfileAwareJobQuery(role, {
        currentRole: profile?.currentRole,
        headline: profile?.headline,
        skills: profile?.skills,
        experienceYears: profile?.experienceYears,
        location: profile?.location,
      });
    }
    const trimmed = q.trim();
    return trimmed.length > 0 ? trimmed : 'jobs';
  }

  /** City-only location for LinkedIn/Naukri (APIs expect "Bangalore", not "Bengaluru, India"). */
  function normalizeExternalLocation(raw: string): string {
    const trimmed = raw.trim();
    if (!trimmed) return 'India';
    const city = (trimmed.split(',')[0]?.trim() ?? trimmed);
    if (/^india$/i.test(city)) return 'India';
    return city.replace(/\bbengaluru\b/i, 'Bangalore');
  }

  function jobMatchesLocationHint(
    jobLocation: string | null | undefined,
    hint: string,
  ): boolean {
    if (!hint || /^india$/i.test(hint)) return true;
    const loc = (jobLocation ?? '').toLowerCase();
    if (!loc) return true;
    const hintCity = hint.split(',')[0].trim().toLowerCase();
    const aliases: Record<string, string[]> = {
      bengaluru: ['bangalore', 'bengaluru', 'blr'],
      bangalore: ['bangalore', 'bengaluru', 'blr'],
      mumbai: ['mumbai', 'bombay'],
      delhi: ['delhi', 'ncr', 'gurugram', 'gurgaon', 'noida', 'new delhi'],
      hyderabad: ['hyderabad', 'secunderabad'],
      chennai: ['chennai', 'madras'],
      pune: ['pune', 'pimpri'],
    };
    const terms = [hintCity, ...(aliases[hintCity] ?? [])];
    return terms.some((t) => loc.includes(t));
  }

  function applyRoleAlignmentToRecScore(
    score: number,
    userRole: string,
    jobTitle: string | null | undefined,
  ): number {
    const domainScore = roleAlignmentScore(userRole, undefined, jobTitle);
    const isCrossDomain = domainScore !== 50 && domainScore < 30;
    if (isCrossDomain) return Math.min(20, Math.round(score * 0.35));
    if (domainScore !== 50 && domainScore < 45) {
      return Math.min(score, Math.round((score * 0.55) + domainScore * 0.45));
    }
    return score;
  }

  /** Build skill-overlap rec entries for jobs missing Pinecone scores. */
  function fillSyntheticRecScores(
    jobs: AgentJob[],
    userId: string,
    recMap: Map<string, RecommendationResult>,
  ) {
    if (!profile) return;
    const userSkillSet = new Set(
      [...(profile.skills ?? []), ...(profile.currentRole ?? '').split(/\s+/)]
        .map((s) => s.toLowerCase().trim())
        .filter(Boolean),
    );

    for (const job of jobs) {
      if (recMap.has(job.id)) continue;
      const jobSkills = [
        ...(job.skills ?? []),
        ...(job.must_have_skills ?? []),
        ...(job.good_to_have_skills ?? []),
        ...(job.title ?? '').toLowerCase().split(/\s+/),
      ].map((s) => s.toLowerCase().trim()).filter((s) => s.length > 2);
      const matched = jobSkills.filter((s) => userSkillSet.has(s));
      let score = Math.min(100, Math.round((matched.length / Math.max(jobSkills.length, 1)) * 140));
      score = applyRoleAlignmentToRecScore(score, profile.currentRole ?? '', job.title);
      const band =
        score >= 70 ? 'strong_fit' :
        score >= 50 ? 'good_fit' :
        score >= 30 ? 'partial_fit' : 'not_recommended';
      recMap.set(job.id, {
        id: '',
        user_id: userId,
        job_id: job.id,
        score,
        band,
        matched_skills: [...new Set(matched)].slice(0, 10),
        missing_skills: [],
        bonus_skills: [],
        why_matched: `${matched.length} skill${matched.length !== 1 ? 's' : ''} matched`,
        skill_gaps: [],
        action_items: [],
        score_breakdown: {
          skill_overlap: score,
          semantic_similarity: 0,
          experience_alignment: 50,
          budget_fit: 50,
          location_fit: 50,
          deadline_feasibility: 50,
        },
        generated_at: new Date().toISOString(),
      });
    }
  }

  /** Fetch jobs — recommendation engine first, keyword search fallback. */
  async function fetchJobResults(
    query: string,
    source?: 'internal' | 'external',
    opts?: { openPanel?: boolean },
  ) {
    const activeSource = source ?? searchSource;
    const fetchKey = `${activeSource}|${deepSearch}|${query.trim().toLowerCase()}`;
    const inflight = jobFetchInflightRef.current;
    if (inflight?.key === fetchKey) {
      return inflight.promise;
    }

    const promise = (async () => {
    const openPanel = opts?.openPanel !== false;
    const fetchGen = ++jobFetchGenRef.current;
    const isStale = () => fetchGen !== jobFetchGenRef.current;
    const activeSessionId = sessionRef.current?.id;
    setJobsLoading(true);
    setChannelFetchPhase('channels');
    setJobQuery(query);
    if (openPanel) {
      setJobResults([]);
      setHistoryOpen(false);
    }

    // Persist the query so it can be restored when the user returns to this session
    if (activeSessionId) {
      persistSessionJobQuery(activeSessionId, query);
      persistSessionSearchPrefs(activeSessionId, activeSource, deepSearch);
    }

    const SEL = 'id, title, company, company_logo_url, location, remote, job_type, experience_level, skills, must_have_skills, good_to_have_skills, url, salary_min, salary_max, salary_currency, compensation_text, posted_at, description_md, source_channel';

    const STOP = new Set([
      'find','show','me','get','list','give','search','jobs','job','role','roles',
      'position','positions','opening','openings','for','in','at','from','with',
      'that','the','a','an','and','or','want','looking','need','hiring','available',
    ]);

    const keywords = query
      .toLowerCase().replace(/[^a-z0-9.\s]/g,' ').split(/\s+/)
      .map((t) => t.replace(/^\.+|\.+$/g, ''))
      .filter((t) => t.length > 2 && !STOP.has(t)).slice(0, 4);
    if (keywords.length === 0) {
      const fb = query.trim().split(/\s+/).find((t) => t.length > 2);
      if (fb) keywords.push(fb.toLowerCase());
    }

    // When the query is vague (e.g. "based on my profile show recommended"),
    // supplement with the user's actual profile skills + role so we search the
    // DB for something meaningful rather than falling to a recent-jobs dump.
    if (keywords.length < 2 && profile) {
      const extra: string[] = [
        ...(profile.currentRole ?? '').toLowerCase().split(/\s+/).filter((t) => t.length > 2 && !STOP.has(t)),
        ...(profile.skills ?? []).slice(0, 4).map((s) => s.toLowerCase().replace(/[^a-z0-9]/g, '')).filter((t) => t.length > 2),
      ];
      const seen = new Set(keywords);
      for (const t of extra) {
        if (!seen.has(t)) { keywords.push(t); seen.add(t); }
        if (keywords.length >= 4) break;
      }
    }

    try {
      let jobs: AgentJob[] = [];
      const recMap = new Map<string, RecommendationResult>();
      const authUserId = (await supabase.auth.getUser()).data.user?.id ?? '';
      if (isStale()) return;
      const locMatch = query.match(/\bin\s+([A-Za-z\s]+?)(?:\s+jobs?)?$/i);
      const externalLocationHint = normalizeExternalLocation(
        locMatch ? locMatch[1].trim() : (profile?.location?.trim() ?? ''),
      );

      // ═══════════════════════════════════════════════════════════════════
      // UNIFIED SEARCH: fetch from ALL sources simultaneously
      // 1. Internal DB (peerlist_jobs via unified feed)
      // 2. External portals (LinkedIn + Naukri) — results persisted to DB
      // 3. Direct keyword search on peerlist_jobs (fallback)
      // ═══════════════════════════════════════════════════════════════════
      const userId = authUserId;

      // ── Step 1: Internal DB feed (runs in parallel with external) ───────
      const internalPromise = (async () => {
        let dbRecs: RecommendationResult[] = [];
        if (userId) {
          try {
            dbRecs = await getTopJobsForUser(userId, RECOMMENDED_POOL_LIMIT);
            dbRecs.forEach((r) => recMap.set(r.job_id, r));
          } catch (e) {
            console.warn('[ai-agent] recommendation scores unavailable:', e);
            void supabase.functions.invoke('match-jobs-for-user').catch(() => {});
          }
        }

        const profileJobTitle =
          profile?.currentRole?.trim() ?? profileSkillsJobTitle ?? undefined;

        // Same pool as Jobs for You tab — do NOT pass searchQuery here (that filter
        // drops most of the recommended pool and caused fewer results vs Jobs tab).
        let feedJobs: import('../dashboard/components/JobCard').Job[] = [];
        try {
          feedJobs = await buildUnifiedRecommendedFeedCore({
            limit: RECOMMENDED_POOL_LIMIT,
            dbRecs,
            profile,
            profileJobTitle,
            userCtx,
            hasSkillContext,
            skillContextReady,
          });
        } catch (feedErr) {
          console.warn('[ai-agent] Unified feed failed:', feedErr);
        }

        const dbJobs = jobsToAgentJobs(feedJobs);
        fillSyntheticRecScores(dbJobs, userId, recMap);
        for (const feedJob of feedJobs) {
          if (feedJob.matchScore == null) continue;
          const existing = recMap.get(feedJob.id);
          if (existing) recMap.set(feedJob.id, { ...existing, score: feedJob.matchScore });
        }
        return dbJobs;
      })();

      // ── Step 2: External portal search (runs in parallel with internal) ─
      const externalPromise = (async (): Promise<AgentJob[]> => {
        const extCreds = loadExternalCreds();
        const credentialMap: Record<string, string> = {};
        for (const c of extCreds) credentialMap[c.key] = c.value;

        const locationHint = externalLocationHint;
        const externalKeywords = buildExternalSearchKeywords(query, keywords);
        const profileSkillList = (profile?.skills ?? []).slice(0, 8);

        let linkedInExp: string[] | undefined;
        const expYears = profile?.experienceYears;
        if (expYears != null) {
          if (expYears <= 1) linkedInExp = ['internship', 'entry'];
          else if (expYears <= 3) linkedInExp = ['entry', 'associate'];
          else if (expYears <= 6) linkedInExp = ['associate', 'mid-senior'];
          else if (expYears <= 12) linkedInExp = ['mid-senior', 'director'];
          else linkedInExp = ['director', 'executive'];
        }

        console.info('[ai-agent] External search keywords:', externalKeywords, 'location:', locationHint || 'India');

        try {
          const { data: extData, error: extErr } = await supabase.functions.invoke<{
            ok: boolean;
            jobs: Array<{
              source_job_id: string; source_url: string; title: string; company: string;
              company_logo_url: string | null; location: string | null; work_mode: string | null;
              job_type: string | null; experience_level: string | null;
              required_skills: string[]; preferred_skills: string[];
              salary_min: number | null; salary_max: number | null; salary_currency: string;
              description_md: string | null; posted_at: string | null; source: string;
            }>;
            sources: string[];
            source_info: Record<string, { found: number; has_credentials: boolean }>;
            total: number;
          }>('search-jobs-external', {
            body: {
              keywords: externalKeywords,
              location: locationHint,
              credentials: credentialMap,
              max_results: deepSearch ? 40 : 25,
              persist_jobs: true,
              linkedin: {
                date_posted: deepSearch ? 'past-week' : 'past-day',
                sort_by: 'date',
                ...(linkedInExp ? { experience_level: linkedInExp } : {}),
              },
              naukri: {
                freshness: deepSearch ? 7 : 1,
                key_skills: profileSkillList,
                ...(expYears != null
                  ? { experience_min: Math.max(0, expYears - 1), experience_max: expYears + 2 }
                  : {}),
              },
            },
          });

          if (extErr) {
            console.warn('[ai-agent] search-jobs-external failed:', extErr.message);
            return [];
          }
          if (!extData?.ok || !extData.jobs?.length) {
            console.info('[ai-agent] External returned 0 results');
            return [];
          }

          console.info(`[ai-agent] External: ${extData.total} jobs from ${extData.sources?.join(', ')}`);
          return extData.jobs.map((j) => ({
            id: j.source_job_id, title: j.title, company: j.company,
            company_logo_url: j.company_logo_url, location: j.location,
            remote: j.work_mode === 'remote', job_type: j.job_type,
            experience_level: j.experience_level, skills: j.required_skills,
            must_have_skills: j.required_skills, good_to_have_skills: j.preferred_skills,
            url: j.source_url, salary_min: j.salary_min, salary_max: j.salary_max,
            salary_currency: j.salary_currency, description_md: j.description_md,
            posted_at: j.posted_at, source_channel: j.source,
          } as AgentJob));
        } catch (e) {
          console.warn('[ai-agent] External search threw:', e);
          return [];
        }
      })();

      // ── Step 3: Direct keyword search on peerlist_jobs (parallel) ────────
      const keywordPromise = keywords.length > 0
        ? Promise.allSettled(
            keywords.map((kw) =>
              supabase.from('peerlist_jobs').select(SEL)
                .or(`title.ilike.%${kw}%,description_md.ilike.%${kw}%`)
                .eq('is_active', true)
                .order('posted_at', { ascending: false })
                .limit(20),
            ),
          )
        : Promise.resolve([]);

      // ── Wait for all three sources ────────────────────────────────────────
      const [internalJobs, externalJobs, keywordResults] = await Promise.all([
        internalPromise,
        externalPromise,
        keywordPromise,
      ]);
      if (isStale()) return;

      // ── Merge & deduplicate — external channels first, then internal DB ──
      const seen = new Set<string>();
      for (const j of externalJobs) {
        if (!seen.has(j.id)) { seen.add(j.id); jobs.push(j); }
      }
      for (const j of internalJobs) {
        if (!seen.has(j.id)) { seen.add(j.id); jobs.push(j); }
      }

      // Keyword search fallback — add any unique results
      if (Array.isArray(keywordResults)) {
        for (const result of keywordResults) {
          if (result.status === 'fulfilled' && result.value?.data) {
            for (const row of result.value.data) {
              if (row?.id && !seen.has(row.id)) {
                seen.add(row.id);
                jobs.push(row as AgentJob);
              }
            }
          }
        }
      }

      console.info(`[ai-agent] Merged: ${externalJobs.length} external + ${internalJobs.length} internal = ${jobs.length} total`);

      if (!isStale()) {
        setChannelFetchPhase('scoring');
        if (openPanel) {
          setJobsPanelVisible(true);
          if (activeSessionId) persistSessionJobsPanelOpen(activeSessionId, true);
        }
      }

      // ── Client-side skill-overlap scoring for external jobs ──────────
      if (externalJobs.length > 0 && profile) {
        const userSkillsRaw = [
          ...(profile.skills ?? []),
          ...(profile.currentRole ?? '').split(/\s+/),
        ].map((s) => s.toLowerCase().trim()).filter(Boolean);
        const userSkillSet = new Set(userSkillsRaw);

        for (const job of externalJobs) {
          if (recMap.has(job.id)) continue;
          const jobSkills = [
            ...(job.skills ?? []), ...(job.must_have_skills ?? []),
            ...(job.good_to_have_skills ?? []),
            ...(job.title ?? '').toLowerCase().split(/\s+/),
          ].map((s) => s.toLowerCase().trim()).filter((s) => s.length > 2);

          const matched = jobSkills.filter((s) => userSkillSet.has(s));
          const missing = [...new Set(
            (job.must_have_skills ?? job.skills ?? [])
              .map((s) => s.toLowerCase()).filter((s) => !userSkillSet.has(s)),
          )];
          const rawPct = (matched.length / Math.max(jobSkills.length, 1)) * 100;
          let score = Math.min(100, Math.round(rawPct * 1.4));
          score = applyRoleAlignmentToRecScore(score, profile.currentRole ?? '', job.title);

          const band = score >= 70 ? 'strong_fit' : score >= 50 ? 'good_fit' : score >= 30 ? 'partial_fit' : 'not_recommended';
          recMap.set(job.id, {
            id: '', user_id: userId, job_id: job.id, score, band,
            matched_skills: [...new Set(matched)].slice(0, 10),
            missing_skills: missing.slice(0, 5), bonus_skills: [],
            why_matched: `${matched.length} skill${matched.length !== 1 ? 's' : ''} matched`,
            skill_gaps: missing.slice(0, 3), action_items: [],
            score_breakdown: { skill_overlap: score, semantic_similarity: 0, experience_alignment: 50, budget_fit: 50, location_fit: 50, deadline_feasibility: 50 },
            generated_at: new Date().toISOString(),
          } as import('../recommendations/types').RecommendationResult);
        }
      }

      // No fallback to old DB jobs — only live channel results are shown

      jobs = jobs.slice(0, RECOMMENDED_POOL_LIMIT);

      // Default sort: newest first (posted date descending). Portal jobs were buried
      // under high-match stale DB rows when we sorted by relevance.
      const postedTs = (j: AgentJob) => {
        const t = j.posted_at;
        return t ? new Date(t).getTime() : 0;
      };
      jobs.sort((a, b) => postedTs(b) - postedTs(a));

      if (profile) {
        jobs = jobs.map((j) => ({
          ...j,
          matchScore: recMap.get(j.id)?.score ?? (j as { matchScore?: number }).matchScore,
        }));
        console.info(`[ai-agent] Sorted ${jobs.length} jobs by date (newest first)`);
      }

      if (isStale()) return;

      setChannelFetchPhase('done');
      setJobResults(jobs);
      setJobRecs(recMap);
      if (openPanel && jobs.length > 0) {
        setJobsPanelVisible(true);
        if (activeSessionId) persistSessionJobsPanelOpen(activeSessionId, true);
      }

      // Update the last "Searching…" assistant message to confirm results loaded
      if (jobs.length > 0) {
        const channels: string[] = [];
        const channelSet = new Set(externalJobs.map((j) => j.source_channel?.toLowerCase()).filter(Boolean));
        if (channelSet.has('linkedin')) channels.push('LinkedIn');
        if (channelSet.has('naukri')) channels.push('Naukri');
        if (channels.length === 0 && externalJobs.length > 0) channels.push('job portals');
        const sourceBreakdown = channels.length > 0 ? `${externalJobs.length} from ${channels.join(' & ')}` : '';
        const label = [
          `Found **${jobs.length}** matching jobs${sourceBreakdown ? ` (${sourceBreakdown})` : ''}. Results are shown in the panel on the right.`,
          '',
          '**What would you like to do next?**',
          '- **Optimize your resume** for any of these roles',
          '- **Search with different keywords** or location',
          '- **Get interview prep tips** for a specific role',
        ].join('\n');
        setMessages((prev) => {
          const idx = [...prev].reverse().findIndex(
            (m) => m.role === 'assistant' && m.content.includes('Searching for matching jobs'),
          );
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;
          return prev.map((m, i) => i === realIdx ? { ...m, content: label } : m);
        });
      } else {
        const noResultsMsg = [
          'No matching jobs found for this search. Here are some things we can try:',
          '',
          '- **Broaden your search** — try different keywords or remove location filters',
          '- **Build or optimize your resume** — I can help you create a strong resume',
          '- **Update your profile** — add skills and preferences so I can find better matches',
        ].join('\n');
        setMessages((prev) => {
          const idx = [...prev].reverse().findIndex(
            (m) => m.role === 'assistant' && m.content.includes('Searching for matching jobs'),
          );
          if (idx === -1) return prev;
          const realIdx = prev.length - 1 - idx;
          return prev.map((m, i) => i === realIdx ? { ...m, content: noResultsMsg } : m);
        });
      }
    } catch (e) {
      console.warn('[ai-agent] fetchJobResults failed:', e);
      if (!isStale()) { setJobResults([]); setChannelFetchPhase('idle'); }
    } finally {
      if (!isStale()) { setJobsLoading(false); setChannelFetchPhase('idle'); }
    }
    })();

    jobFetchInflightRef.current = { key: fetchKey, promise };
    try {
      await promise;
    } finally {
      if (jobFetchInflightRef.current?.key === fetchKey) {
        jobFetchInflightRef.current = null;
      }
    }
  }
  const handleNewSession = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setJobResults(null);
    setJobRecs(new Map());
    setShortlisted(new Set());
    setWebResults(null);
    setWebSearchQuery('');
    setWorkspaceLetter(null);
    setThinkingContent({});
    setCanvasJob(null);
    setCanvasResume('');
    setCanvasSaved(false);
    setCanvasSaveErr(null);
    setCanvasApplied(false);
    setOptimizingMsgId(null);
    setChatVisible(true);
    setJobsPanelVisible(false);
    agentic.reset();
    // Reset canvas so resume/cover letter/notes from a previous session don't bleed in
    canvas.dispatch({ type: 'RESET_CANVAS' });
    const fresh = await createSession();
    setSession(fresh);
    setMessages([]);
    setComposerValue('');
    setActiveCapability(null);
    setMessageActions({});
    setHistoryKey((k) => k + 1);
    persistLastSessionId(fresh.id);
    window.dispatchEvent(new CustomEvent('ggj:agent-session-changed', { detail: { sessionId: fresh.id } }));
    window.dispatchEvent(new CustomEvent('ggj:agent-history-refresh'));
    requestAnimationFrame(() => composerRef.current?.focus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectSession = useCallback(async (s: AgentSession) => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsStreaming(false);
    setComposerValue('');
    setJobResults(null);
    setJobRecs(new Map());
    setShortlisted(new Set());
    setJobsPanelVisible(false);
    setWebResults(null);
    setWebSearchQuery('');
    setCanvasJob(null);
    setThinkingContent({});
    // Reset canvas so the previous session's resume/cover letter doesn't bleed into this one
    canvas.dispatch({ type: 'RESET_CANVAS' });
    await loadSession(s);
    window.dispatchEvent(new CustomEvent('ggj:agent-session-changed', { detail: { sessionId: s.id } }));
    if (!isDesktop) setHistoryOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  // ── Hub / shell bridge ─────────────────────────────────────────────
  // AgentHubPage (and legacy AgentSidebarPanel) dispatch session events;
  // we listen and sync chat state.
  // NOTE: placed here intentionally — after handleNewSession/handleSelectSession are defined.
  useEffect(() => {
    if (!embedded) return;

    const onNew = () => { void handleNewSession(); };
    const onSelect = (e: Event) => {
      const { sessionId } = (e as CustomEvent<{ sessionId: string }>).detail ?? {};
      if (!sessionId) return;
      listRecentSessions(80).then(list => {
        const found = list.find(s => s.id === sessionId);
        if (found) void handleSelectSession(found);
      });
    };
    // When the sidebar deletes the active session, start fresh
    const onDelete = (e: Event) => {
      const { sessionId } = (e as CustomEvent<{ sessionId: string }>).detail ?? {};
      if (sessionId && session?.id === sessionId) void handleNewSession();
    };

    window.addEventListener('ggj:agent-new-session', onNew);
    window.addEventListener('ggj:agent-select-session', onSelect);
    window.addEventListener('ggj:agent-delete-session', onDelete);
    return () => {
      window.removeEventListener('ggj:agent-new-session', onNew);
      window.removeEventListener('ggj:agent-select-session', onSelect);
      window.removeEventListener('ggj:agent-delete-session', onDelete);
    };
  }, [embedded, handleNewSession, handleSelectSession, session?.id]);

  // Keep the ref always pointing to the latest fetchJobResults so memoized
  // callbacks never capture a stale closure.
  fetchJobResultsRef.current = fetchJobResults;
  sessionRef.current = session;

  const collapseJobsPanel = useCallback(() => {
    const sid = sessionRef.current?.id;
    if (sid) persistSessionJobsPanelOpen(sid, false);
    setJobsPanelVisible(false);
    setMobileSplitTab('chat');
  }, []);

  // Mobile: focus jobs when results open; default to grid (table is unreadable on narrow screens)
  useEffect(() => {
    if (isDesktop) return;
    const hasJobs = jobResults !== null || jobsLoading;
    if (jobsPanelVisible && hasJobs) {
      setMobileSplitTab('jobs');
      setJobViewMode((m) => (m === 'list' ? 'grid' : m));
    }
  }, [isDesktop, jobsPanelVisible, jobResults, jobsLoading]);

  // If the user leaves the tab and returns without a full remount, re-open the jobs
  // Removed: auto-restore jobs on visibility change — user must explicitly request jobs.

  // Persist toolbar choices so they survive refresh and new sessions on this device.
  useEffect(() => {
    persistAgentSearchPrefs('internal', deepSearch);
  }, [deepSearch]);

  // Re-fetch job results when the user toggles Deep Search.
  // Only fires after initial mount (isMountedRef guard) and only when there is a query to re-run.
  const isMountedRef = React.useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) { isMountedRef.current = true; return; }
    if (!jobsPanelVisible) return;
    const sid = sessionRef.current?.id;
    if (!sid) return;
    const savedQuery = getPersistedJobQuery(sid) ?? jobQuery;
    if (savedQuery) {
      void fetchJobResultsRef.current(savedQuery, 'internal', { openPanel: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepSearch]);

  // Re-rank when resume/profile skills finish loading (same gate as Jobs tab).
  const prevSkillReadyRef = React.useRef(false);
  useEffect(() => {
    const wasReady = prevSkillReadyRef.current;
    prevSkillReadyRef.current = skillContextReady;
    if (wasReady || !skillContextReady) return;
    if (!jobsPanelVisible) return;
    const sid = sessionRef.current?.id;
    const savedQuery = (sid ? getPersistedJobQuery(sid) : null) ?? jobQuery;
    if (savedQuery) {
      void fetchJobResultsRef.current(savedQuery, 'internal', { openPanel: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillContextReady, jobsPanelVisible, searchSource]);

  const handleChipAction = useCallback(async (preset: AgentCapabilityPreset) => {
    if (preset.id === 'recommended-jobs') {
      const canvasJobTitle = canvas.state.job?.title;
      const role   = canvasJobTitle ?? profile?.currentRole?.trim() ?? 'engineer';
      const skills = (profile?.skills ?? []).slice(0, 3).join(' ');
      const query  = [role, skills].filter(Boolean).join(' ');
      const { source, query: resolvedQuery } = resolveJobSearchParams(query || 'recent jobs');

      setSearchSource(source);

      const userMsg: AgentMessageT = {
        id: `chip-rec-${Date.now()}`,
        sessionId: session?.id ?? '',
        role: 'user',
        content: canvasJobTitle ? `Show me similar jobs to ${canvasJobTitle}` : 'Show me recommended jobs',
        ts: new Date().toISOString(),
      };
      const assistantMsg: AgentMessageT = {
        id: `chip-rec-reply-${Date.now()}`,
        sessionId: session?.id ?? '',
        role: 'assistant',
        content: canvasJobTitle
          ? `Searching for **${canvasJobTitle}** roles similar to the one you're optimizing for. Ranked by your profile match.`
          : source === 'internal'
            ? `Loading your unified job feed${role ? ` for **${role}**` : ''}${skills ? ` · skills: *${skills}*` : ''} — ranked by profile match.`
            : `Searching external job boards${query ? ` for **${query}**` : ''}. Finish your profile for AI-ranked matches.`,
        ts: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMsg, assistantMsg]);

      await fetchJobResultsRef.current(resolvedQuery, source);
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.currentRole, profile?.skills, session?.id]);

  const persistHeaderTitle = useCallback(async () => {
    if (!session) return;
    const next = draftTitle.trim().slice(0, 200);
    const prevNorm = session.title?.trim() ?? '';
    if (next === prevNorm) return;
    try {
      await renameSession(session.id, next);
      setSession((s) => (s ? { ...s, title: next.length ? next : null } : s));
      setTitleEditing(false);
      setHistoryKey((k) => k + 1);
    } catch {
      setDraftTitle(prevNorm);
      setTitleEditing(false);
    }
  }, [session, draftTitle]);

  const handleSidebarRenamed = useCallback((id: string, title: string | null) => {
    setSession((s) => (s?.id === id ? { ...s, title } : s));
    window.dispatchEvent(new CustomEvent('ggj:agent-history-refresh'));
  }, []);

  const handleSidebarDeletedSession = useCallback((deletedId: string) => {
    if (session?.id !== deletedId) return;
    void handleNewSession();
  }, [session?.id, handleNewSession]);

  // ── Agentic optimize flow ──────────────────────────────────────────
  /**
   * Open the optimize canvas (right pane) and run the per-section agentic pipeline.
   * Replaces the old chat-only walkthrough — chat just shows a brief stub; all live
   * progress and final actions live in the canvas (ChatGPT-canvas style).
   */
  const startAgenticOptimize = useCallback(async (
    job:        AgentJob | { id: string; title: string; company: string; description?: string; skills?: string[]; requirements?: string[]; url?: string },
    resumeText: string,
    opts?: { skipUserChatMessage?: boolean; precomputedMatchScore?: number },
  ) => {
    if (!profile) return;

    // ── Credit gate: resume optimization costs 3 credits ──────────────
    const optimizeCost = getCreditCost('resume_optimize');
    if (!creditSystem.bypassEnabled && !creditSystem.hasEnoughCredits(optimizeCost)) {
      openCreditGate('Resume Optimization', optimizeCost);
      return;
    }

    // Bootstrap session if needed
    let sess = session;
    if (!sess) {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!s?.user.id) return;
        sess = await createSession(s.user.id);
        setSession(sess);
      } catch { return; }
    }

    // Build OptimizeJobContext for the canvas + JobContext for the hook
    const jdText = 'description_md' in job ? buildJdFromJob(job as AgentJob) : (job.description ?? '');
    const jobCtx: OptimizeJobContext = {
      id:      job.id,
      title:   job.title,
      company: job.company,
      jdText,
      // Carry forward the external listing URL so the Apply button can open
      // it in a new tab. Without this, Apply silently no-op'd for every
      // external job (LinkedIn / Naukri / Peerlist).
      url:           (job as AgentJob).url ?? (job as { url?: string }).url ?? null,
      location:      (job as AgentJob).location ?? null,
      sourceChannel: (job as AgentJob).source_channel ?? null,
    };
    const skills = ('skills' in job && Array.isArray(job.skills) ? job.skills :
                    'must_have_skills' in job ? (job as AgentJob).must_have_skills ?? (job as AgentJob).skills ?? [] :
                    []) ?? [];
    const requirements = ('requirements' in job && Array.isArray(job.requirements) ? job.requirements : []) ?? [];

    // ── Reset canvas + open it (right pane swap) ──────────────────────
    optimizationIdRef.current = null;
    setCanvasJob(jobCtx);
    setCanvasResume(resumeText);
    setCanvasSaving(false);
    setCanvasSaved(false);
    setCanvasSaveErr(null);
    setCanvasApplied(false);
    agentic.reset();

    // Switch right panel to canvas view immediately so the user sees the canvas
    // instead of the job list while the agentic pipeline runs.
    canvasRef.current.dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
    canvasRef.current.dispatch({
      type: 'SET_JOB',
      job: {
        id:      job.id,
        title:   job.title,
        company: job.company,
        description: jdText,
        skills,
        requirements,
        url:           jobCtx.url ?? undefined,
        location:      jobCtx.location ?? undefined,
        sourceChannel: jobCtx.sourceChannel ?? undefined,
      },
    });

    const fromProfileCard = profileToResumeData(profile, { authEmail, authPhone });
    const resolvedResume = getBestResumePlainTextForAgent(profile);
    const finalResumeText = resolvedResume.length >= 50 ? resolvedResume : resumeText;
    const fromText = resumePlainTextToResumeData(finalResumeText);
    const resumeData = fromProfileCard ? {
      ...fromProfileCard,
      contact: {
        ...fromText.contact,
        ...fromProfileCard.contact,
        email: fromProfileCard.contact?.email || fromText.contact?.email,
        phone: fromProfileCard.contact?.phone || fromText.contact?.phone,
      }
    } : fromText;

    if (resumeData) {
      canvasRef.current.dispatch({ type: 'SET_ORIGINAL_RESUME', data: resumeData });
    }
    canvasRef.current.dispatch({ type: 'SET_RAW_RESUME_TEXT', text: finalResumeText });
    canvasRef.current.dispatch({ type: 'SET_TAB', tab: 'resume' });

    // Show profile-backed canonical immediately — never block the canvas on Gemini.
    let canonical: CanonicalResume | null = resumeData ? resumeDataToCanonical(resumeData) : null;
    if (canonical) {
      const seedRows = canonicalResumeToCanvasSections(canonical);
      canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: canonical });
      canvasRef.current.dispatch({ type: 'SET_SECTIONS_DIRECT', sections: seedRows });
    } else {
      try {
        canonical = await processResumeWithGemini({
          resumeText: finalResumeText,
          signal: AbortSignal.timeout(25_000),
        });
        const seedRows = canonicalResumeToCanvasSections(canonical);
        canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: canonical });
        canvasRef.current.dispatch({ type: 'SET_SECTIONS_DIRECT', sections: seedRows });
      } catch (err) {
        console.warn('[ai-agent] canonical Gemini extract failed:', err);
        const errMsg: AgentMessageT = {
          id: crypto.randomUUID(),
          sessionId: sess.id,
          role: 'assistant',
          content: 'Could not structure your resume for optimization. Please complete your profile experience and summary in Settings, then try again.',
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errMsg]);
        setOptimizingMsgId(null);
        return;
      }
    }

    // Background Gemini canonical refinement — only updates metadata, never overwrites
    // sections after optimization completes (phase becomes 'fulfillment'/'done').
    void processResumeWithGemini({
      resumeText: finalResumeText,
      signal: AbortSignal.timeout(25_000),
    })
      .then((refined) => {
        const currentPhase = canvasRef.current.state.phase;
        // Only update sections if still in the pre-optimization canvas view
        if (currentPhase === 'idle' || currentPhase === 'consent') {
          canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: refined });
          canvasRef.current.dispatch({
            type: 'SET_SECTIONS_DIRECT',
            sections: canonicalResumeToCanvasSections(refined),
          });
        } else {
          // During/after optimization — only update canonical structure, don't touch visible sections
          canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: refined });
        }
      })
      .catch((err) => {
        console.warn('[ai-agent] background canonical refine skipped:', err);
      });

    const hydrated = canonicalResumeToCanvasSections(canonical);

    // ── Brief chat trail ──────────────────────────────────────────────
    if (!opts?.skipUserChatMessage) {
      const userText = optimizeUserMessage(job.title, job.company);
      const userMsg: AgentMessageT = {
        id: crypto.randomUUID(), sessionId: sess.id, role: 'user', content: userText, createdAt: new Date().toISOString(),
      };
      await appendMessage(sess.id, 'user', userText);
      setMessages(prev => [...prev, userMsg]);
    }

    const stubId = crypto.randomUUID();
    const stub: AgentMessageT = {
      id: stubId, sessionId: sess.id, role: 'assistant',
      content: startingAnalysisMessage(job.title, job.company),
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, stub]);
    setOptimizingMsgId(stubId);

    // ── Run the agentic per-section pipeline ──────────────────────────
    try {
      await agentic.start({
        resumeText: finalResumeText,
        preParsedSections: hydrated,
        job: {
          title:        job.title,
          company:      job.company,
          description:  jdText,
          skills,
          requirements,
        },
        precomputedMatchScore: opts?.precomputedMatchScore,
      });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      console.warn('[ai-agent] startAgenticOptimize failed:', err);
    }
  }, [profile, session, agentic, creditSystem.bypassEnabled, creditSystem.credits, openCreditGate]);

  /** Open the right-panel resume canvas with profile content — stay on /agent. */
  const openAgentResumeCanvas = useCallback((opts?: {
    jobTitle?: string;
    company?: string;
    jdText?: string;
  }) => {
    if (!profile) return;

    const resumeFromAgent = getBestResumePlainTextForAgent(profile);
    const profileResume = buildResumePlainTextFromUserProfile(profile);
    const finalResumeText = resumeFromAgent.length >= 50 ? resumeFromAgent : profileResume;

    const jobTitle = opts?.jobTitle?.trim() || profile.currentRole?.trim() || 'Target Role';
    const company = opts?.company?.trim() || 'Paste job details in chat';
    const jdText = opts?.jdText?.trim() ?? '';

    const jobCtx: OptimizeJobContext = {
      id: `canvas-${Date.now()}`,
      title: jobTitle,
      company,
      jdText,
      url: null,
      location: profile.location ?? null,
      sourceChannel: null,
    };

    setCanvasJob(jobCtx);
    setCanvasResume(finalResumeText);
    setCanvasSaving(false);
    setCanvasSaved(false);
    setCanvasSaveErr(null);
    setCanvasApplied(false);

    const fromProfileCard = profileToResumeData(profile, { authEmail, authPhone });
    const fromText = resumePlainTextToResumeData(finalResumeText);
    const resumeData = fromProfileCard ? {
      ...fromProfileCard,
      contact: {
        ...fromText.contact,
        ...fromProfileCard.contact,
        email: fromProfileCard.contact?.email || fromText.contact?.email,
        phone: fromProfileCard.contact?.phone || fromText.contact?.phone,
      }
    } : fromText;

    canvasRef.current.dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
    canvasRef.current.dispatch({ type: 'SET_TAB', tab: 'resume' });
    canvasRef.current.dispatch({
      type: 'SET_JOB',
      job: {
        id: jobCtx.id,
        title: jobTitle,
        company,
        description: jdText,
        skills: profile.skills ?? [],
        requirements: [],
        location: profile.location ?? undefined,
      },
    });
    if (resumeData) {
      canvasRef.current.dispatch({ type: 'SET_ORIGINAL_RESUME', data: resumeData });
    }
    canvasRef.current.dispatch({ type: 'SET_RAW_RESUME_TEXT', text: finalResumeText });
    canvasRef.current.dispatch({ type: 'SET_PHASE', phase: 'analysis' });

    const seedCanonical = resumeData ? resumeDataToCanonical(resumeData) : null;
    if (seedCanonical) {
      const seedRows = canonicalResumeToCanvasSections(seedCanonical);
      canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: seedCanonical });
      canvasRef.current.dispatch({ type: 'SET_SECTIONS_DIRECT', sections: seedRows });
      canvasRef.current.dispatch({ type: 'SET_COMMITTED_SECTIONS', sections: seedRows });
      canvasRef.current.dispatch({ type: 'SET_PHASE', phase: 'fulfillment' });
    }

    void (async () => {
      try {
        const canonical = await processResumeWithGemini({
          resumeText: finalResumeText,
          signal: AbortSignal.timeout(25_000),
        });
        const rows = canonicalResumeToCanvasSections(canonical);
        canvasRef.current.dispatch({ type: 'SET_CANONICAL_RESUME', data: canonical });
        canvasRef.current.dispatch({ type: 'SET_SECTIONS_DIRECT', sections: rows });
        canvasRef.current.dispatch({ type: 'SET_COMMITTED_SECTIONS', sections: rows });
        canvasRef.current.dispatch({ type: 'SET_PHASE', phase: 'fulfillment' });
      } catch (err) {
        console.warn('[ai-agent] openAgentResumeCanvas canonical extract failed:', err);
        if (!seedCanonical) {
          canvasRef.current.dispatch({ type: 'SET_PHASE', phase: 'analysis' });
        }
      }
    })();
  }, [profile]);

  /** Shared handler bundle for workflow + universal engagement action chips. */
  const canvasEngagementHandlers = useCallback((): GeneralEngagementHandlers => {
    const cState = canvasRef.current.state;
    const jobTitle = cState.job?.title ?? canvasJob?.title ?? profile?.currentRole;
    const company = cState.job?.company ?? canvasJob?.company ?? '';
    return {
      onGenerateCoverLetter: () => {
        const sid = sessionRef.current?.id;
        if (!sid) return;
        const id = crypto.randomUUID();
        setMessages((prev) => [...prev, {
          id, sessionId: sid, role: 'assistant' as const,
          content: draftingCoverLetterMessage(jobTitle ?? 'the role', company),
          createdAt: new Date().toISOString(),
        }]);
        void handleLetterActionRef.current('cover', { jobTitle, company }, id);
      },
      onGenerateApplicationNote: () => {
        const sid = sessionRef.current?.id;
        if (!sid) return;
        const id = crypto.randomUUID();
        setMessages((prev) => [...prev, {
          id, sessionId: sid, role: 'assistant' as const,
          content: draftingApplicationLetterMessage(jobTitle ?? 'the role', company),
          createdAt: new Date().toISOString(),
        }]);
        void handleLetterActionRef.current('application', { jobTitle, company }, id);
      },
      onDownloadPdf: () => {
        window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
      },
      onApply: () => {
        window.dispatchEvent(new CustomEvent('ggj:agent-apply'));
      },
      onFindSimilarJobs: () => {
        canvasRef.current.dispatch({ type: 'BACK_TO_LIST' });
        const q = [jobTitle, profile?.currentRole].filter(Boolean).join(' ') || 'similar jobs';
        void fetchJobResultsRef.current(q, searchSource);
      },
      onOpenDashboard: () => { spaNavigate('/dashboard'); },
      onOpenApplications: () => { spaNavigate('/history'); },
      onOpenInterviewPrep: () => { spaNavigate('/prepare-for-interview'); },
      onFindMatchingJobs: () => {
        setJobsPanelVisible(true);
        const q = [profile?.currentRole, jobTitle].filter(Boolean).join(' ') || 'matching jobs';
        void fetchJobResultsRef.current(q, searchSource);
      },
      onOptimizeResume: () => {
        const sid = sessionRef.current?.id;
        if (!sid) return;
        const id = crypto.randomUUID();
        setMessages((prev) => [...prev, {
          id, sessionId: sid, role: 'assistant' as const,
          content: ASK_OPTIMIZE_JD_REPLY,
          createdAt: new Date().toISOString(),
        }]);
        composerRef.current?.focus?.();
      },
      onBuildResume: () => {
        void handleSubmitRef.current('I don\'t have a resume yet. Help me create one by asking about my experience.');
      },
      onCompleteProfile: () => {
        spaNavigate('/profile');
      },
      onWriteCoverLetter: () => {
        const sid = sessionRef.current?.id;
        if (!sid) return;
        void handleSubmitRef.current('Help me write a cover letter');
      },
      onWriteApplicationLetter: () => {
        const sid = sessionRef.current?.id;
        if (!sid) return;
        void handleSubmitRef.current('Draft a formal application letter');
      },
      onInterviewPrep: () => {
        spaNavigate('/prepare-for-interview');
      },
      onSkillAssessment: () => {
        spaNavigate('/skill-assessment');
      },
    };
  }, [canvasJob, profile?.currentRole, searchSource]);

  /** Attach proactive next-step copy + action chips to an assistant message. */
  const postCanvasEngagementMessage = useCallback((
    messageId: string | null,
    leadLine: string,
    opts: EngagementOptions = {},
  ) => {
    const snap = getCanvasWorkflowSnapshot(canvasRef.current.state);
    const content = buildEngagementContent(leadLine, snap, opts);

    const actions = buildWorkflowActions(snap, canvasEngagementHandlers(), opts);

    const attachActions = (id: string) => {
      if (actions.length > 0) {
        setMessageActions((prev) => ({ ...prev, [id]: actions }));
      }
    };

    if (messageId) {
      void updateMessageContent(messageId, content).catch(() => {});
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, content } : m)));
      attachActions(messageId);
      return;
    }

    const sid = sessionRef.current?.id;
    if (!sid) return;
    void appendMessage(sid, 'assistant', content)
      .then((persisted) => {
        setMessages((prev) => [...prev, { ...persisted, content }]);
        attachActions(persisted.id);
      })
      .catch(() => {});
  }, [canvasEngagementHandlers]);

  /** Ensure every assistant reply ends with next-step copy + up to 3 action chips. */
  const attachUniversalEngagement = useCallback((
    messageId: string,
    content: string,
    userText: string,
    skipAction?: string | null,
  ) => {
    const skipActions = new Set(['cover_letter', 'application_letter', 'optimize_resume']);
    if (skipAction && skipActions.has(skipAction)) return;
    if (!content.trim() || content.trim() === '…') return;

    const snap = getCanvasWorkflowSnapshot(canvasRef.current.state);
    const isProfileAssessment =
      activeCapability?.id === 'profile-assessment' || isProfileAssessmentIntent(userText);

    let nextContent = content;
    if (!hasEngagementFooter(content)) {
      const footer = buildGeneralEngagementFooter(snap, {
        profileRole: profile?.currentRole ?? undefined,
        isProfileAssessment,
      });
      nextContent = `${content.trim()}${footer}`;
      void updateMessageContent(messageId, nextContent).catch(() => {});
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, content: nextContent } : m)));
    }

    const capId = activeCapability?.id as CapabilityId | undefined;
    const profileHasResume = Boolean(profile?.resumeRawText?.trim());
    const profileHasSkills = (profile?.skills?.length ?? 0) > 2;
    const profileHasExperience = Boolean(profile?.currentRole?.trim());
    const profileComplete = profileHasResume && profileHasSkills;
    const handlers = canvasEngagementHandlers();

    setMessageActions((prev) => {
      if (prev[messageId]?.length) return prev;
      const actions = capId
        ? buildCapabilityEngagementActions(capId, snap, handlers, profileComplete)
        : buildGeneralEngagementActions(snap, handlers, { isProfileAssessment });
      if (actions.length === 0) return prev;
      return { ...prev, [messageId]: actions };
    });

    if (isProfileAssessment && !profileComplete) {
      const checks = [
        { label: 'Resume uploaded', done: profileHasResume },
        { label: 'Skills added (3+)', done: profileHasSkills },
        { label: 'Current role set', done: profileHasExperience },
        { label: 'Experience details', done: Boolean(profile?.experience?.length) },
      ];
      const completedCount = checks.filter((c) => c.done).length;
      const pct = Math.round((completedCount / checks.length) * 100);
      setMessageWidgets((prev) => ({
        ...prev,
        [messageId]: (
          <div className="agent-profile-completion mt-3 rounded-xl border p-4" style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-surface)' }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[14px] font-semibold" style={{ color: 'var(--ds-text)' }}>Profile Strength</p>
              <span className="text-[13px] font-bold" style={{ color: pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }}>{pct}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden mb-3" style={{ background: 'var(--ds-border)' }}>
              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: pct >= 75 ? '#10b981' : pct >= 50 ? '#f59e0b' : '#ef4444' }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {checks.map((c) => (
                <div key={c.label} className="flex items-center gap-2 text-[13px]">
                  <span className={`flex items-center justify-center h-5 w-5 rounded-full text-[11px] ${c.done ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-100 text-gray-400'}`}>
                    {c.done ? '✓' : '—'}
                  </span>
                  <span style={{ color: c.done ? 'var(--ds-text)' : 'var(--ds-text-secondary)' }}>{c.label}</span>
                </div>
              ))}
            </div>
            {pct < 100 && (
              <p className="mt-3 text-[12px]" style={{ color: 'var(--ds-text-secondary)' }}>
                Complete your profile to unlock better job matches and higher ATS scores.
              </p>
            )}
          </div>
        ),
      }));
    }
  }, [activeCapability, profile, canvasEngagementHandlers]);

  const handleApplyNudgeResolve = useCallback((
    outcome: 'applied' | 'not_applied' | 'later',
  ) => {
    const pending = returnNudge.pending;
    void returnNudge.resolve(outcome);
    if (!pending || outcome === 'later') return;

    const role = `**${pending.jobTitle}** at **${pending.company}**`;
    if (outcome === 'applied') {
      postCanvasEngagementMessage(
        null,
        `Marked ${role} as **applied** in My Activities. Nice work!`,
      );
    } else {
      postCanvasEngagementMessage(
        null,
        `Got it — ${role} stays **in progress**. You can retry apply anytime or refine your materials first.`,
      );
    }
  }, [returnNudge, postCanvasEngagementMessage]);

  /** Convenience wrapper — used by the job card "Optimize & Apply" CTA.
   *  Now uses the 7-step agentic flow with canvas. */
  const handleOptimizeForJob = useCallback(async (
    job: AgentJob,
    opts?: { skipDupCheck?: boolean; fromJobsHandoff?: boolean },
  ) => {
    if (!profile) {
      // Show a chat message so the user knows why nothing happened
      const currentSession = sessionRef.current;
      if (currentSession) {
        setMessages(prev => [...prev, {
          id: crypto.randomUUID(),
          sessionId: currentSession.id,
          role: 'assistant' as const,
          content: 'Your profile isn\'t loaded yet. Please complete your profile in **Settings → Profile** before optimizing a resume, then try again.',
          createdAt: new Date().toISOString(),
        }]);
      }
      return;
    }

    // ── Re-optimize guard ────────────────────────────────────────────────────
    // If the user already optimized or applied to this job, prompt them in chat
    // before kicking off a fresh round. This avoids silently duplicating work
    // and lets them refresh the artifacts with a single click.
    if (!opts?.skipDupCheck && session) {
      const prior = appliedJobsLookupRef.current(job.id, job.title, job.company);
      if (prior) {
        // Must match the DB CHECK constraint for job_applications.application_status.
        const isApplied =
          prior.status === 'applied' ||
          prior.status === 'interviewing' ||
          prior.status === 'offered' ||
          prior.status === 'accepted';
        const hasArtifacts = prior.hasResume || prior.hasCoverLetter;

        // Post user intent message first so the agent's prompt has context
        const userMsg: AgentMessageT = {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'user',
          content: `Optimize for ${job.title} at ${job.company}`,
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);

        const priorMatch = prior.matchScore != null ? ` Your last match score was **${prior.matchScore}%**.` : '';
        const activityLink = '\n\n[View in Activity →](/history)';
        const promptBody = isApplied
          ? `You already **applied** to **${job.title}** at **${job.company}** on ${new Date(prior.lastUpdated).toLocaleDateString()}.${priorMatch}\n\nWould you like to optimize a fresh version of your resume for it anyway?${activityLink}`
          : hasArtifacts
            ? `You already optimized your resume for **${job.title}** at **${job.company}** earlier.${priorMatch}\n\nWould you like to re-run the optimization with the latest profile data?${activityLink}`
            : `You viewed this role earlier but didn't optimize it yet. Want to start now?`;

        // Helper — hydrate canvas with the saved optimized resume so the user
        // immediately sees their previous result without having to re-run optimization.
        const openSavedInCanvas = () => {
          if (!prior.resumeMarkdown) {
            void pushAgentBellNotification(
              'Saved resume unavailable — try re-optimizing instead.',
              'agent_info',
            );
            return;
          }
          const jdText = buildJdFromJob(job);
          const listingUrl = job.url ?? prior.jobUrl ?? null;
          const jobCtx = {
            id: job.id,
            title: job.title,
            company: job.company,
            description: jdText,
            skills: job.must_have_skills ?? job.skills ?? [],
            requirements: job.requirements ?? [],
            url: listingUrl,
            location: job.location ?? undefined,
            sourceChannel: job.source_channel ?? undefined,
          };

          setCanvasJob({
            id: job.id,
            title: job.title,
            company: job.company,
            jdText,
            url: listingUrl,
            location: job.location ?? null,
            sourceChannel: job.source_channel ?? null,
          });

          const fromProfileCard = profileToResumeData(profile, { authEmail, authPhone });
          const fromText = resumePlainTextToResumeData(prior.resumeMarkdown);
          const resumeData = fromProfileCard ? {
            ...fromProfileCard,
            contact: {
              ...fromText.contact,
              ...fromProfileCard.contact,
              email: fromProfileCard.contact?.email || fromText.contact?.email,
              phone: fromProfileCard.contact?.phone || fromText.contact?.phone,
            }
          } : fromText;
          const canvasSections = applyOptimizedCanvasSections(
            prepareHydratedCanvasSections(
              prior.resumeMarkdown,
              profile,
              resumeData,
            ),
            profile,
            resumeData,
          );
          const seedCanonical = canvasSectionsToCanonicalResume(
            canvasSections,
            resumeData ? resumeDataToCanonical(resumeData) : null,
          );

          // Hydrate the canvas state machine with the saved data
          canvas.dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
          canvas.dispatch({ type: 'SET_TAB', tab: 'resume' });
          canvas.dispatch({ type: 'SET_JOB', job: jobCtx });
          if (resumeData) {
            canvas.dispatch({ type: 'SET_ORIGINAL_RESUME', data: resumeData });
          }
          canvas.dispatch({ type: 'SET_CANONICAL_RESUME', data: seedCanonical });
          canvas.dispatch({ type: 'SET_SECTIONS_DIRECT', sections: canvasSections });
          canvas.dispatch({ type: 'SET_COMMITTED_SECTIONS', sections: canvasSections });
          canvas.dispatch({ type: 'SET_PHASE', phase: 'fulfillment' });
          if (prior.matchScore != null) {
            canvas.dispatch({
              type: 'SET_ANALYSIS',
              analysis: {
                matchScore: prior.matchScore,
                gaps: [],
                strengths: [],
                suggestedTemplate: 'ats-optimized',
                keywordHits: [],
                keywordMisses: [],
              },
            });
          }
          // If there's a saved cover letter, dispatch it too so the tab shows content
          if (prior.coverLetter) {
            canvas.dispatch({ type: 'SET_COVER_LETTER', content: prior.coverLetter });
          }
          if (prior.applicationNote) {
            canvas.dispatch({ type: 'SET_APPLICATION_NOTE', content: prior.applicationNote });
          }
          void pushAgentBellNotification(
            'Opened your saved resume — you can edit, download, or re-optimize it.',
            'agent_info',
            job.id,
          );
        };

        const reoptActions: AgentMessageT['actions'] = [];
        if (prior.resumeMarkdown) {
          reoptActions.push({
            id: 'reopt-open-canvas',
            label: 'Open saved resume in canvas',
            variant: 'primary',
            onClick: openSavedInCanvas,
          });
        }
        reoptActions.push({
          id: 'reopt-yes',
          label: prior.resumeMarkdown ? 'Re-optimize fresh' : 'Yes, optimize again',
          variant: prior.resumeMarkdown ? 'secondary' : 'primary',
          onClick: () => { void handleOptimizeForJob(job, { skipDupCheck: true }); },
        });
        reoptActions.push({
          id: 'reopt-skip',
          label: 'Pick a different job',
          variant: 'secondary',
          onClick: () => { /* No-op — user stays in chat */ },
        });
        if (hasArtifacts) {
          reoptActions.push({
            id: 'reopt-activity',
            label: 'View in Activity',
            variant: 'secondary',
            onClick: () => { spaNavigate('/history'); },
          });
        }

        const reoptMsg: AgentMessageT = {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'assistant',
          content: promptBody,
          createdAt: new Date().toISOString(),
          actions: reoptActions,
        } as AgentMessageT;
        setMessages(prev => [...prev, reoptMsg]);

        // ── Auto-open the saved resume in canvas immediately so the user SEES it ──
        // Don't make them click an extra button when we already have the content.
        if (prior.resumeMarkdown) {
          openSavedInCanvas();
        }
        return;
      }
    }

    // Same resume source as single-optimize: longest among profile raw text, structured
    // build, session upload draft, multi-job store, guest store (see getBestResumePlainTextForAgent).
    const resumeFromAgent = getBestResumePlainTextForAgent(profile);
    const profileResume = buildResumePlainTextFromUserProfile(profile);
    const effectiveResumeText = resumeFromAgent.length >= 50 ? resumeFromAgent : profileResume;
    if (effectiveResumeText.length < 50) {
      if (session) {
        const errorMsg: AgentMessageT = {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'assistant',
          content: 'Your profile is too sparse to optimize. Upload a resume in My Documents or complete your profile sections first.',
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, errorMsg]);
      }
      return;
    }

    // Jobs tab handoff — open canvas immediately and run the agentic pipeline.
    // Get the precomputed match score from the recommendation engine (shown on job card)
    // so the canvas ATS score never drops below what the user saw.
    const recScore = jobRecs.get(job.id)?.score;
    const precomputedMatchScore = recScore !== undefined ? recScore : (job as AgentJob & { matchScore?: number }).matchScore;

    if (opts?.fromJobsHandoff) {
      if (session) {
        const title = `${job.title} @ ${job.company}`.slice(0, 200);
        void renameSession(session.id, title).then(() => {
          setSession((s) => (s?.id === session.id ? { ...s, title } : s));
          setHistoryKey((k) => k + 1);
        }).catch(() => { /* best-effort */ });
      }
      setChatVisible(true);
      if (!isDesktop) setMobileSplitTab('jobs');
      await startAgenticOptimize(job, effectiveResumeText, { precomputedMatchScore });
      return;
    }

    // Build job context for canvas (chat trail + job panel state).
    const jdText = buildJdFromJob(job);
    const listingUrl = job.url ?? null;
    setCanvasJob({
      id: job.id,
      title: job.title,
      company: job.company,
      jdText,
      url: listingUrl,
      location: job.location ?? null,
      sourceChannel: job.source_channel ?? null,
    });

    // Post user message (Jobs panel path — same agentic pipeline as mobile handoff).
    if (session) {
      const userMsg: AgentMessageT = {
        id: crypto.randomUUID(),
        sessionId: session.id,
        role: 'user',
        content: optimizeUserMessage(job.title, job.company),
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, userMsg]);
      void appendMessage(session.id, 'user', userMsg.content);
    }

    await startAgenticOptimize(job, effectiveResumeText, { precomputedMatchScore });
    if (!isDesktop) setMobileSplitTab('chat');
  }, [profile, session, startAgenticOptimize, isDesktop, jobRecs]);

  const handleAgentOptimizeFromJobs = useCallback((job: AgentJob) => {
    if (!isDesktop) {
      setMobileSplitTab('jobs');
      void handleOptimizeForJob(job, { fromJobsHandoff: true });
      return;
    }
    void handleOptimizeForJob(job);
  }, [isDesktop, handleOptimizeForJob]);
  handleAgentOptimizeFromJobsRef.current = handleAgentOptimizeFromJobs;

  const handleCanvasOpenConversation = useCallback(() => {
    setMobileAgentFollowUp(false);
    setMobileSplitTab('chat');
    requestAnimationFrame(() => composerRef.current?.focus?.());
  }, []);

  const handleCanvasStartOptimization = useCallback(async () => {
    const canvasJobCtx = canvasRef.current.state.job;
    if (!canvasJobCtx || !profile) return;
    const raw = jobResults?.find((j) => j.id === canvasJobCtx.id);
    const job = (raw ?? {
      id: canvasJobCtx.id,
      title: canvasJobCtx.title,
      company: canvasJobCtx.company,
      description: canvasJobCtx.description,
      skills: canvasJobCtx.skills,
      requirements: canvasJobCtx.requirements,
      url: canvasJobCtx.url ?? undefined,
      location: canvasJobCtx.location ?? undefined,
      source_channel: canvasJobCtx.sourceChannel ?? undefined,
    }) as AgentJob;
    if (!isDesktop) setMobileSplitTab('jobs');
    await handleOptimizeForJob(job, { fromJobsHandoff: true, skipDupCheck: true });
  }, [profile, jobResults, isDesktop, handleOptimizeForJob]);

  const handleOptimizeForJobRef = useRef(handleOptimizeForJob);
  handleOptimizeForJobRef.current = handleOptimizeForJob;
  const jobsHandoffStartedRef = useRef(false);

  /** Jobs tab "Optimize & Apply" → handoff consumed here once profile + session are ready. */
  useEffect(() => {
    if (!profile || !session || jobsHandoffStartedRef.current) return;
    const job = consumeAgentOptimizeHandoff();
    if (!job) return;
    jobsHandoffStartedRef.current = true;
    void handleOptimizeForJobRef.current(job, { fromJobsHandoff: true, skipDupCheck: true });
  }, [profile, session]);

  // ── React to phase transitions in the agentic hook ────────────────
  const donePostedRef = useRef(false);
  useEffect(() => {
    if (agentic.phase !== 'done' || !agentic.finalResult || !canvasJob || !session) return;
    if (donePostedRef.current) return;
    donePostedRef.current = true;
    if (!isDesktop) setMobileAgentFollowUp(true);

    // ── Debit wallet for successful optimization ──────────────────────
    if (!creditSystem.bypassEnabled) {
      void creditSystem.deductForOperation('resume_optimize');
    }

    const final = agentic.finalResult;
    const improvement = final.matchScoreAfter - final.matchScoreBefore;

    // Update the original stub message with a summary (widget stays attached to it)
    const lead = [
      `**Optimization complete.**`,
      '',
      `Your resume match improved from **${final.matchScoreBefore}%** to **${final.matchScoreAfter}%** (+${improvement} points).`,
      '',
      final.matchedSkills.length > 0 ? `**Skills highlighted:** ${final.matchedSkills.slice(0, 5).join(', ')}` : '',
      final.missingSkills.length > 0 ? `**Consider adding:** ${final.missingSkills.slice(0, 4).join(', ')}` : '',
    ].filter(Boolean).join('\n');

    console.log('[AIAgentPanel] 🎯 handleOptimizationComplete triggered', {
      sectionsCount: final.sections.length,
      sectionTitles: final.sections.map((s) => s.title),
      profileDisplayName: profile?.displayName,
      authEmail,
    });

    const { state: canvasState, dispatch: canvasDispatch } = canvasRef.current;
    canvasDispatch({ type: 'SET_PHASE', phase: 'fulfillment' });
    const fromProfileCard = profileToResumeData(profile, { authEmail, authPhone });
    
    console.log('[AIAgentPanel] 👤 Profile data for canvas:', {
      hasProfile: Boolean(profile),
      displayName: profile?.displayName,
      fromProfileCardName: fromProfileCard?.name,
    });
    
    let canonical = canvasState.canonicalResume;
    if (!canonical) {
      const fromText = resumePlainTextToResumeData(canvasState.rawResumeText || '');
      const resumeData = fromProfileCard ? {
        ...fromProfileCard,
        contact: {
          ...fromText.contact,
          ...fromProfileCard.contact,
          email: fromProfileCard.contact?.email || fromText.contact?.email,
          phone: fromProfileCard.contact?.phone || fromText.contact?.phone,
        }
      } : fromText;
      canonical = resumeData ? resumeDataToCanonical(resumeData) : null;
    }
    if (!canonical) {
      console.error('[AIAgentPanel] ❌ No canonical resume data available!');
      return;
    }
    
    // Log Personal Info in incoming sections
    const personalSection = final.sections.find((s) => /personal|contact|header/i.test(s.title));
    if (personalSection) {
      console.log('[AIAgentPanel] 👤 Personal Info from optimizer:', {
        title: personalSection.title,
        content: personalSection.content?.slice(0, 200),
        hasWorkBullets: /•.*\b(engineered|developed|led|managed|implemented)\b/i.test(personalSection.content || ''),
      });
    }
    
    const optimizedRows = applyOptimizedCanvasSections(
      final.sections.map((s) => ({ title: s.title, content: s.content })),
      profile,
      fromProfileCard,
    );
    
    console.log('[AIAgentPanel] 📝 After applyOptimizedCanvasSections:', {
      rowCount: optimizedRows.length,
      firstRowTitle: optimizedRows[0]?.title,
      firstRowContent: optimizedRows[0]?.content?.slice(0, 150),
    });
    
    for (const row of optimizedRows) {
      canonical = mergeOptimizedSectionIntoCanonical(canonical, row.title, row.content);
    }
    
    console.log('[AIAgentPanel] 📋 Canonical personalInfo after merge:', {
      fullName: canonical.personalInfo.fullName,
      email: canonical.personalInfo.email,
      phone: canonical.personalInfo.phone,
    });
    
    canvasDispatch({ type: 'SET_CANONICAL_RESUME', data: canonical });
    
    // Validate displayName is not a work experience bullet (can happen if profile got corrupted)
    const isValidDisplayName = (name: string | undefined | null): boolean => {
      if (!name) return false;
      const t = name.trim();
      if (!t || t.length > 80) return false;
      // Reject if starts with bullet character
      if (/^[\u2022\u2023\u2043•●○◦▪▸►‣⁃∙⋅◾◽\-*]/.test(t)) return false;
      // Reject if contains work experience action verbs
      if (/\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved)\b/i.test(t)) return false;
      return true;
    };
    const validDisplayName = isValidDisplayName(profile?.displayName) ? profile?.displayName : undefined;
    
    console.log('[AIAgentPanel] 👤 Display name validation:', {
      original: profile?.displayName?.slice(0, 60),
      isValid: isValidDisplayName(profile?.displayName),
      usingName: validDisplayName ?? '(none - will use canonical)',
    });
    
    // Pass profile data to ensure personal info is always correct (prevents work exp bullets in header)
    const canvasRows = canonicalResumeToCanvasSections(canonical, profile ? {
      displayName: validDisplayName,
      email: authEmail,
      phone: authPhone,
      location: profile.location,
      linkedinUrl: profile.linkedinUrl,
      githubUrl: profile.githubUrl,
    } : null);
    
    console.log('[AIAgentPanel] 🖼️ Final canvas rows:', {
      count: canvasRows.length,
      firstRow: canvasRows[0] ? {
        title: canvasRows[0].title,
        content: canvasRows[0].content?.slice(0, 150),
      } : null,
    });
    
    canvasDispatch({ type: 'SET_SECTIONS_DIRECT', sections: canvasRows });
    canvasDispatch({ type: 'SET_COMMITTED_SECTIONS', sections: canvasRows });

    // Update ATS ring with post-optimization score
    canvasDispatch({
      type: 'SET_ANALYSIS',
      analysis: {
        matchScore: final.matchScoreAfter,
        gaps: final.missingSkills.slice(0, 5),
        strengths: final.matchedSkills.slice(0, 5),
        suggestedTemplate: canvasState.templateId ?? 'ats-optimized',
        keywordHits: final.matchedSkills,
        keywordMisses: final.missingSkills,
      },
    });

    // Background Gemini refinement — only update canonical structure, NOT visible sections.
    // The optimized sections are already correct; Gemini just improves canonical metadata.
    void processResumeWithGemini({
      resumeText: final.fullText,
      jobDescription: [canvasJob.title, canvasJob.company, canvasJob.description].filter(Boolean).join('\n'),
    })
      .then((refined) => {
        canvasDispatch({ type: 'SET_CANONICAL_RESUME', data: refined });
      })
      .catch((err) => {
        console.warn('[ai-agent] post-optimize canonical refine failed — keeping local canonical:', err);
      });
    if (agentic.baselineReport) {
      canvasDispatch({
        type: 'SET_ANALYSIS',
        analysis: {
          matchScore: final.matchScoreAfter,
          gaps: final.missingSkills,
          strengths: final.matchedSkills,
          suggestedTemplate: 'ats-optimized',
          keywordHits: final.matchedSkills,
          keywordMisses: final.missingSkills,
        },
      });
    }

    const stubId = optimizingMsgId;
    if (stubId) {
      postCanvasEngagementMessage(stubId, lead);
    }

    // Clear the optimizing message ID (the original stub no longer needs updating)
    setOptimizingMsgId(null);

    // Persist agent_artifacts breadcrumb (fire-and-forget)
    void saveArtifact({
      sessionId: session.id,
      kind:      'optimization_summary',
      title:     `${canvasJob.title} — ${canvasJob.company}`,
      body:      final.fullText.slice(0, 12_000),
      metadata: {
        matchScoreBefore: final.matchScoreBefore,
        matchScoreAfter:  final.matchScoreAfter,
        missingSkills:    final.missingSkills,
        matchedSkills:    final.matchedSkills,
      },
      jobId: canvasJob.id.startsWith('synthetic-') ? null : canvasJob.id,
    }).catch(() => {});

    // Auto-save optimized resume to My Activities so it's available for Interview Prep
    const resumeMarkdown = final.sections.map((s) => `## ${s.title}\n${s.content}`).join('\n\n');
    console.log('[ai-agent] Auto-saving to My Activities:', { jobId: canvasJob.id, jobTitle: canvasJob.title, company: canvasJob.company });
    void saveAgentArtifacts({
      jobId:          String(canvasJob.id),
      jobTitle:       canvasJob.title,
      companyName:    canvasJob.company,
      jobUrl:         canvasJob.url ?? undefined,
      sourceChannel:  'other',
      resumeMarkdown: resumeMarkdown.length > 50 ? resumeMarkdown : undefined,
      matchScore:     final.matchScoreAfter,
      applicationStatus: 'in_progress',
    }).then(() => {
      console.log('[ai-agent] Auto-save to My Activities succeeded:', { jobTitle: canvasJob.title, company: canvasJob.company });
    }).catch((err) => {
      console.warn('[ai-agent] Auto-save to My Activities failed:', err);
    });

    // ── Engagement loop: suggest similar jobs after a short delay ──────
    const currentJobId = canvasJob.id;
    const sessionId = session.id;
    const name = profile?.displayName?.trim()?.split(' ')[0] || 'there';

    // Ensure job pool is loaded for left-panel suggestions (same feed as Jobs for You)
    if (!jobResultsRef.current?.length) {
      const role = profile?.currentRole?.trim() ?? canvasJob.title ?? 'engineer';
      const skills = (profile?.skills ?? []).slice(0, 3).join(' ');
      void fetchJobResultsRef.current([role, skills].filter(Boolean).join(' '), undefined, { openPanel: false });
    }

    window.setTimeout(() => {
      const pool = (jobResults ?? []).filter((j) => {
        if (j.id === currentJobId) return false;
        const rec = jobRecs.get(j.id);
        return rec ? rec.score >= 40 : true;
      });

      const similar = pool
        .sort((a, b) => {
          const sa = jobRecs.get(a.id)?.score ?? 0;
          const sb = jobRecs.get(b.id)?.score ?? 0;
          return sb - sa;
        })
        .slice(0, 3);

      if (similar.length === 0) return;

      const nudgeId = crypto.randomUUID();
      setMessages((prev) => [...prev, {
        id: nudgeId,
        sessionId,
        role: 'assistant' as const,
        content: `Hey ${name}, based on your profile and our matching algorithm, we think these roles could be a great fit for you. Tap any card to see the full details!`,
        createdAt: new Date().toISOString(),
      }]);

      const cardData: JobMiniCardData[] = similar.map((j) => {
        const applied = appliedJobsLookupRef.current(j.id, j.title, j.company);
        const ws = applied
          ? (applied.status === 'applied' || applied.status === 'interviewing' || applied.status === 'offered' || applied.status === 'accepted')
            ? 'applied' as const
            : (applied.hasResume || applied.hasCoverLetter) ? 'optimized' as const : null
          : null;
        return {
          id: j.id, title: j.title, company: j.company,
          company_logo_url: j.company_logo_url, location: j.location,
          score: jobRecs.get(j.id)?.score, source_channel: j.source_channel,
          workflowStatus: ws,
        };
      });

      setMessageWidgets((prev) => ({
        ...prev,
        [nudgeId]: (
          <JobMiniCards
            jobs={cardData}
            onOptimize={(jobId) => {
              const job = (jobResults ?? []).find((r) => r.id === jobId);
              if (job) handleAgentOptimizeFromJobs(job);
            }}
          />
        ),
      }));

      const chipActions: AgentAction[] = [
        {
          id: 'find-more-jobs',
          label: 'Find more jobs',
          variant: 'primary' as const,
          onClick: () => {
            const role = profile?.currentRole?.trim() ?? 'engineer';
            const skills = (profile?.skills ?? []).slice(0, 3).join(' ');
            canvas.dispatch({ type: 'SET_MODE', mode: 'LIST' });
            void fetchJobResults([role, skills].filter(Boolean).join(' '), undefined, { openPanel: true });
          },
        },
      ];

      setMessageActions((prev) => ({ ...prev, [nudgeId]: chipActions }));
    }, 3500);
  }, [agentic.phase, agentic.finalResult, canvasJob, session, postCanvasEngagementMessage, profile, isDesktop]);

  useEffect(() => {
    if (agentic.phase !== 'error' || !agentic.error) return;
    const id = optimizingMsgId;
    if (!id) return;
    setMessages(prev => prev.map(m => m.id === id ? { ...m, content: `Optimization failed: ${agentic.error}. You can try another job from the results panel.` } : m));
    setOptimizingMsgId(null);
  }, [agentic.phase, agentic.error, optimizingMsgId]);

  /** Mirror agentic pipeline progress into the canvas while Jobs handoff / agentic optimize runs. */
  useEffect(() => {
    const { state: canvasState, dispatch: canvasDispatch } = canvasRef.current;
    if (!optimizingMsgId || canvasState.mode !== 'CANVAS') return;
    if (agentic.phase !== 'baseline' && agentic.phase !== 'optimizing') return;

    if (canvasState.phase !== 'optimization') {
      canvasDispatch({ type: 'SET_PHASE', phase: 'optimization' });
    }

    if (agentic.sections.length > 0) {
      let nextCanonical = canvasState.canonicalResume;
      if (!nextCanonical) {
        const fromProfileCard = profileToResumeData(profile, { authEmail, authPhone });
        const fromText = resumePlainTextToResumeData(canvasState.rawResumeText || '');
        const resumeData = fromProfileCard ? {
          ...fromProfileCard,
          contact: {
            ...fromText.contact,
            ...fromProfileCard.contact,
            email: fromProfileCard.contact?.email || fromText.contact?.email,
            phone: fromProfileCard.contact?.phone || fromText.contact?.phone,
          }
        } : fromText;
        if (resumeData) nextCanonical = resumeDataToCanonical(resumeData);
      }
      if (nextCanonical) {
        for (const s of agentic.sections) {
          if (s.status === 'done' || s.status === 'skipped') {
            nextCanonical = mergeOptimizedSectionIntoCanonical(
              nextCanonical,
              s.title,
              s.optimizedContent || s.originalContent,
            );
          }
        }
        canvasDispatch({ type: 'SET_CANONICAL_RESUME', data: nextCanonical });
        
        // Validate displayName is not a work experience bullet
        const isValidName = (name: string | undefined | null): boolean => {
          if (!name) return false;
          const t = name.trim();
          if (!t || t.length > 80) return false;
          if (/^[\u2022\u2023\u2043•●○◦▪▸►‣⁃∙⋅◾◽\-*]/.test(t)) return false;
          if (/\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved)\b/i.test(t)) return false;
          return true;
        };
        const validName = isValidName(profile?.displayName) ? profile?.displayName : undefined;
        
        canvasDispatch({
          type: 'SET_SECTIONS_DIRECT',
          // Pass profile to ensure personal info is correct
          sections: canonicalResumeToCanvasSections(nextCanonical, profile ? {
            displayName: validName,
            email: authEmail,
            phone: authPhone,
            location: profile.location,
            linkedinUrl: profile.linkedinUrl,
            githubUrl: profile.githubUrl,
          } : null),
        });
      }
    }

    if (agentic.baselineReport) {
      canvasDispatch({
        type: 'SET_ANALYSIS',
        analysis: {
          matchScore: agentic.baselineReport.overallScore,
          gaps: [],
          strengths: agentic.baseline?.strengths ?? [],
          suggestedTemplate: 'ats-optimized',
          keywordHits: [],
          keywordMisses: agentic.baseline?.warnings ?? [],
        },
      });
    }
  }, [optimizingMsgId, agentic.phase, agentic.sections, agentic.baselineReport, agentic.baseline, profile]);

  // ── Reset optimization state when job context clears or new optimization starts ──
  useEffect(() => {
    if (!canvasOpen) {
      donePostedRef.current = false;
    }
  }, [canvasOpen]);

  // Reset donePostedRef when a new optimization starts (phase goes back to idle/baseline)
  // This ensures subsequent "Optimize for more roles" saves correctly to My Activities
  useEffect(() => {
    if (agentic.phase === 'idle' || agentic.phase === 'baseline') {
      donePostedRef.current = false;
    }
  }, [agentic.phase]);

  // ── Canvas action: close (swap back to jobs panel) ────────────────
  const handleCanvasClose = useCallback(() => {
    agentic.abort();
    agentic.reset();
    setCanvasJob(null);
    setCanvasResume('');
    setCanvasSaved(false);
    setCanvasSaveErr(null);
    setCanvasApplied(false);
    optimizationIdRef.current = null;
  }, [agentic]);

  // ── Canvas action: save to llm_optimization_level2 + agent_artifacts ──
  const handleCanvasSave = useCallback(async () => {
    if (!canvasJob || !agentic.finalResult || canvasSaving || canvasSaved) return;
    setCanvasSaving(true);
    setCanvasSaveErr(null);

    const isValidUuid = (s: string) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(s);

    try {
      const final = agentic.finalResult;
      const baseline = agentic.baseline;
      const safeResumeId = profile?.id && isValidUuid(profile.id) ? crypto.randomUUID() : crypto.randomUUID();
      const safeJobId    = isValidUuid(canvasJob.id) ? canvasJob.id : crypto.randomUUID();

      const optimizationResult = {
        id:               `opt_${Date.now()}`,
        resumeId:         safeResumeId,
        jobId:            safeJobId,
        jobTitle:         canvasJob.title,
        companyName:      canvasJob.company,
        model:            'agentic-per-section',
        optimizedText:    final.fullText,
        optimizedResume:  final.fullText,
        final_resume:     final.fullText,
        optimized_resume: final.fullText,
        sections:         final.sections,
        originalScore:    final.matchScoreBefore,
        optimizedScore:   final.matchScoreAfter,
        beforeScores: baseline ?? {
          formatting: final.matchScoreBefore, keywords: final.matchScoreBefore,
          structure:  final.matchScoreBefore, overall:   final.matchScoreBefore,
          atsRating:  'Fair' as const,
        },
        afterScores: {
          formatting: Math.min(100, (baseline?.formatting ?? final.matchScoreBefore) + 18),
          keywords:   Math.min(100, (baseline?.keywords   ?? final.matchScoreBefore) + 22),
          structure:  Math.min(100, (baseline?.structure  ?? final.matchScoreBefore) + 15),
          overall:    final.matchScoreAfter,
          atsRating:  (final.matchScoreAfter >= 85 ? 'Excellent' : final.matchScoreAfter >= 70 ? 'Good' : 'Fair') as 'Excellent' | 'Good' | 'Fair',
        },
        improvements: final.improvements,
        confidence:   0.9,
        completedAt:  new Date(),
        ai_insights:  { selectedTemplateId: canvasTemplate, sections: final.sections },
      };

      const saved = await saveComprehensiveOptimization(safeResumeId, safeJobId, optimizationResult as any);
      optimizationIdRef.current = saved.optimization_id ?? null;
      setCanvasSaved(true);

      // Breadcrumb in agent_artifacts (fire-and-forget)
      void saveArtifact({
        sessionId: session?.id,
        kind:      'optimization_summary',
        title:     `${canvasJob.title} — ${canvasJob.company}`,
        body:      final.fullText.slice(0, 12_000),
        metadata: {
          optimization_id:   saved.optimization_id,
          matchScoreBefore:  final.matchScoreBefore,
          matchScoreAfter:   final.matchScoreAfter,
          missingSkills:     final.missingSkills,
        },
        jobId: canvasJob.id.startsWith('synthetic-') ? null : canvasJob.id,
      }).catch(() => {});
    } catch (err) {
      console.warn('[ai-agent] handleCanvasSave failed:', err);
      setCanvasSaveErr((err as Error)?.message ?? 'Save failed');
    } finally {
      setCanvasSaving(false);
    }
  }, [canvasJob, agentic.finalResult, agentic.baseline, canvasTemplate, canvasSaving, canvasSaved, profile?.id, session?.id]);

  // ── Canvas action: save optimized resume to user profile ──────────
  const [profileSaved, setProfileSaved] = useState(false);
  const handleSaveToProfile = useCallback(async () => {
    const sections = canvasRef.current.state.sections ?? [];
    if (!sections.length || !profile) return;

    const fullText = sections.map((s) => `## ${s.title}\n${s.content}`).join('\n\n');
    try {
      await saveProfile({
        resumeRawText: fullText,
        resumeSource: 'manual' as const,
        resumeUpdatedAt: new Date().toISOString(),
      });
      setProfileSaved(true);

      if (session) {
        const confirmMsg: AgentMessageT = {
          id: crypto.randomUUID(),
          sessionId: session.id,
          role: 'assistant',
          content: 'Your optimized resume has been saved to your profile. It will be used for future job matching and optimization.',
          createdAt: new Date().toISOString(),
        };
        setMessages(prev => [...prev, confirmMsg]);
      }
    } catch (err) {
      console.warn('[ai-agent] handleSaveToProfile failed:', err);
    }
  }, [profile, session, saveProfile]);

  // ── Canvas action: download (PDF / DOCX / TXT) ────────────────────
  // Reads from `canvas.state.sections` — the canvas reducer is the single
  // source of truth for what's actually on screen. The legacy `agentic`
  // hook is a parallel system that the new state-machine flow never
  // populates, which is why this handler used to silently early-return.
  const handleCanvasDownload = useCallback(async (format: DownloadFormat, options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    const job = canvasRef.current.state.job ?? canvasJob;
    const sections = canvasRef.current.state.sections ?? [];

    console.log('[ai-agent] handleCanvasDownload invoked', {
      format,
      silent,
      hasJob: !!job,
      sectionCount: sections.length,
      phase: canvasRef.current.state.phase,
    });

    if (!job) {
      console.warn('[ai-agent] handleCanvasDownload: no job in canvas state — aborting');
      setCanvasSaveErr('No job selected. Optimize for a job first.');
      return;
    }
    if (sections.length === 0) {
      console.warn('[ai-agent] handleCanvasDownload: no resume sections to export — aborting');
      setCanvasSaveErr('No resume content yet. Finish optimizing first.');
      return;
    }

    try {
      const safeName = (job.title || 'Resume').replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Resume_${safeName}.${format}`;

      // Use the canvas's actual selected template, mapped via TEMPLATE_LIBRARY
      const canvasTplId = canvasRef.current.state.templateId ?? 'ats-optimized';
      const tplSkin = TEMPLATE_LIBRARY.find((t) => t.id === canvasTplId)?.skin ?? 'blue-classic';
      const tplId = tplSkin as TemplateId;

      // Build the plain-text resume from the canvas sections (clean for export).
      const exportSections = prepareSectionsForExport(sections);
      const docSections = exportSections.map((s) => ({
        title:   s.title,
        content: s.content,
        preview: s.content.slice(0, 120),
      }));
      const fullText = docSections
        .map((s) => `${s.title.toUpperCase()}\n${s.content}`)
        .join('\n\n');
      const text = normalizeResumeText(fullText);

      let blob: Blob;
      if (format === 'txt') {
        blob = new Blob(['\uFEFF' + text], { type: 'text/plain;charset=utf-8' });
      } else if (format === 'pdf') {
        blob = await generateResumePDF(text, docSections, '', tplId);
      } else {
        blob = await generateResumeDOCX(text, docSections, '', tplId);
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('[ai-agent] handleCanvasDownload: downloaded', { fileName });
      if (!silent) {
        void pushAgentBellNotification(`Resume downloaded: ${fileName}`, 'agent_info');
      }
    } catch (err) {
      console.error('[ai-agent] handleCanvasDownload failed:', err);
      setCanvasSaveErr(`Download failed: ${(err as Error)?.message ?? 'unknown error'}`);
      if (!silent) {
        void pushAgentBellNotification(
          `Download failed: ${(err as Error)?.message ?? 'Unknown error'}`,
          'agent_info',
        );
      }
    }
  }, [canvasJob, pushAgentBellNotification]);

  // ── Canvas action: email resume to user ──────────────────────────
  const handleCanvasEmail = useCallback(async () => {
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) {
        setCanvasSaveErr('Please sign in to use Email Me.');
        return;
      }
      const userEmail = authSession.user?.email;
      if (!userEmail) {
        setCanvasSaveErr('No email address found for your account.');
        return;
      }

      // Build plain-text resume from canvas sections
      const canvasSections = canvasRef.current.state.sections ?? [];
      const resumeText = canvasSections
        .map((s) => `${s.title.toUpperCase()}\n${s.content}`)
        .join('\n\n');

      if (!resumeText.trim()) {
        setCanvasSaveErr('No resume content to send yet. Optimize first.');
        return;
      }

      const job = canvasRef.current.state.job;
      const response = await supabase.functions.invoke('send-resume-email', {
        headers: { Authorization: `Bearer ${authSession.access_token}` },
        body: {
          recipientEmail: userEmail,
          recipientName: profile?.displayName ?? authSession.user?.email?.split('@')[0] ?? 'there',
          resumeText,
          jobTitle: job?.title ?? '',
          companyName: job?.company ?? '',
          format: 'pdf',
          fileName: `Resume_${(job?.title ?? 'Optimized').replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
        },
      });

      if (response.error) throw new Error(response.error.message);

      // Debit for resume email send
      void import('../../services/creditLedgerService').then(({ ledger }) =>
        ledger.emailSend('resume', { job_title: canvasRef.current.state.job?.title }),
      );

      setMessages((prev) => [
        ...prev,
        {
          id: `email-sent-${Date.now()}`,
          sessionId: session?.id ?? '',
          role: 'assistant' as const,
          content: `Resume sent to **${userEmail}**. Check your inbox!`,
          ts: new Date().toISOString(),
        },
      ]);
    } catch (err) {
      console.warn('[ai-agent] handleCanvasEmail failed:', err);
      setCanvasSaveErr(`Email failed: ${(err as Error)?.message ?? 'unknown error'}`);
    }
  }, [profile, session]);

  // ── Canvas action: apply (Assisted Handoff orchestration) ──────────────────
  //
  // This is the "Apply" button on the resume canvas. We don't actually submit
  // to the portal (impossible cross-origin + ToS-violating) — instead we make
  // the user's life as easy as possible right before they hit the portal:
  //
  //   1. Copy cover letter to clipboard       (so paste-into-textarea works)
  //   2. Trigger PDF download                 (so the file is in Downloads)
  //   3. Save row as 'in_progress'            (not 'applied' — they haven't
  //                                            actually submitted yet)
  //   4. Open the portal in a new tab         (must be inside the user gesture)
  //   5. Arm the return-nudge                 (when they come back, prompt
  //                                            them to confirm whether they
  //                                            actually applied)
  //
  // Steps 1, 2, and 4 must run synchronously (or near-synchronously) so the
  // browser still associates them with the click — popup blockers and the
  // clipboard API both require the active user gesture.
  const handleCanvasApply = useCallback(async () => {
    const job = canvasRef.current.state.job ?? canvasJob;
    const sections = canvasRef.current.state.sections ?? [];

    console.log('[ai-agent] handleCanvasApply invoked', {
      hasJob: !!job,
      jobId: job?.id,
      title: job?.title,
      sectionCount: sections.length,
      phase: canvasRef.current.state.phase,
    });

    if (!job) {
      console.warn('[ai-agent] handleCanvasApply: no job in canvas state — aborting');
      return;
    }

    const jobId = (job as { id?: string }).id ?? canvasJob?.id;
    const panelJob =
      jobId && jobResults ? jobResults.find((j) => j.id === jobId) : undefined;
    const priorApp = appliedJobsLookupRef.current(
      jobId,
      job.title,
      job.company,
    );
    const externalUrl = resolveJobListingUrl({
      canvasJob,
      canvasStateJob: canvasRef.current.state.job,
      panelJob,
      savedJobUrl: priorApp?.jobUrl,
    });

    console.log('[ai-agent] handleCanvasApply: resolved externalUrl', {
      externalUrl,
      jobId,
      panelUrl: panelJob?.url,
      canvasJobUrl: canvasJob?.url,
      canvasStateUrl: canvasRef.current.state.job?.url,
      savedJobUrl: priorApp?.jobUrl,
    });

    // ── Synchronous user-gesture steps ────────────────────────────────────
    // window.open, navigator.clipboard.writeText, and the anchor-click
    // download all need to run inside the same user-activation tick.
    // We do NOT await between them — each call is initiated synchronously
    // (the returned promises resolve later in the background) so the
    // activation flag survives for all three.

    // 0) IMMEDIATE feedback — fires synchronously so the user sees something
    //    the instant they click, even if everything else hits popup
    //    blockers, missing URLs, or LLM latency downstream. Without this
    //    the click feels like it did nothing because the download toast
    //    masks the apply flow and the final save toast appears 10–30s
    //    later (after auto-letter generation).
    void pushAgentBellNotification(
      externalUrl
        ? `Opening ${job.company} application — downloading resume, copying cover letter, opening portal.`
        : `Preparing application for ${job.company} — bundle is saving to My Activities (no portal URL on this listing).`,
      'apply_handoff',
    );

    // 1) Kick off clipboard write (no await — keeps activation alive).
    //    The actual write happens in the microtask queue; we await the
    //    result later just to surface it in the toast.
    const cState = canvasRef.current.state;
    const coverLetterForClipboard =
      typeof cState.coverLetter === 'string' && cState.coverLetter.trim().length > 50
        ? cState.coverLetter
        : '';
    const clipboardPromise: Promise<boolean> = coverLetterForClipboard
      ? safeClipboardWrite(coverLetterForClipboard).catch(() => false)
      : Promise.resolve(false);

    // 2) Trigger PDF download (silent — its own success toast would mask
    //    the apply progress messages). Fire-and-forget; the anchor-click
    //    fires under transient activation.
    if (sections.length > 0) {
      void handleCanvasDownload('pdf', { silent: true });
    }

    // 3) Open the portal IMMEDIATELY in the user gesture — popup blockers
    //    only fire if this happens after an await.
    let portalOpened = false;
    if (externalUrl) {
      const opened = window.open(externalUrl, '_blank', 'noopener,noreferrer');
      portalOpened = !!opened;
      if (!opened) {
        console.warn('[ai-agent] handleCanvasApply: window.open returned null — popup may be blocked', { externalUrl });
      }
    } else {
      console.warn('[ai-agent] handleCanvasApply: no externalUrl on this job — skipping window.open', {
        jobId: job.id,
        title: job.title,
      });
    }

    // From here on, all async work can use await freely — the portal is
    // already opening and the bundle is already underway.
    const clipboardCopied = await clipboardPromise;

    // ── Step 3 (deferred): persist as 'in_progress' + arm return-nudge ──
    try {
      const resumeMarkdown = sections
        .map((s) => `## ${s.title}\n${s.content}`)
        .join('\n\n');

      const matchScore =
        cState.analysis?.matchScore
        ?? agentic.finalResult?.matchScoreAfter;

      // Save what we already have — DO NOT block on LLM letter generation.
      // Missing letters get filled in by a background job (kicked off below)
      // which patches the same row when it completes. The user sees the
      // "Bundle ready" toast in under a second instead of after 10–30s.
      const coverLetterToSave: string | undefined =
        typeof cState.coverLetter === 'string' && cState.coverLetter.trim().length > 50
          ? cState.coverLetter
          : undefined;
      const appNoteToSave: string | undefined =
        typeof cState.applicationNote === 'string' && cState.applicationNote.trim().length > 50
          ? cState.applicationNote
          : undefined;

      console.log('[ai-agent] handleCanvasApply: saving artifacts', {
        jobId: String(job.id),
        applicationStatus: 'in_progress',
        resumeLen: resumeMarkdown.length,
        coverLen: coverLetterToSave?.length ?? 0,
        appNoteLen: appNoteToSave?.length ?? 0,
        matchScore,
      });

      const saved = await saveAgentArtifacts({
        jobId:          String(job.id),
        jobTitle:       job.title,
        companyName:    job.company,
        jobUrl:         externalUrl ?? undefined,
        sourceChannel:  'other',
        resumeMarkdown: resumeMarkdown.length > 50 ? resumeMarkdown : undefined,
        coverLetter:    coverLetterToSave,
        applicationNote: appNoteToSave,
        matchScore,
        optimizationId: optimizationIdRef.current ?? undefined,
        // Assisted Handoff: mark as in_progress now — the return-nudge will
        // flip this to 'applied' or 'not_applied' once the user confirms
        // they actually submitted on the portal.
        applicationStatus: 'in_progress',
      });

      // ── Background: fill in any missing letters and patch the row ──────
      // We fire this AFTER the save so the user gets immediate feedback.
      // Failure here doesn't affect the apply outcome.
      const needsCover = !coverLetterToSave;
      const needsApp   = !appNoteToSave;
      if (saved && (needsCover || needsApp)) {
        const optimizedResume = resumeMarkdown.slice(0, 6000);
        void Promise.all([
          needsCover
            ? generateLetter({
                mode:    'cover',
                resume:  optimizedResume,
                jobTitle: job.title || 'the role',
                company:  job.company || 'this company',
                tone:    'professional',
                locale:  getDocumentResponseLanguage(cState.documentLocale),
              }).catch((e) => { console.warn('[ai-agent] cover letter gen failed:', e); return null; })
            : Promise.resolve(null),
          needsApp
            ? generateLetter({
                mode:    'application',
                resume:  optimizedResume,
                jobTitle: job.title || 'the role',
                company:  job.company || 'this company',
                locale:  getDocumentResponseLanguage(cState.documentLocale),
              }).catch((e) => { console.warn('[ai-agent] app note gen failed:', e); return null; })
            : Promise.resolve(null),
        ]).then(([coverRes, appRes]) => {
          const patchCover = needsCover && coverRes?.letter && !letterLooksLikeSafetyRefusal(coverRes.letter)
            ? coverRes.letter.trim() : undefined;
          const patchApp   = needsApp && appRes?.letter && !letterLooksLikeSafetyRefusal(appRes.letter)
            ? appRes.letter.trim() : undefined;
          if (patchCover) canvasRef.current.dispatch({ type: 'SET_COVER_LETTER', content: patchCover });
          if (patchApp)   canvasRef.current.dispatch({ type: 'SET_APPLICATION_NOTE', content: patchApp });
          if (patchCover || patchApp) {
            void saveAgentArtifacts({
              jobId:           String(job.id),
              jobTitle:        job.title,
              companyName:     job.company,
              jobUrl:          externalUrl ?? undefined,
              sourceChannel:   'other',
              coverLetter:     patchCover,
              applicationNote: patchApp,
              optimizationId:  optimizationIdRef.current ?? undefined,
              // Do NOT pass applicationStatus — we want to preserve whatever
              // the return-nudge has set it to by now.
            });
          }
        });
      }

      console.log('[ai-agent] handleCanvasApply: save result', {
        ok: !!saved,
        rowId: saved?.id,
        status: saved?.application_status,
      });

      // We mark the button as "applied" in canvas state optimistically so the
      // green check appears immediately — the persisted status is still
      // 'in_progress' and will only become 'applied' on confirmation.
      setCanvasApplied(true);

      // Arm the return-nudge so the next visible-after-30s-hidden event
      // surfaces the "Did you finish applying?" prompt.
      if (saved?.id && externalUrl) {
        returnNudge.arm({
          applicationId: saved.id,
          jobTitle:      job.title || 'this role',
          company:       job.company || 'this company',
        });
      }

      // Final summary toast — replaces the "Opening…" toast that fired on
      // click and tells the user exactly what landed where.
      if (saved) {
        const bundleParts: string[] = [];
        if (sections.length > 0) bundleParts.push('PDF downloaded');
        if (clipboardCopied) bundleParts.push('cover letter copied');
        bundleParts.push('tracked in My Activities');

        if (externalUrl) {
          if (portalOpened) {
            void pushAgentBellNotification(
              `Bundle ready for ${job.title} at ${job.company}: ${bundleParts.join(' · ')}. Confirm in the Agent tab when you're back from the portal.`,
              'apply_handoff',
              saved.id,
            );
          } else {
            void pushAgentBellNotification(
              `Popup blocked for ${job.company}. ${bundleParts.join(' · ')}. Allow popups or open the listing from Jobs.`,
              'apply_handoff',
              saved.id,
            );
          }
        } else {
          void pushAgentBellNotification(
            `Application bundle ready for ${job.title} at ${job.company}: ${bundleParts.join(' · ')}. No portal URL — submit on the company site, then mark as Applied.`,
            'apply_handoff',
            saved.id,
          );
        }
      } else {
        void pushAgentBellNotification(
          `Could not save your application to ${job.company}. Check the browser console for details.`,
          'apply_handoff',
        );
      }

      // Post a confirmation in the chat with proactive next steps + action chips.
      if (session) {
        const bundleBits: string[] = [];
        if (clipboardCopied) bundleBits.push('cover letter copied to clipboard');
        if (sections.length > 0) bundleBits.push('PDF downloaded');
        const bundlePrefix = bundleBits.length ? ` Bundle ready (${bundleBits.join(', ')}).` : '';
        const opened = externalUrl
          ? ' Opening the application page in a new tab — I\'ll ask you to confirm when you\'re back.'
          : '';
        const failure = saved ? '' : ' (Save to My Activities failed — check console.)';
        const leadLine = saved
          ? `Prepared your application to **${job.title}** at **${job.company}**.${bundlePrefix}${opened}`
          : `Tried to record your application to **${job.title}** at **${job.company}** but persistence failed.${opened}${failure}`;

        postCanvasEngagementMessage(null, leadLine, {
          postApplyHandoff: true,
          portalOpened: Boolean(externalUrl),
        });
      }
    } catch (err) {
      console.error('[ai-agent] handleCanvasApply failed:', err);
      void pushAgentBellNotification(
        `Apply failed: ${(err as Error)?.message ?? 'Unknown error'}`,
        'apply_handoff',
      );
    }
  }, [
    canvasJob,
    agentic.finalResult,
    session,
    handleCanvasDownload,
    returnNudge,
    jobResults,
    pushAgentBellNotification,
    postCanvasEngagementMessage,
  ]);

  // ── Bridge: chat hyperlinks → canvas actions ───────────────────────────────
  // The state machine emits CustomEvents because it doesn't have direct access
  // to the AIAgentPanel callbacks. We listen here and forward to the handlers.
  // Stable refs prevent the event listener from being torn down and re-attached
  // on every canvas.state update (which caused 20+ reattachments per session).
  const handleCanvasDownloadRef = useRef(handleCanvasDownload);
  const handleCanvasApplyRef    = useRef(handleCanvasApply);
  useEffect(() => { handleCanvasDownloadRef.current = handleCanvasDownload; }, [handleCanvasDownload]);
  useEffect(() => { handleCanvasApplyRef.current = handleCanvasApply; }, [handleCanvasApply]);

  useEffect(() => {
    const onDownloadEvt = (e: Event) => {
      const detail = (e as CustomEvent<{ format?: DownloadFormat }>).detail;
      void handleCanvasDownloadRef.current(detail?.format ?? 'pdf');
    };
    const onApplyEvt = () => {
      void handleCanvasApplyRef.current();
    };
    const onNavigateEvt = (e: Event) => {
      const detail = (e as CustomEvent<{ path?: string }>).detail;
      if (detail?.path) spaNavigate(detail.path);
    };
    window.addEventListener('ggj:agent-download', onDownloadEvt as EventListener);
    window.addEventListener('ggj:agent-apply', onApplyEvt as EventListener);
    window.addEventListener('ggj:agent-navigate', onNavigateEvt as EventListener);
    return () => {
      window.removeEventListener('ggj:agent-download', onDownloadEvt as EventListener);
      window.removeEventListener('ggj:agent-apply', onApplyEvt as EventListener);
      window.removeEventListener('ggj:agent-navigate', onNavigateEvt as EventListener);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — refs always have the latest handler

  // ── Handle deep-link actions from FloatingCompanion (?action=...) ──
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (!action) return;
    // Clean URL
    const url = new URL(window.location.href);
    url.searchParams.delete('action');
    window.history.replaceState({}, '', url.pathname + url.search);

    const actionMap: Record<string, string> = {
      'optimize-resume': 'Optimize my resume for my target role',
      'find-jobs': 'Find matching jobs for my profile',
      'cover-letter': 'Write a cover letter for my latest application',
      'interview-prep': 'Help me prepare for an upcoming interview',
    };
    const prompt = actionMap[action];
    if (prompt) {
      setTimeout(() => {
        const composer = document.querySelector<HTMLTextAreaElement>('[data-agent-composer]');
        if (composer) {
          composer.value = prompt;
          composer.dispatchEvent(new Event('input', { bubbles: true }));
          composer.focus();
        }
      }, 500);
    }
  }, []);

  // ── Canvas action: search similar jobs (close canvas → re-search) ──
  const handleCanvasSearchSimilar = useCallback(() => {
    if (!canvasJob) return;
    const q = canvasJob.title;
    handleCanvasClose();
    void fetchJobResults(q, searchSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasJob, handleCanvasClose, searchSource]);

  // ── Letter generation (cover letter / application letter) ────────
  /**
   * Handle cover_letter or application_letter action from the LLM.
   * Uses ai-companion edge function (JWT auth) — NOT api-cover-letter.
   */
  const handleLetterAction = useCallback(async (
    mode: 'cover' | 'application',
    actionData: { jobTitle?: string; company?: string; tone?: string; referredBy?: string },
    replyMsgId: string,
  ) => {
    // ── Credit gate: letters cost 2 credits each ──────────────────────
    const letterOp = mode === 'cover' ? 'cover_letter' : 'application_letter';
    const letterCost = getCreditCost(letterOp as 'cover_letter' | 'application_letter');
    if (!creditSystem.bypassEnabled && !creditSystem.hasEnoughCredits(letterCost)) {
      const label = mode === 'cover' ? 'Cover Letter' : 'Application Letter';
      openCreditGate(label, letterCost);
      const gatedMsg = `You need ${letterCost} credits to generate a ${label.toLowerCase()}. Tap "Unlock Unlimited Access" to continue.`;
      setMessages(prev => prev.map(m =>
        m.id === replyMsgId ? { ...m, content: gatedMsg } : m
      ));
      return;
    }

    if (!profile) {
      const noProfileMsg = 'Please complete your profile first so I can draft a personalized letter.';
      setMessages(prev => prev.map(m =>
        m.id === replyMsgId
          ? { ...m, content: noProfileMsg }
          : m
      ));
      attachUniversalEngagement(replyMsgId, noProfileMsg, '', null);
      return;
    }

    // Cancel any in-flight letter generation
    letterAbortRef.current?.abort();
    const ctl = new AbortController();
    letterAbortRef.current = ctl;

    setIsGeneratingLetter(true);
    setGeneratingLetterType(mode);
    setProcessingMsgId(replyMsgId);

    const loadingText = mode === 'cover'
      ? draftingCoverLetterMessage(actionData.jobTitle || 'the role', actionData.company || 'the company')
      : draftingApplicationLetterMessage(actionData.jobTitle || 'the role', actionData.company || 'the company');

    setMessages(prev => prev.map(m =>
      m.id === replyMsgId ? { ...m, content: loadingText } : m
    ));

    const streamSection = mode === 'application' ? 'application_note' : 'cover_letter';
    const { dispatch: canvasDispatch } = canvasRef.current;
    canvasDispatch({ type: 'SET_MODE', mode: 'CANVAS' });
    canvasRef.current.startStreaming(streamSection);
    setWorkspaceLetter(null);

    try {
      const resumeText = buildResumePlainTextFromUserProfile(profile);

      const request: LetterRequest = {
        mode,
        resume: resumeText,
        jobTitle: actionData.jobTitle || 'the role',
        company: actionData.company || 'the company',
        tone: actionData.tone,
        referredBy: actionData.referredBy,
        locale: getDocumentResponseLanguage(canvasRef.current.state.documentLocale),
      };

      const result = await generateLetter(request, ctl.signal);

      if (ctl.signal.aborted) return;

      if (letterLooksLikeSafetyRefusal(result.letter)) {
        const fallback = buildFallbackLetter(
          mode,
          actionData.jobTitle || 'the role',
          actionData.company || 'the company',
          resumeText,
          { referredBy: actionData.referredBy },
        );
        canvasRef.current.appendStream(fallback);
        if (mode === 'application') {
          canvasRef.current.dispatch({ type: 'SET_APPLICATION_NOTE', content: fallback });
        } else {
          canvasRef.current.dispatch({ type: 'SET_COVER_LETTER', content: fallback });
        }
        await waitForCanvasTypewriter(fallback);
        canvasRef.current.stopStreaming();

        const leadLine = createdLetterLeadLine(mode, actionData.jobTitle || 'the role', actionData.company || 'the company');
        postCanvasEngagementMessage(replyMsgId, leadLine);
        return;
      }

      canvasRef.current.appendStream(result.letter);
      if (mode === 'application') {
        canvasRef.current.dispatch({ type: 'SET_APPLICATION_NOTE', content: result.letter });
      } else {
        canvasRef.current.dispatch({ type: 'SET_COVER_LETTER', content: result.letter });
      }
      await waitForCanvasTypewriter(result.letter);
      canvasRef.current.stopStreaming();
      setWorkspaceLetter(null);

      const leadLine = createdLetterLeadLine(
        mode,
        actionData.jobTitle || 'the role',
        actionData.company || 'the company',
      );
      postCanvasEngagementMessage(replyMsgId, leadLine);

      // ── Debit wallet for successful letter generation ───────────────
      if (!creditSystem.bypassEnabled) {
        const letterOp = mode === 'cover' ? 'cover_letter' : 'application_letter';
        void creditSystem.deductForOperation(letterOp as 'cover_letter' | 'application_letter');
      }

      // Persist artifact (fire-and-forget)
      void saveArtifact({
        sessionId: session?.id,
        kind:      mode === 'cover' ? 'cover_letter' : 'application_letter',
        title:     `${actionData.jobTitle || 'Role'} — ${actionData.company || 'Company'}`,
        body:      result.letter,
        metadata:  { ...actionData },
      }).catch(() => {});

      // ── Suggest similar jobs after letter completion ──────
      const currentJobId = canvasRef.current.state.job?.id;
      const currentResults = jobResultsRef.current;
      const currentRecs = jobRecsRef.current;
      const sid = session?.id;
      if (sid && currentResults && currentResults.length > 0) {
        window.setTimeout(() => {
          const pool = currentResults.filter((j) => {
            if (j.id === currentJobId) return false;
            const rec = currentRecs.get(j.id);
            return rec ? rec.score >= 40 : true;
          });
          const similar = pool
            .sort((a, b) => (currentRecs.get(b.id)?.score ?? 0) - (currentRecs.get(a.id)?.score ?? 0))
            .slice(0, 3);
          if (similar.length === 0) return;

          const userName = profile?.displayName?.trim()?.split(' ')[0] || 'there';
          const jobNudgeId = crypto.randomUUID();
          setMessages((prev) => [...prev, {
            id: jobNudgeId,
            sessionId: sid,
            role: 'assistant' as const,
            content: `Hey ${userName}, while you're at it — our algorithm found these roles that match your profile really well. Tap any card to explore!`,
            createdAt: new Date().toISOString(),
          }]);

          const cardData: JobMiniCardData[] = similar.map((j) => {
            const applied = appliedJobsLookupRef.current(j.id, j.title, j.company);
            const ws = applied
              ? (applied.status === 'applied' || applied.status === 'interviewing' || applied.status === 'offered' || applied.status === 'accepted')
                ? 'applied' as const
                : (applied.hasResume || applied.hasCoverLetter) ? 'optimized' as const : null
              : null;
            return {
              id: j.id, title: j.title, company: j.company,
              company_logo_url: j.company_logo_url, location: j.location,
              score: currentRecs.get(j.id)?.score, source_channel: j.source_channel,
              workflowStatus: ws,
            };
          });

          setMessageWidgets((prev) => ({
            ...prev,
            [jobNudgeId]: (
              <JobMiniCards
                jobs={cardData}
                onOptimize={(jobId) => {
                  const job = (jobResultsRef.current ?? []).find((r) => r.id === jobId);
                  if (job) handleAgentOptimizeFromJobsRef.current(job);
                }}
              />
            ),
          }));

          setMessageActions((prev) => ({
            ...prev,
            [jobNudgeId]: [{
              id: 'find-more-jobs',
              label: 'Find more jobs',
              variant: 'primary' as const,
              onClick: () => {
                const role = profile?.currentRole?.trim() ?? 'engineer';
                const skills = (profile?.skills ?? []).slice(0, 3).join(' ');
                canvasRef.current.dispatch({ type: 'SET_MODE', mode: 'LIST' });
                void fetchJobResultsRef.current([role, skills].filter(Boolean).join(' '), undefined, { openPanel: true });
              },
            }],
          }));
        }, 3000);
      }

    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      console.error('[ai-agent] handleLetterAction error:', err);
      const errMsg = `Sorry, I couldn't generate the ${mode === 'cover' ? 'cover' : 'application'} letter right now. Please try again.`;
      setMessages(prev => prev.map(m =>
        m.id === replyMsgId ? { ...m, content: errMsg } : m
      ));
      attachUniversalEngagement(replyMsgId, errMsg, '', null);
    } finally {
      canvasRef.current.stopStreaming();
      setIsGeneratingLetter(false);
      setProcessingMsgId(null);
      letterAbortRef.current = null;
    }
  }, [profile, session?.id, postCanvasEngagementMessage, attachUniversalEngagement, creditSystem.bypassEnabled, creditSystem.credits, openCreditGate]);

  useEffect(() => {
    handleLetterActionRef.current = handleLetterAction;
  }, [handleLetterAction]);

  // ── Web search helper ─────────────────────────────────────────────
  const triggerWebSearch = useCallback((query: string) => {
    setWebResults([]);
    setWebSearchQuery(query);
    setWebLoading(true);
    performWebSearch(query).then((res) => {
      setWebResults(res);
      setWebLoading(false);
    }).catch(() => { setWebLoading(false); setWebResults([]); });
  }, []);

  /**
   * Detect job search intent directly from user text.
   * Returns a clean query string, or null if not a job-search request.
   * This fires REGARDLESS of whether the LLM emits an action tag — small
   * local models (llama3.2:1b, etc.) often skip the tag entirely.
   */
  function detectJobSearchIntent(userText: string): string | null {
    const t = userText.toLowerCase();

    // Reject vague career questions — these should NOT trigger a job search
    const VAGUE_REJECT = [
      /^(hi|hello|hey|sup|yo|howdy)\b/,
      /\b(what should i do|help me with my career|how do i get a job|career advice|what next)\b/,
      /\b(how can i|how do i|what can i|tips for|advice on|guide me)\b.{0,30}(career|interview|resume|job search)\b/,
      /\b(tell me about|explain|what is|what are)\b/,
    ];
    if (VAGUE_REJECT.some((re) => re.test(t))) return null;

    const JOB_TRIGGERS = [
      /\b(find|search|show|get|list|fetch|give me|look for|looking for)\b.{0,40}\b(job|jobs|role|roles|opening|openings|position|positions|vacancy|vacancies|listing|listings)\b/,
      /\b(job|jobs|role|roles|opening|openings|position|positions)\b.{0,30}\b(for me|matching|match|available|hiring|near|in|at|remote)\b/,
      /\b(who is hiring|companies hiring|currently hiring)\b/,
      /\b(show me results|show me the results|show results|show jobs)\b/,
      /\b(recommended|recommend)\s*(job|jobs|role|roles|position|positions)\b/,
      /\b(jobs?|roles?|positions?)\s*(that\s+)?(match|suit|fit)\s+(my|me|profile)\b/,
      /\b(search for)\s+(roles?|jobs?|positions?|openings?)\b/,
      /\b(software engineer|product (designer|manager)|data scientist|full stack|frontend|backend|devops|sre|marketing|designer|developer|analyst|intern)\b.{0,20}\b(job|jobs|role|roles|opening|position)\b/,
      /\b(job|jobs)\b.{0,20}\b(software engineer|product|data|full stack|frontend|backend|devops|designer|developer|analyst)\b/,
      /\bfind.{0,30}(engineer|developer|designer|manager|analyst|scientist|developer|intern)\b/,
    ];
    if (!JOB_TRIGGERS.some((re) => re.test(t))) return null;

    // Build a clean query: strip filler phrases, keep the role/intent
    const query = userText
      .replace(/\b(find|search for|show me|get me|list|fetch|give me|look for)\b/gi, '')
      .replace(/\b(can you|please|could you|go ahead and|i want|i need|i am looking for|i'm looking for)\b/gi, '')
      .replace(/\b(job|jobs|opening|openings|role|roles|position|positions|listing|listings)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^[,:\s]+/, '')
      .trim();
    return query.length > 2 ? query : userText.trim();
  }

  /** Detect if user explicitly wants a web/online search (LLM fallback). */
  function detectWebSearchIntent(userText: string): string | null {
    const t = userText.toLowerCase();
    const WEB_TRIGGERS = [
      /\b(search online|search the web|search internet|web search|online search)\b/,
      /\b(do (online |web |internet |a )?research)\b/,
      /\b(find (online|on the web|on internet))\b/,
      /\b(look (it )?up online)\b/,
      /\b(google|bing|duckduckgo)\b.*\b(this|it|for|about)\b/,
      /\b(browse|scrape)\b.*\b(web|internet|online)\b/,
      /\bshow me (links|sites|websites|pages|results)\b/,
      /\b(latest|current|recent|live|real.time)\b.*\b(news|info|data|results|updates)\b/,
    ];
    if (!WEB_TRIGGERS.some((re) => re.test(t))) return null;
    // Extract the query: remove trigger keywords, keep the subject
    const cleaned = userText
      .replace(/\b(search online|search the web|do online research|do web research|find online|show me links|show me sites|look up online|browse the web)\b/gi, '')
      .replace(/\b(can you|please|could you|go ahead and)\b/gi, '')
      .trim()
      .replace(/^[,:\s]+/, '')
      .trim();
    return cleaned.length > 2 ? cleaned : userText;
  }

  /** Detect explicit app navigation (jobs, dashboard, etc.) — not resume canvas. */
  function detectNavigateIntent(userText: string): CompanionDashboardPage | null {
    if (isProfileAssessmentIntent(userText)) return null;

    const t = userText.toLowerCase();
    if (/\b(my activities|application history|applied jobs)\b/.test(t)) return 'my-activities';
    if (/\b(interview prep|prepare for interview)\b/.test(t)) return 'prepare-for-interview';
    if (/\baccount settings\b/.test(t)) return 'profile';
    if (/\b(go to|open|take me to|show me|navigate to)\b.{0,30}\b(profile settings|my profile page)\b/.test(t)) return 'profile';
    if (/\b(edit|update|change)\b.{0,24}\b(my )?profile\b/.test(t)) return 'profile';
    if (/\b(buy credits|get credits)\b/.test(t)) return 'credits';
    if (/\b(go to|open|take me to|show me)\b.{0,30}\b(jobs?|job board)\b/.test(t)) return 'jobs';
    if (/\b(go to|open|take me to)\b.{0,30}\b(dashboard|home)\b/.test(t)) return 'dashboard';
    return null;
  }

  function isAffirmativeReply(userText: string): boolean {
    return /^(yes|yeah|yep|yup|yesh|ok|okay|sure|do it|go ahead|please do|let'?s do it|sounds good)\b/i.test(
      userText.trim(),
    );
  }

  function lastAssistantOfferedJobSearch(recentMessages: AgentMessageT[]): string {
    const lastAssistant = [...recentMessages].reverse().find((m) => m.role === 'assistant');
    if (!lastAssistant?.content) return '';
    const c = lastAssistant.content;
    if (!/\b(find|search|show|let me find|matching|top|fresh|awesome)\b/i.test(c)) return '';
    if (!/\b(job|role|opening|position|product owner|product manager)\b/i.test(c)) return '';

    const rolePhrase =
      c.match(/\b((?:senior|lead|principal|staff|head of)\s+)?(product owner|product manager|program manager)[^.!?\n]*/i)?.[0]
      ?? c.match(/\b((?:senior|lead|principal|staff|head of)\s+[a-z\s]{3,40}?)\s+(?:role|job|opening|position)s?\b/i)?.[0]
      ?? '';
    const locPhrase = c.match(/\bin\s+([A-Za-z][A-Za-z\s,]{2,40}?)(?:[.!?\n]|$)/i)?.[1]?.trim() ?? '';

    const parts = [
      rolePhrase.replace(/\s+(role|job|opening|position)s?\b/i, '').trim(),
      locPhrase,
    ].filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
    return profile?.currentRole?.trim() ?? '';
  }

  function extractJobQueryFromAgentReply(agentText: string): string {
    const rolePhrase =
      agentText.match(/\b((?:senior|lead|principal|staff|head of)\s+)?(product owner|product manager|program manager)[^.!?\n]*/i)?.[0]
      ?? agentText.match(/\b((?:senior|lead|principal|staff|head of)\s+[a-z\s]{3,40}?)\s+(?:role|job|opening|position)s?\b/i)?.[0]
      ?? '';
    const locPhrase = agentText.match(/\bin\s+([A-Za-z][A-Za-z\s,]{2,40}?)(?:[.!?\n]|$)/i)?.[1]?.trim() ?? '';
    const parts = [
      rolePhrase.replace(/\s+(role|job|opening|position)s?\b/i, '').trim(),
      locPhrase,
    ].filter(Boolean);
    return parts.join(' ').trim();
  }

  async function startOptimizeFromChatJd(userText: string): Promise<void> {
    if (!session || !profile) return;
    const resumeText = buildResumePlainTextFromUserProfile(profile);
    if (!resumeText || resumeText.trim().length < 50) return;

    setComposerValue('');
    const userMsg: AgentMessageT = {
      id: `optim-user-${Date.now()}`,
      sessionId: session.id,
      role: 'user',
      content: userText,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    void appendMessage(session.id, 'user', userText);

    const meta = parseJobMetaFromText(userText);
    await startAgenticOptimize(
      {
        id: `synthetic-${Date.now()}`,
        title: meta.title,
        company: meta.company,
        description: userText,
      },
      resumeText,
      { skipUserChatMessage: true },
    );
  }

  async function runProfileJobSearchShortcut(
    userText: string,
    searchQuery: string,
    statusLine = 'Searching for matching jobs…',
  ): Promise<void> {
    if (!session) return;
    setComposerValue('');
    const userMsg: AgentMessageT = {
      id: `job-user-${Date.now()}`, sessionId: session.id, role: 'user', content: userText, ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    void appendMessage(session.id, 'user', userText);

    const statusId = `job-status-${Date.now()}`;
    const statusMsg: AgentMessageT = {
      id: statusId, sessionId: session.id, role: 'assistant',
      content: statusLine, ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, statusMsg]);

    try {
      const { source: jobSource, query: resolvedQuery } = resolveJobSearchParams(searchQuery);
      if (jobSource === 'internal') setSearchSource('internal');
      await fetchJobResultsRef.current(resolvedQuery, jobSource);
      setMessages((prev) =>
        prev.map((m) => m.id === statusId
          ? { ...m, content: 'Found matching jobs across all sources — check the right panel to browse and apply.\n\n**What would you like to do next?**\n- **Optimize your resume** for any of these roles\n- **Search with different keywords** or location\n- **Get interview prep tips** for a specific role' }
          : m),
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) => m.id === statusId
          ? { ...m, content: 'Could not fetch jobs right now. Please try again.' }
          : m),
      );
    }
  }

    // ── Submit ─────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (text: string) => {
    if (!session || isStreaming) return;

    const rawText = text.trim();
    if (!rawText) return;

    const canvasPhaseEarly = canvas.state.phase;
    const canvasHasContextEarly =
      (canvas.state.mode === 'CANVAS' || canvasOpen) &&
      Boolean(canvas.state.job ?? canvasJob) &&
      canvasPhaseEarly !== 'idle';

    text = normalizeUserCommandTypos(rawText);
    if (!text) return;

    // Detect intent upfront — used as fallback if LLM doesn't emit an action tag
    const jobIntent = detectJobSearchIntent(text);
    const webIntent = detectWebSearchIntent(text);

    const explicitCanvasIntent = detectExplicitCanvasOpenIntent(text);
    const optimizeIntent = detectOptimizeResumeIntent(text);
    const awaitingOptimizeJd = lastAssistantAwaitingOptimizeJd(messages);
    const jdPayload = looksLikeJobDescription(text);

    // ── Optimize resume: collect JD in chat first — do not open canvas yet ──
    if (optimizeIntent && !jdPayload) {
      const userMsg: AgentMessageT = {
        id: `optim-user-${Date.now()}`, sessionId: session.id, role: 'user', content: text, createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      void appendMessage(session.id, 'user', text);
      const reply: AgentMessageT = {
        id: `optim-reply-${Date.now()}`, sessionId: session.id, role: 'assistant',
        content: ASK_OPTIMIZE_JD_REPLY,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, reply]);
      void appendMessage(session.id, 'assistant', reply.content);
      setComposerValue('');
      return;
    }

    if (jdPayload && (awaitingOptimizeJd || optimizeIntent) && profile) {
      await startOptimizeFromChatJd(text);
      return;
    }

    if (isAffirmativeReply(text) && awaitingOptimizeJd) {
      const userMsg: AgentMessageT = {
        id: `optim-user-${Date.now()}`, sessionId: session.id, role: 'user', content: text, createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      void appendMessage(session.id, 'user', text);
      const reply: AgentMessageT = {
        id: `optim-reply-${Date.now()}`, sessionId: session.id, role: 'assistant',
        content: REMIND_OPTIMIZE_JD_REPLY,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, reply]);
      void appendMessage(session.id, 'assistant', reply.content);
      setComposerValue('');
      return;
    }

    // ── Explicit canvas open only ("open canvas", "show optimizer") ─────
    if (explicitCanvasIntent) {
      if (canvasOpen && canvas.state.mode === 'CANVAS') {
        const msg: AgentMessageT = {
          id: `optim-user-${Date.now()}`, sessionId: session.id, role: 'user', content: text, createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, msg]);
        const reply: AgentMessageT = {
          id: `optim-reply-${Date.now()}`, sessionId: session.id, role: 'assistant',
          content: 'The resume canvas is open on the right panel. Paste a job description in chat when you are ready to tailor it.',
          createdAt: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, reply]);
        setComposerValue('');
        return;
      }
      openAgentResumeCanvas();
      const msg: AgentMessageT = {
        id: `optim-user-${Date.now()}`, sessionId: session.id, role: 'user', content: text, createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, msg]);
      void appendMessage(session.id, 'user', text);
      const reply: AgentMessageT = {
        id: `optim-reply-${Date.now()}`, sessionId: session.id, role: 'assistant',
        content: 'Resume canvas is open on the right — your profile sections are loaded. Paste the job description here when you want me to optimize.',
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, reply]);
      void appendMessage(session.id, 'assistant', reply.content);
      setComposerValue('');
      return;
    }

    // Affirmative reply after agent offered job search ("sure", "yes", …)
    const offeredJobQuery = isAffirmativeReply(text) ? lastAssistantOfferedJobSearch(messages) : '';
    if (offeredJobQuery && !canvasHasContextEarly) {
      await runProfileJobSearchShortcut(text, offeredJobQuery);
      return;
    }

    // ── Job-search shortcut — bypass LLM entirely ─────────────────────
    // When the user clearly wants to see jobs ("show me jobs", "find roles
    // matching my profile", etc.) we skip the slow LLM round-trip and
    // immediately trigger fetchJobResults with status feedback in chat.
    if (jobIntent && !canvasHasContextEarly) {
      setComposerValue('');
      const userMsg: AgentMessageT = {
        id: `job-user-${Date.now()}`, sessionId: session.id, role: 'user', content: text, ts: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      void appendMessage(session.id, 'user', text);

      // Show a searching status in chat
      const statusId = `job-status-${Date.now()}`;
      const statusMsg: AgentMessageT = {
        id: statusId, sessionId: session.id, role: 'assistant',
        content: 'Searching for matching jobs…', ts: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, statusMsg]);

      // Derive a meaningful query from the user's input + profile
      const profileRole = profile?.currentRole?.trim() ?? '';
      const profileFallback = [
        profileRole,
        ...(profile?.skills ?? []).slice(0, 2),
      ].filter(Boolean).join(' ') || 'software engineer';

      const intentKeywordCount = jobIntent
        ? jobIntent.toLowerCase().replace(/[^a-z0-9.\s]/g, ' ').split(/\s+/)
            .map((t) => t.replace(/^\.+|\.+$/g, ''))
            .filter((t) => t.length > 2 && !new Set(['find','show','me','get','list','give','search','jobs','job','role','roles','position','positions','opening','openings','for','in','at','from','with','that','the','a','an','and','or','want','looking','need','hiring','available']).has(t)).length
        : 0;

      // When user provides specific keywords, use THOSE directly rather than
      // falling back to profile role. This ensures "find .NET jobs" actually
      // searches for .NET, not "Product Manager".
      const hasExplicitKeywords = intentKeywordCount > 0;
      const cleanQuery =
        hasExplicitKeywords
          ? jobIntent
          : (jobIntent.length > 2 && isProfileIntentJobQuery(jobIntent, intentKeywordCount) && profileRole)
            ? profileRole
            : (jobIntent.length > 2 ? jobIntent : profileFallback);

      try {
        const { source: jobSource, query: resolvedQuery } = resolveJobSearchParams(cleanQuery);
        if (jobSource === 'internal') setSearchSource('internal');

        await fetchJobResultsRef.current(resolvedQuery, jobSource);
        const successMessage = hasExplicitKeywords
          ? `Found jobs matching **${jobIntent.trim()}** across all sources. Browse and apply from the right panel.`
          : `Found matching jobs for your profile across all sources. Browse and apply from the right panel.`;
        const engagementSuffix = [
          '',
          '',
          '**What would you like to do next?**',
          '- **Optimize your resume** for any of these roles',
          '- **Search with different keywords** or location',
          '- **Get interview prep tips** for a specific role',
        ].join('\n');
        setMessages((prev) =>
          prev.map((m) => m.id === statusId
            ? { ...m, content: successMessage + engagementSuffix }
            : m),
        );
        void appendMessage(session.id, 'assistant', successMessage + engagementSuffix);
      } catch {
        setMessages((prev) =>
          prev.map((m) => m.id === statusId
            ? { ...m, content: 'Could not fetch jobs right now. Please try again in a moment.' }
            : m),
        );
        void appendMessage(session.id, 'assistant', 'Could not fetch jobs right now. Please try again.');
      }
      return;
    }

    // Canvas phase — used by tool-call router only
    const canvasPhase = canvas.state.phase;

    /** Canvas has a job context — route ALL edits through classifyCanvasToolCall (no regex shortcuts). */
    const canvasHasContext =
      (canvas.state.mode === 'CANVAS' || canvasOpen) &&
      Boolean(canvas.state.job ?? canvasJob) &&
      canvasPhase !== 'idle';

    setComposerValue('');
    setIsStreaming(true);

    const optimisticUser: AgentMessageT = {
      id: `optim-user-${Date.now()}`,
      sessionId: session.id,
      role: 'user',
      content: text,
      ts: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);

    try {
      const persistedUser = await appendMessage(session.id, 'user', text);
      setMessages((prev) => prev.map((m) => (m.id === optimisticUser.id ? persistedUser : m)));

      setSession((prev) => {
        if (!prev || !isGenericSessionTitle(prev.title)) return prev;
        return { ...prev, title: deriveSessionTitle(text) };
      });

      const stubAssistant = await appendMessage(session.id, 'assistant', '');
      setMessages((prev) => [...prev, { ...stubAssistant, content: '' }]);
      setStreamProcessingMsgId(stubAssistant.id);
      setStreamStatusLabels([]);

      // Letter requests: call ai-companion directly (skip local Ollama) — avoids refusals + fake "drafted" text.
      const earlyApplicationLetter =
        /\b(application\s*letter)\b/i.test(text) ||
        /\b(write|draft)\s+(an?\s+)?application\s+letter\b/i.test(text) ||
        /\bwrite\s+application\s+letter\b/i.test(text);
      const earlyCoverLetter =
        (/\b(cover\s*letter)\b/i.test(text) ||
          /\b(write|draft)\s+(an?\s+)?cover\s+letter\b/i.test(text) ||
          /\bwrite\s+cover\s+letter\b/i.test(text)) &&
        !earlyApplicationLetter;

      if (profile && (earlyApplicationLetter || earlyCoverLetter)) {
        const data = {
          jobTitle:
            canvas.state.job?.title ??
            canvasJob?.title ??
            profile.currentRole ??
            'the role',
          company: canvas.state.job?.company ?? canvasJob?.company ?? '',
          tone: 'professional' as const,
        };
        if (earlyApplicationLetter) {
          await handleLetterAction('application', data, stubAssistant.id);
        } else {
          await handleLetterAction('cover', data, stubAssistant.id);
        }
        setHistoryKey((k) => k + 1);
        return;
      }

      // ── Canvas: tool-call router ──────────────────────────────────────────────
      // The LLM picks exactly one tool: reorder_sections | patch_section | chat_only.
      // Action and chat response are returned in the same API call — the agent
      // can never claim it updated the canvas without actually updating it.

      if (canvasHasContext) {
        const sectionPreviews: Record<string, string> = {};
        for (const s of canvas.state.sections ?? []) {
          sectionPreviews[s.title] = s.content.replace(/\s+/g, ' ').slice(0, 180);
        }

        const toolCall = await classifyCanvasToolCall(
          text,
          {
            jobTitle:       canvas.state.job?.title   ?? '',
            jobCompany:     canvas.state.job?.company  ?? '',
            activeSections: (canvas.state.sections ?? []).map((s) => s.title),
            currentPhase:   canvasPhase,
            sectionPreviews,
          },
          undefined,
          messages.slice(-8).map((m) => ({ role: m.role, content: m.content })),
        );

        const replyId       = stubAssistant.id;
        const currentSections = canvas.state.sections ?? [];
        const currentJob    = canvas.state.job;

        // ── reorder_sections — pure array sort, zero content rewrite ─────────
        if (toolCall.tool === 'reorder_sections') {
          // parsePlacementFromUserText handles typos better; LLM section key as fallback
          const placement = parsePlacementFromUserText(text);
          const moveKey   = placement?.moveKey ?? toolCall.move_section;

          if (moveKey) {
            const reorder = applySectionReorder(currentSections, moveKey, text.trim());
            if (reorder.moved) {
              canvas.dispatch({ type: 'SET_SECTIONS', sections: reorder.sections });
              const newOrder      = reorder.sections.map((s) => s.title).join(' → ');
              const placementNote = placement
                ? ` (now ${placement.placement === 'before' ? 'above' : 'below'} **${canonicalSectionTitle(placement.anchorKey)}**)`
                : '';
              postCanvasEngagementMessage(
                replyId,
                `Moved **${canonicalSectionTitle(moveKey)}**${placementNote}.\n\nSection order: ${newOrder}`,
              );
            } else {
              // Section missing — ask rather than silently create
              postCanvasEngagementMessage(
                replyId,
                `**${canonicalSectionTitle(moveKey)}** isn't on your canvas yet. Say **"add ${canonicalSectionTitle(moveKey)} section"** to create it first.`,
              );
            }
          } else {
            // No specific section mentioned — sort all into canonical order
            const sorted = sortSectionsByCanonicalOrder(currentSections.map((s) => ({ title: s.title, content: s.content })));
            canvas.dispatch({ type: 'SET_SECTIONS', sections: sorted });
            postCanvasEngagementMessage(replyId, toolCall.chat_response || 'Sections sorted into standard resume order.');
          }
          setIsStreaming(false);
          setHistoryKey((k) => k + 1);
          return;
        }

        // ── remove_section — delete section row, no content rewrite ───────────
        if (toolCall.tool === 'remove_section') {
          const removeKey =
            toolCall.section ||
            parseRemoveSectionKeyFromText(text) ||
            parseCanvasSectionKeyFromText(text);
          if (removeKey) {
            const removal = removeSectionByKey(currentSections, removeKey);
            if (removal.removed) {
              canvas.dispatch({ type: 'REMOVE_SECTION', sectionKey: removeKey });
              const remaining = removal.sections.map((s) => s.title).join(' → ') || '(none)';
              postCanvasEngagementMessage(
                replyId,
                `${toolCall.chat_response}\n\nRemoved **${removal.removedTitle ?? canonicalSectionTitle(removeKey)}**.\n\nRemaining sections: ${remaining}`,
              );
            } else {
              postCanvasEngagementMessage(
                replyId,
                removal.message ?? `No **${canonicalSectionTitle(removeKey)}** section on the canvas.`,
              );
            }
          } else {
            postCanvasEngagementMessage(
              replyId,
              'Which section should I remove? (e.g. **Awards**, **Research**, **References**)',
            );
          }
          setIsStreaming(false);
          setHistoryKey((k) => k + 1);
          return;
        }

        // ── rewrite_all — full resume re-optimization (LLM must explicitly choose this) ──
        if (toolCall.tool === 'rewrite_all') {
          if (canvas.state.mode !== 'CANVAS') {
            canvas.dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
            canvas.dispatch({ type: 'SET_TAB', tab: 'resume' });
          }
          if (canvasPhase === 'fulfillment') {
            canvas.dispatch({ type: 'SNAPSHOT_ORIGINAL_SECTIONS' });
          }
          await updateMessageContent(replyId, toolCall.chat_response).catch(() => {});
          setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, content: toolCall.chat_response } : m)));
          void agentStateMachine.startOptimizationPhase?.(['summary', 'experience', 'skills']);
          setIsStreaming(false);
          setHistoryKey((k) => k + 1);
          return;
        }

        // ── patch_section — update one section's content ──────────────────────
        if (toolCall.tool === 'patch_section') {
          const sectionKey   = toolCall.section;
          const displayTitle = canonicalSectionTitle(sectionKey);
          const existingIdx  = findSectionIndexByKey(currentSections, sectionKey);
          const existingSection = existingIdx >= 0 ? currentSections[existingIdx] : undefined;
          let sectionContent = existingSection?.content ?? '';
          if (!existingSection) {
            const seed = buildSectionSeedFromProfile(sectionKey, profile, canvas.state.originalResumeData);
            if (seed) sectionContent = seed;
          }

          canvas.setTab('resume');
          const ackText = `Updating **${displayTitle}** — watch the canvas.`;
          await updateMessageContent(replyId, ackText).catch(() => {});
          setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, content: ackText } : m)));

          try {
            let optimizedContent: string;

            if (sectionKey === 'personal') {
              // Contact block: deterministic merge from profile — no LLM needed
              optimizedContent = buildPersonalSectionContent(
                sectionContent, profile, canvas.state.originalResumeData, { enrichMissing: true },
              );
            } else if (
              wantsSectionFormattingEmphasis(text) ||
              wantsSectionFormattingEmphasis(toolCall.instruction)
            ) {
              optimizedContent = applyBoldEmphasisToSectionContent(sectionContent, sectionKey);
            } else if (
              wantsDummyOrSampleFill(text) ||
              wantsDummyOrSampleFill(toolCall.instruction) ||
              sectionContentHasPlaceholders(sectionContent)
            ) {
              optimizedContent =
                buildDummySectionContent(sectionKey, profile, canvas.state.originalResumeData) ?? sectionContent;
            } else {
              // Scoped context: only this section's content + job description.
              // Passing the full resume caused hallucination (scrambled sections).
              const jobCtx = {
                title:        currentJob?.title        ?? '',
                company:      currentJob?.company       ?? '',
                description:  (currentJob?.description  ?? '').slice(0, 1500),
                skills:       currentJob?.skills        ?? [],
                requirements: currentJob?.requirements  ?? [],
              };
              const preserveExpand =
                wantsPreserveAndExpandContent(text) ||
                wantsPreserveAndExpandContent(toolCall.instruction);
              const hasSubstantiveContent = sectionContent.trim().length > 80;

              let instruction = toolCall.instruction;
              if (preserveExpand && hasSubstantiveContent && sectionKey === 'experience') {
                instruction += '\n\nCRITICAL: Keep every existing employer name, job title, and date range exactly as written. Only add or expand bullets with metrics.';
              }

              const result = await optimizeSection(
                { title: displayTitle, content: sectionContent },
                jobCtx,
                undefined,
                {
                  candidateName:           canvas.state.originalResumeData?.name     ?? profile?.displayName ?? undefined,
                  headline:                canvas.state.originalResumeData?.headline  ?? profile?.headline    ?? undefined,
                  skills:                  canvas.state.originalResumeData?.skills    ?? [],
                  generateMode:            !existingSection || sectionContentHasPlaceholders(sectionContent),
                  preserveExistingContent: preserveExpand && hasSubstantiveContent,
                },
                instruction,
              );
              optimizedContent = result.optimizedContent;

              // Employer hallucination guard for experience
              if (preserveExpand && hasSubstantiveContent && sectionKey === 'experience') {
                const missing = employersMissingFromRewrite(sectionContent, optimizedContent);
                if (missing.length > 0) {
                  const retry = await optimizeSection(
                    { title: displayTitle, content: sectionContent },
                    jobCtx,
                    undefined,
                    {
                      candidateName: canvas.state.originalResumeData?.name ?? profile?.displayName ?? undefined,
                      preserveExistingContent: true,
                      skills: [],
                    },
                    `${instruction}\n\nYou MUST preserve these employers exactly: ${missing.join(', ')}.`,
                  );
                  const stillMissing = employersMissingFromRewrite(sectionContent, retry.optimizedContent);
                  optimizedContent = stillMissing.length === 0 ? retry.optimizedContent : sectionContent;
                }
              }
            }

            // Stream word-by-word to canvas
            canvas.dispatch({ type: 'START_STREAMING', section: displayTitle });
            const words = optimizedContent.split(' ');
            for (let i = 0; i < words.length; i++) {
              canvas.dispatch({ type: 'APPEND_STREAM', content: words[i] + ' ' });
              await new Promise((r) => setTimeout(r, 20));
            }
            canvas.dispatch({ type: 'STOP_STREAMING' });

            const sanitized = sanitizeSectionContentForCanvas(optimizedContent, displayTitle);
            let nextSections = [...currentSections];
            if (existingIdx >= 0) {
              nextSections[existingIdx] = { title: displayTitle, content: sanitized };
            } else {
              nextSections.push({ title: displayTitle, content: sanitized });
            }
            canvas.dispatch({ type: 'SET_SECTIONS', sections: dedupeCanvasSections(nextSections) });

            const draftHint =
              canvas.state.isDraftDirty || !canvas.state.committedSections?.length
                ? '\n\n⚠️ Changes are in **draft** — click **Save resume** when you\'re happy.'
                : '';
            postCanvasEngagementMessage(replyId, `${toolCall.chat_response}${draftHint}`);
          } catch (editErr) {
            console.warn('[ai-agent] patch_section failed:', editErr);
            canvas.dispatch({ type: 'STOP_STREAMING' });
            postCanvasEngagementMessage(replyId, `Could not update **${displayTitle}**. Please try again.`);
          }
          setIsStreaming(false);
          setHistoryKey((k) => k + 1);
          return;
        }

        // ── chat_only — LLM decided no canvas mutation needed ────────────────
        const chatReply = (toolCall as { tool: 'chat_only'; response: string }).response
          || 'Let me know what you\'d like to refine on your resume.';
        postCanvasEngagementMessage(replyId, chatReply);
        setIsStreaming(false);
        setHistoryKey((k) => k + 1);
        return;
      }

      const historyForApi: AgentHistoryItem[] = [...messages, persistedUser]
        .slice(-10)
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      const controller = new AbortController();
      abortRef.current = controller;

      let lastPersistedAt = 0;
      const PERSIST_INTERVAL_MS = 600;

      const result = await streamCompletion(
        text,
        historyForApi,
        (_delta, accumulated) => {
          const statuses = extractStatusTags(accumulated);
          if (statuses.length > 0) {
            setStreamStatusLabels(statuses);
          }
          setMessages((prev) =>
            prev.map((m) => (m.id === stubAssistant.id ? { ...m, content: accumulated } : m)),
          );
          const now = Date.now();
          if (now - lastPersistedAt > PERSIST_INTERVAL_MS) {
            lastPersistedAt = now;
            void updateMessageContent(stubAssistant.id, accumulated).catch(() => {});
          }
        },
        controller.signal,
        {
          systemPromptHint: [
            UNIVERSAL_ENGAGEMENT_PROMPT,
            activeCapability?.systemPromptHint ?? '',
            buildJobMatchingSystemHint(profile),
            isProfileConfigured(profile)
              ? 'JOB SEARCH MODE: Unified search — all sources (database, LinkedIn, Naukri, Peerlist) are searched simultaneously. When searching for jobs, trigger job_search immediately. CRITICAL: If the user asks for a specific role/technology (e.g. ".NET jobs", "Java developer"), put THOSE exact keywords in the query — do NOT substitute with their profile role. Profile-derived query is only for vague requests like "show me jobs".'
              : 'JOB SEARCH MODE: Unified search — all sources searched simultaneously. User profile is NOT fully configured — but DO NOT restrict the user. Search IMMEDIATELY when they ask for specific roles (e.g. "find .NET jobs", "Java developer"). Use their EXACT keywords in the job_search query. Only ask clarifying questions if the request has zero specifics. The user can explore ANY role freely.',
            focusedJobId
              ? `Currently focused job id: ${focusedJobId}. If the user asks "why this job" or "why is this a fit", use this id in a match_explain action.`
              : '',
            // Give the LLM full visibility into what's in the canvas so it can answer questions,
            // suggest improvements, and never blindly re-write sections the user didn't ask to change.
            canvasHasContext && canvasPhase !== 'idle'
              ? (() => {
                  const cSections = canvas.state.sections ?? [];
                  const sectionList = cSections.length > 0
                    ? cSections.map((s, i) => `  ${i + 1}. ${s.title} (${s.content.split(/\s+/).length} words)`).join('\n')
                    : '  (no sections written yet)';
                  const sectionOrder = cSections.map(s => s.title).join(' → ');
                  const snap = getCanvasWorkflowSnapshot(canvas.state);
                  return [
                    `══ CANVAS STATE (LIVE) ══`,
                    `Phase: ${canvasPhase}`,
                    `Job: "${canvas.state.job?.title ?? ''}" at "${canvas.state.job?.company ?? ''}"`,
                    `ATS Score: ${canvas.state.analysis?.matchScore ?? 'N/A'}%`,
                    `Active Tab: ${canvas.state.tab}`,
                    `Artifacts: resume=${snap.hasResume ? 'yes' : 'no'}, cover_letter=${snap.hasCoverLetter ? 'yes' : 'no'}, application_note=${snap.hasApplicationNote ? 'yes' : 'no'}`,
                    ``,
                    `Current resume sections (in order):`,
                    sectionList,
                    `Section order: ${sectionOrder || '(empty)'}`,
                    ``,
                    `══ CANVAS RULES ══`,
                    `1. DO NOT trigger optimize_resume or open a new canvas — one is already active.`,
                    `2. Section MOVE / ADD / FILL / EDIT requests are handled by the canvas engine (not you). NEVER claim you moved or updated a section in chat — the right panel updates automatically.`,
                    `3. NEVER paste full rewritten section text in chat; the user reads changes on the canvas.`,
                    `4. For general resume-structure questions, answer briefly and suggest which section to improve next.`,
                    `5. NEVER end with a one-line acknowledgment only. After EVERY turn, list 2–4 concrete next steps (apply, download, refine section, cover letter, similar jobs).`,
                    `6. If resume + cover letter + application note are all done, prioritize: Apply → Download → Interview prep → Similar jobs.`,
                    ``,
                    `══ CANVAS JOB LOCK (MANDATORY) ══`,
                    `The user is working ONLY on "${canvas.state.job?.title ?? ''}" at "${canvas.state.job?.company ?? ''}".`,
                    `- NEVER emit job_search or web_search.`,
                    `- NEVER list other employers or roles (e.g. L'Oréal, Google) in chat.`,
                    `- If the user says yes/ok/sure after a re-optimization question, that means re-run optimization for THIS job — not a job search.`,
                  ].join('\n');
                })()
              : '',
          ].filter(Boolean).join('\n') || null,
          country:          profile?.location ?? undefined,
          userContext:      [userContext, recentActivity, profileVectorContext].filter(Boolean).join('\n\n') || null,
          model:            selectedModel ?? DEFAULT_AGENT_CLOUD_MODEL ?? undefined,
          responseLanguage: getAgentResponseLanguage(),
        },
      );

      // If model returned empty content (or only stray punctuation like `}`) but
      // emitted an action, fill the stub with a short confirming message so the
      // user sees something useful — not a blank bubble or rogue brace.
      let finalContent = result.content.trim();
      // Treat responses that are only punctuation/braces as empty
      if (/^[{}.,;:\s]*$/.test(finalContent)) finalContent = '';
      if (!finalContent && result.action) {
        const actionLabels: Record<string, string> = {
          cover_letter:       'Writing your cover letter…',
          application_letter: 'Writing your application letter…',
          optimize_resume:    'Optimizing your resume for this role…',
          job_search:         'Searching for matching jobs…',
          web_search:         'Searching the web…',
          company_research:   'Researching this company…',
          navigate:           'Taking you there…',
          match_explain:      'Analyzing your match…',
        };
        finalContent = actionLabels[result.action] ?? `Working on: ${result.action}`;
      }
      if (!finalContent) finalContent = '…';

      // Update the stub with final content (visible in chat)
      setMessages((prev) =>
        prev.map((m) => (m.id === stubAssistant.id ? { ...m, content: finalContent } : m)),
      );
      await updateMessageContent(stubAssistant.id, finalContent);

      let replyContent = finalContent;

      // Persist profile assessment artifact (fire-and-forget, TASK A-8)
      if (activeCapability?.id === 'profile-assessment' && result.content.length > 50) {
        void saveArtifact({
          sessionId: session?.id,
          kind:      'profile_assessment',
          title:     `Profile assessment — ${new Date().toLocaleDateString('en-IN')}`,
          body:      result.content.slice(0, 12_000),
        }).catch(() => {});
      }

      // Store thinking time for this assistant message
      if (result.thinkingMs) {
        setThinkingMap((prev) => ({ ...prev, [stubAssistant.id]: result.thinkingMs! }));
      }

      // Store which model answered — shown inline in the message
      if (result.model) {
        const publicLabel = isOwnerAdmin
          ? result.model
          : formatPublicAgentModelLabel(selectedModel ?? DEFAULT_AGENT_CLOUD_MODEL);
        setModelMap((prev) => ({ ...prev, [stubAssistant.id]: publicLabel }));
      }

      // Store active model for badge display
      if (result.model && result.backend) {
        setActiveModel({
          label: isOwnerAdmin
            ? result.model
            : formatPublicAgentModelLabel(selectedModel ?? DEFAULT_AGENT_CLOUD_MODEL),
          backend: result.backend,
        });
      }

      // Store <think> reasoning block for this message
      if (result.thinking) {
        setThinkingContent((prev) => ({ ...prev, [stubAssistant.id]: result.thinking! }));
      }

      // ── Job search ──────────────────────────────────────────────────
      const isPlaceholderQuery = (q?: string) =>
        !q || /^<[^>]+>$/.test(q.trim()) || q.trim().length < 2;

      const canvasJobLocked =
        canvasHasContext && Boolean(canvas.state.job?.title ?? canvasJob?.title);

      if (result.action === 'job_search' && canvasJobLocked) {
        console.warn('[ai-agent] Suppressed job_search — canvas job in focus:', canvas.state.job?.title);
        const suppressedNote =
          `Staying focused on **${canvas.state.job?.title ?? canvasJob?.title}** at **${canvas.state.job?.company ?? canvasJob?.company}**. ` +
          `Say **"re-optimize"** or **"yes"** to refresh your resume for this role, or use the buttons on the previous message.`;
        finalContent = finalContent.trim() ? `${finalContent}\n\n${suppressedNote}` : suppressedNote;
        replyContent = finalContent;
      } else if (result.action === 'job_search') {
        const { source: jobSource, query: finalQuery } = resolveJobSearchParams(
          isPlaceholderQuery(result.query) ? '' : (result.query ?? ''),
        );
        if (jobSource === 'internal') setSearchSource('internal');
        else setSearchSource('external');
        void fetchJobResults(finalQuery, jobSource);
      } else if (jobIntent && !result.action && !canvasJobLocked) {
        const panelPhraseRe = /^(right\s*panel|panel|results|show me on|show on|on right|display)\s*$/i;
        const rawIntent = panelPhraseRe.test(jobIntent.trim())
          ? (getPersistedJobQuery(sessionRef.current?.id ?? '') ?? '')
          : jobIntent;
        const { source: jobSource, query: effectiveQuery } = resolveJobSearchParams(rawIntent);
        void fetchJobResults(effectiveQuery, jobSource);
      }

      // Agent promised job search in prose but skipped job_search action tag
      const promisedJobSearch =
        !result.action &&
        !jobIntent &&
        !canvasJobLocked &&
        /\b(find|search|show|let me find|matching|top|fresh)\b.{0,80}\b(job|role|opening|position|product owner|product manager)\b/i.test(
          finalContent,
        );
      if (promisedJobSearch) {
        const extracted = extractJobQueryFromAgentReply(finalContent);
        const { source: jobSource, query: effectiveQuery } = resolveJobSearchParams(extracted || profile?.currentRole || '');
        if (jobSource === 'internal') setSearchSource('internal');
        void fetchJobResults(effectiveQuery, jobSource);
      }

      // ── Web search ───────────────────────────────────────────────────
      // Citations from Groq+Web model — show them directly in the panel (no separate search needed)
      const resultWithCitations = result as typeof result & { citations?: WebSearchResult[] };
      if (resultWithCitations.citations && resultWithCitations.citations.length > 0) {
        setWebResults(resultWithCitations.citations);
        setWebSearchQuery(text);
        setWebLoading(false);
      } else if (result.action === 'web_search' && result.query) {
        triggerWebSearch(result.query);
      } else if (
        webIntent &&
        !result.action &&
        !jobIntent &&
        !canvasJobLocked &&
        !(selectedModel?.startsWith(WEB_MODEL_PREFIX))
      ) {
        // LLM didn't emit an action tag — trigger from detected user intent (only for non-web models, skip if job intent already handled)
        triggerWebSearch(webIntent);
      }

      // ── Cover letter action ─────────────────────────────────────────
      if (result.action === 'cover_letter' && result.actionData) {
        const data = result.actionData as { jobTitle?: string; company?: string; tone?: string };
        await handleLetterAction('cover', data, stubAssistant.id);
      } else if (!result.action && /cover.?letter|write.+letter|draft.+letter/i.test(text)) {
        // Small models often skip the action tag — detect intent and fire anyway
        const data = {
          jobTitle: canvasJob?.title ?? profile?.currentRole ?? '',
          company:  canvasJob?.company ?? '',
          tone:     'professional' as const,
        };
        await handleLetterAction('cover', data, stubAssistant.id);
      }

      // ── Application letter action ───────────────────────────────────
      if (result.action === 'application_letter' && result.actionData) {
        const data = result.actionData as { jobTitle?: string; company?: string; referredBy?: string };
        await handleLetterAction('application', data, stubAssistant.id);
      } else if (!result.action && /application.?letter|write.+application|draft.+application/i.test(text)) {
        // Small models often skip the action tag — detect intent and fire anyway
        const data = {
          jobTitle: canvas.state.job?.title ?? canvasJob?.title ?? profile?.currentRole ?? '',
          company:  canvas.state.job?.company ?? canvasJob?.company ?? '',
          tone:     'professional' as const,
        };
        await handleLetterAction('application', data, stubAssistant.id);
      }

      // ── Navigate action ──────────────────────────────────────────────
      if (result.action === 'navigate') {
        const rawPage = typeof result.actionData?.page === 'string' ? result.actionData.page : '';
        const mappedPage = rawPage ? mapCompanionNavigatePage(rawPage) : null;
        const profileAssessmentQuery =
          activeCapability?.id === 'profile-assessment' || isProfileAssessmentIntent(text);
        const suppressProfileNav =
          profileAssessmentQuery &&
          (mappedPage === 'profile' || rawPage.trim().toLowerCase() === 'profile');

        if (suppressProfileNav) {
          console.warn('[ai-agent] Suppressed profile navigate — profile assessment stays in chat');
        } else {
          const resolved = resolveCompanionAction(
            'navigate',
            (result.actionData ?? {}) as Record<string, unknown>,
            t,
          );
          const targetPage = mappedPage ?? resolved.navigateTo ?? '';
          if (targetPage && isAgentCanvasPage(targetPage)) {
            const prompt = ASK_OPTIMIZE_JD_REPLY;
            await updateMessageContent(stubAssistant.id, prompt);
            replyContent = prompt;
            setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: prompt } : m));
          } else if (resolved.navigateTo) {
            executeAgentNavigation(resolved.navigateTo, { agentPrompt: resolved.agentPrompt });
          } else if (mappedPage) {
            executeAgentNavigation(mappedPage);
          }
        }
      }

      // ── Optimize-resume action (TASK A-7) ────────────────────────────
      // Fires when user pastes a JD without picking a job from the panel.
      // Now routes through the same agentic pipeline as the job-card CTA.
      if (result.action === 'optimize_resume' && result.actionData && profile) {
        const ad = result.actionData as Record<string, string | undefined>;
        const resumeText = buildResumePlainTextFromUserProfile(profile);
        const jdText = ad.jdText ?? '';
        if (resumeText && jdText) {
          // Replace the stub with a brief redirect message; canvas is the source of truth
          const redirect = `Opening optimization canvas for **${ad.jobTitle ?? 'Target role'}** at **${ad.company ?? 'Target company'}** →`;
          await updateMessageContent(stubAssistant.id, redirect);
          replyContent = redirect;
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: redirect } : m));

          // Hand off to the shared agentic pipeline
          void startAgenticOptimize(
            {
              id:          `synthetic-${Date.now()}`,
              title:       ad.jobTitle ?? 'Target role',
              company:     ad.company  ?? 'Target company',
              description: jdText,
            },
            resumeText,
          );

        } else if (!jdText) {
          const prompt = ASK_OPTIMIZE_JD_REPLY;
          await updateMessageContent(stubAssistant.id, prompt);
          replyContent = prompt;
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: prompt } : m));
        }
      }

      // ── Canvas open fallbacks (LLM promised optimizer but skipped action tag) ──
      const navigateIntent = detectNavigateIntent(text);

      if (!result.action) {
        if (detectExplicitCanvasOpenIntent(text)) {
          openAgentResumeCanvas();
        } else if (
          looksLikeJobDescription(text)
          && lastAssistantAwaitingOptimizeJd(messages)
          && profile
        ) {
          await startOptimizeFromChatJd(text);
        } else if (navigateIntent) {
          executeAgentNavigation(navigateIntent);
        }
      }

      // ── Record application action (TASK A-6) ─────────────────────────
      if (result.action === 'record_application' && result.actionData) {
        const ad = result.actionData as Record<string, string | undefined>;
        try {
          // Use saveAgentArtifacts so external (non-UUID) job ids don't
          // trigger a 22P02 cast error against the uuid-typed job_id column.
          await saveAgentArtifacts({
            jobId:        ad.jobId ?? `agent-${Date.now()}`,
            jobTitle:     ad.jobTitle ?? 'Unknown role',
            companyName:  ad.company ?? 'Unknown company',
            jobUrl:       ad.applyUrl,
            sourceChannel: 'other',
            markApplied:  true,
          });
          const confirmation = `\n\nApplication recorded for **${ad.jobTitle ?? 'Unknown role'}** at **${ad.company ?? 'Unknown company'}**. Track it in My Activities.`;
          replyContent = `${result.content}${confirmation}`;
          await updateMessageContent(stubAssistant.id, replyContent);
        } catch (e) {
          console.warn('[A-6] record_application failed:', e);
        }
      }

      // ── Company research action (TASK A-9) ───────────────────────────
      if (result.action === 'company_research' && result.actionData) {
        const company = ((result.actionData.company as string) ?? '').trim();
        if (company) {
          const loadingMsg = `Researching **${company}**… fetching latest news, product info, and culture data via Exa.`;
          await updateMessageContent(stubAssistant.id, loadingMsg);
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: loadingMsg } : m));
          setProcessingMsgId(stubAssistant.id);

          try {
            // Fetch Exa results: company-focused neural search
            const exaResults = await performWebSearch(
              `${company} company recent news leadership product engineering culture`,
            );

            // Build numbered context for the LLM
            const exaContext = exaResults
              .slice(0, 8)
              .map((r, i) => `[${i + 1}] ${r.title}\n${r.url}\n${r.snippet}`)
              .join('\n\n');

            // Generate structured brief via ai-companion
            const { brief } = await generateCompanyBrief(company, exaContext, abortRef.current?.signal);

            if (abortRef.current?.signal.aborted) return;

            await updateMessageContent(stubAssistant.id, brief);
            replyContent = brief;
            setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: brief } : m));

            // Persist as artifact (fire-and-forget)
            void saveArtifact({
              sessionId: session?.id,
              kind:      'company_research',
              title:     company,
              body:      brief.slice(0, 12_000),
              metadata:  { sources: exaResults.slice(0, 8) },
            }).catch(() => {});

          } catch (err) {
            if ((err as Error)?.name === 'AbortError') return;
            console.warn('[A-9] company_research failed:', err);
            const errMsg = `Sorry, I couldn't fetch research for **${company}** right now. Try again in a moment.`;
            replyContent = errMsg;
            await updateMessageContent(stubAssistant.id, errMsg);
            setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: errMsg } : m));
          } finally {
            setProcessingMsgId(null);
          }
        }
      }

      // ── Match explain action (TASK A-10) ─────────────────────────────
      // Surfaces the existing explainMatch / explain-job-match pipeline via chat.
      if (result.action === 'match_explain') {
        const jobId = ((result.actionData?.jobId as string) ?? focusedJobId ?? '').trim();

        // Hard rule: if no jobId, reply gracefully — do NOT call the edge fn
        if (!jobId || !profile?.id) {
          const noJobMsg = 'Pick a job first to see a match explanation.';
          replyContent = noJobMsg;
          await updateMessageContent(stubAssistant.id, noJobMsg);
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: noJobMsg } : m));
        } else {
          setProcessingMsgId(stubAssistant.id);
          try {
            const cached = await explainMatch(profile.id, jobId);

            if (cached) {
              const band = cached.band.replace(/_/g, ' ');
              const gaps = Array.isArray(cached.skill_gaps) && cached.skill_gaps.length
                ? cached.skill_gaps.join(', ')
                : 'None identified';
              const actions = Array.isArray(cached.action_items) && cached.action_items.length
                ? '\n\n**Next steps:**\n' + cached.action_items.map(a => `- ${a}`).join('\n')
                : '';
              const text = [
                `**Match score:** ${cached.score}/100 (${band})`,
                '',
                `**Why matched:** ${cached.why_matched}`,
                '',
                `**Skill gaps:** ${gaps}`,
                actions,
              ].join('\n').trimEnd();

              replyContent = text;
              await updateMessageContent(stubAssistant.id, text);
              setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: text } : m));
            } else {
              // Phase 2: no cache → trigger fresh computation, show pending message
              const pendingMsg = 'Computing your match score — this takes a few seconds. Refresh the panel once complete.';
              replyContent = pendingMsg;
              await updateMessageContent(stubAssistant.id, pendingMsg);
              setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: pendingMsg } : m));

              // Fire-and-forget: compute in background; user can re-ask to see result
              void supabase.functions.invoke('explain-job-match', { body: { job_id: jobId } })
                .catch((e) => console.warn('[A-10] explain-job-match invoke failed:', e));
            }
          } catch (e) {
            console.warn('[A-10] match_explain failed:', e);
            const errMsg = 'Could not retrieve your match explanation right now. Please try again.';
            replyContent = errMsg;
            await updateMessageContent(stubAssistant.id, errMsg);
            setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: errMsg } : m));
          } finally {
            setProcessingMsgId(null);
          }
        }
      }

      // ── Generate resume action (Build Resume flow) ────────────────────
      // When AI has collected enough info and generates a resume preview
      if (result.action === 'generate_resume' && result.actionData) {
        const ad = result.actionData as {
          experiences?: Array<{ title?: string; company?: string; startDate?: string; endDate?: string; bullets?: string[] }>;
          education?: Array<{ degree?: string; institution?: string; year?: string }>;
          skills?: string[];
          summary?: string;
          name?: string;
        };

        // Convert generated data to canvas sections (plain text per section)
        const genSections: import('./canvas').CanvasSection[] = [];

        // Personal Information
        const contactLines = [
          ad.name || profile?.displayName || 'Your Name',
          profile?.headline || profile?.currentRole || '',
          profile?.location || '',
          profile?.linkedinUrl || '',
          profile?.githubUrl || '',
        ].filter(Boolean);
        if (contactLines.length > 0) {
          genSections.push({ title: 'Personal Information', content: contactLines.join('\n') });
        }

        // Summary
        if (ad.summary) {
          genSections.push({ title: 'Professional Summary', content: ad.summary });
        }

        // Work Experience
        if (ad.experiences && ad.experiences.length > 0) {
          const expContent = ad.experiences.map((exp) =>
            [
              `${exp.title || ''} | ${exp.company || ''}`,
              exp.startDate ? `${exp.startDate} – ${exp.endDate || 'Present'}` : '',
              ...(exp.bullets || []).map((b) => `• ${b}`),
            ].filter(Boolean).join('\n')
          ).join('\n\n');
          genSections.push({ title: 'Work Experience', content: expContent });
        }

        // Skills
        if (ad.skills && ad.skills.length > 0) {
          genSections.push({ title: 'Technical Skills', content: ad.skills.join(', ') });
        }

        // Education
        if (ad.education && ad.education.length > 0) {
          const eduContent = ad.education.map((edu) =>
            `${edu.degree || ''} — ${edu.institution || ''}${edu.year ? ` (${edu.year})` : ''}`
          ).join('\n');
          genSections.push({ title: 'Education', content: eduContent });
        }

        // Update canvas state
        canvas.dispatch({ type: 'SET_MODE', mode: 'CANVAS' });
        canvas.dispatch({ type: 'SET_SECTIONS', sections: genSections });
        canvas.dispatch({ type: 'SET_PHASE', phase: 'fulfillment' });
      }

      // ── Save to profile action (Build Resume flow) ────────────────────
      // When user confirms saving generated resume data to their profile
      if (result.action === 'save_to_profile' && result.actionData) {
        const ad = result.actionData as {
          experiences?: Array<{ title?: string; company?: string; startDate?: string; endDate?: string; bullets?: string[] }>;
          education?: Array<{ degree?: string; institution?: string; year?: string }>;
          skills?: string[];
          summary?: string;
        };

        try {
          const profilePatch: Record<string, unknown> = {};

          if (ad.summary) {
            profilePatch.professionalSummary = ad.summary;
          }

          if (ad.skills?.length) {
            profilePatch.skills = ad.skills;
          }

          if (ad.experiences?.length) {
            profilePatch.experiences = ad.experiences.map((exp) => ({
              title: exp.title || '',
              company: exp.company || '',
              startDate: exp.startDate || '',
              endDate: exp.endDate || null,
              current: !exp.endDate,
              bullets: exp.bullets || [],
            }));
          }

          if (ad.education?.length) {
            profilePatch.education = ad.education.map((edu) => ({
              degree: edu.degree || '',
              institution: edu.institution || '',
              year: edu.year || '',
              location: '',
            }));
          }

          profilePatch.resumeSource = 'manual';
          profilePatch.resumeUpdatedAt = new Date().toISOString();

          await saveProfile(profilePatch);

          const confirmation = [
            '',
            '**Saved to your profile.** Your experiences, education, and skills have been updated.',
            '',
            '**What would you like to do next?**',
            '- **Optimize your resume** for a specific job listing — paste a job description and I\'ll tailor it',
            '- **Search for matching jobs** based on your new resume',
            '- **Write a cover letter** for a role you\'re interested in',
            '',
            'You can also view and edit your details anytime in [Account Settings → Resume](/account-settings?tab=resume).',
          ].join('\n');
          replyContent = `${result.content}${confirmation}`;
          await updateMessageContent(stubAssistant.id, replyContent);
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: replyContent } : m));
        } catch (e) {
          console.warn('[save_to_profile] failed:', e);
          const errMsg = `\n\nCouldn't save to your profile right now. You can manually add this in [Account Settings → Profile → Resume](/account-settings).`;
          replyContent = `${result.content}${errMsg}`;
          await updateMessageContent(stubAssistant.id, replyContent);
          setMessages(prev => prev.map(m => m.id === stubAssistant.id ? { ...m, content: replyContent } : m));
        }
      }

      attachUniversalEngagement(stubAssistant.id, replyContent, text, result.action ?? null);

      setHistoryKey((k) => k + 1);
    } catch (err) {
      const wasAbort = err instanceof DOMException && err.name === 'AbortError';
      if (!wasAbort) {
        console.error('[ai-agent] streamCompletion error:', err);
        const errStr = String((err as Error).message ?? err);
        const rateLimited = errStr.includes('429') || /rate\s*limit/i.test(errStr);
        const userFacing = rateLimited
          ? '**Advanced AI** is at its daily limit. Canvas section edits may still work on **Fast AI**; chat will auto-retry on Fast AI when possible. Quota resets on schedule — or use another API key in `.env.development`.'
          : 'Something went wrong while reaching the AI. Please try again in a moment.';
        try {
          const errMsg = await appendMessage(session.id, 'tool', userFacing);
          setMessages((prev) => [...prev, errMsg]);
        } catch { /* ignore */ }
      }
    } finally {
      setIsStreaming(false);
      setStreamProcessingMsgId(null);
      setStreamStatusLabels([]);
      abortRef.current = null;
    }
  }, [session, isStreaming, messages, activeCapability, profile?.location, selectedModel, triggerWebSearch, canvasOpen, canvas.state.phase, canvas.state.mode, agentStateMachine, searchSource, deepSearch, handleLetterAction, postCanvasEngagementMessage, attachUniversalEngagement, userContext, recentActivity, profileVectorContext, focusedJobId, startAgenticOptimize, openAgentResumeCanvas, saveProfile, t]);

  const handleStop = useCallback(() => { abortRef.current?.abort(); }, []);

  useEffect(() => {
    handleSubmitRef.current = handleSubmit;
  }, [handleSubmit]);

  // ── Resume file upload handler ────────────────────────────────────
  const handleResumeFileUpload = useCallback(async (file: File) => {
    if (!session) return;

    const uploadMsgId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: uploadMsgId,
        role: 'user' as const,
        content: `Uploaded resume: **${file.name}**`,
        createdAt: new Date().toISOString(),
      },
    ]);

    const processingId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: processingId,
        role: 'assistant' as const,
        content: `Parsing your resume…`,
        createdAt: new Date().toISOString(),
      },
    ]);

    try {
      const text = await extractResumeText(file);
      if (!text || text.trim().length < 30) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === processingId
              ? { ...m, content: `Could not extract text from **${file.name}**. Please make sure it's a valid PDF or DOCX file with selectable text (not a scanned image).` }
              : m,
          ),
        );
        return;
      }

      // ── Step 1: Parse into structured ResumeData (buckets) ──
      setMessages((prev) =>
        prev.map((m) => m.id === processingId ? { ...m, content: 'Extracting sections and organizing your resume data…' } : m),
      );

      const resumeData = resumePlainTextToResumeData(text);

      // ── Step 2: Save structured data to profile ──
      const profilePatch: Record<string, unknown> = {};

      if (resumeData.name) {
        profilePatch.displayName = resumeData.name;
      }
      if (resumeData.summary) {
        profilePatch.professionalSummary = resumeData.summary;
      }
      if (resumeData.headline) {
        profilePatch.headline = resumeData.headline;
      }
      if (resumeData.skills?.length) {
        profilePatch.skills = resumeData.skills;
      }
      if (resumeData.experience?.length) {
        profilePatch.experiences = resumeData.experience.map((exp) => ({
          title: exp.title || '',
          company: exp.company || '',
          location: exp.location || '',
          startDate: exp.startDate || '',
          endDate: exp.endDate || null,
          current: !exp.endDate,
          bullets: exp.bullets || [],
        }));
      }
      if (resumeData.education?.length) {
        profilePatch.education = resumeData.education.map((edu) => ({
          degree: edu.degree || '',
          institution: edu.institution || '',
          year: edu.year || '',
          gpa: edu.gpa || '',
          location: '',
        }));
      }
      if (resumeData.certifications?.length) {
        profilePatch.certifications = resumeData.certifications.map((c) => ({ name: c }));
      }
      if (resumeData.projects?.length) {
        profilePatch.projects = resumeData.projects.map((p) => ({
          name: p.name || '',
          description: p.description || '',
          technologies: p.technologies || [],
        }));
      }
      if (resumeData.contact?.location) {
        profilePatch.location = resumeData.contact.location;
      }
      if (resumeData.contact?.linkedin) {
        profilePatch.linkedinUrl = resumeData.contact.linkedin;
      }
      if (resumeData.contact?.github) {
        profilePatch.githubUrl = resumeData.contact.github;
      }
      if (resumeData.contact?.portfolio) {
        profilePatch.portfolioUrl = resumeData.contact.portfolio;
      }

      profilePatch.resumeSource = 'upload';
      profilePatch.resumeUpdatedAt = new Date().toISOString();
      profilePatch.resumeRawText = text;

      try {
        await saveProfile(profilePatch);
      } catch (e) {
        console.warn('[AgentPanel] Resume profile save failed:', e);
      }

      // ── Step 3: Build summary of what was extracted ──
      const extractedParts: string[] = [];
      if (resumeData.name) extractedParts.push(`**Name:** ${resumeData.name}`);
      if (resumeData.experience?.length) extractedParts.push(`**Experience:** ${resumeData.experience.length} role${resumeData.experience.length > 1 ? 's' : ''}`);
      if (resumeData.education?.length) extractedParts.push(`**Education:** ${resumeData.education.length} entr${resumeData.education.length > 1 ? 'ies' : 'y'}`);
      if (resumeData.skills?.length) extractedParts.push(`**Skills:** ${resumeData.skills.slice(0, 8).join(', ')}${resumeData.skills.length > 8 ? ` +${resumeData.skills.length - 8} more` : ''}`);
      if (resumeData.certifications?.length) extractedParts.push(`**Certifications:** ${resumeData.certifications.length}`);
      if (resumeData.projects?.length) extractedParts.push(`**Projects:** ${resumeData.projects.length}`);

      const summaryMsg = [
        `Resume parsed and saved to your profile.`,
        '',
        ...extractedParts,
        '',
        'What would you like to do next?',
      ].join('\n');

      setMessages((prev) =>
        prev.map((m) =>
          m.id === processingId
            ? {
                ...m,
                content: summaryMsg,
                actions: [
                  {
                    id: 'find-jobs',
                    label: 'Find matching jobs',
                    variant: 'primary' as const,
                    onClick: () => void handleSubmit('Find jobs matching my resume'),
                  },
                  {
                    id: 'optimize',
                    label: 'Optimize resume',
                    variant: 'secondary' as const,
                    onClick: () => void handleSubmit('Optimize my resume for ATS'),
                  },
                  {
                    id: 'analyze',
                    label: 'Analyze ATS score',
                    variant: 'secondary' as const,
                    onClick: () => void handleSubmit('Analyze my resume and give me an ATS score'),
                  },
                ],
              }
            : m,
        ),
      );
    } catch (err) {
      console.error('[AgentPanel] Resume upload parse error:', err);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === processingId
            ? { ...m, content: `Something went wrong parsing **${file.name}**. Please try again or upload a different file.` }
            : m,
        ),
      );
    }
  }, [session, profile, handleSubmit, saveProfile]);

  const navigateTo = (path: string) => { spaNavigate(path); };

  // ── Render ─────────────────────────────────────────────────────────
  const showEmptyState  = !isLoadingMsgs && messages.length === 0;
  const chatActive      = !showEmptyState;
  const mobileHubLanding = hubLayout && !isDesktop && !chatActive;

  const renderSuggestedChips = () => (
    <SuggestedPromptChips
      onSelectChat={(preset) => {
        setActiveCapability(preset);
        const starter = getLocalizedStarterPrompt(preset, t);
        if (starter) {
          setComposerValue(starter);
        }
        composerRef.current?.focus?.();
      }}
      onDeepLink={(preset) => {
        if (preset.deepLinkPath) {
          window.location.href = preset.deepLinkPath;
        }
      }}
      onAction={handleChipAction}
    />
  );

  const composerDockClass = chatActive
    ? 'relative border-t px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 sm:px-6 lg:px-10'
    : mobileHubLanding
      ? 'relative shrink-0 border-t px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2 sm:px-6'
      : 'absolute inset-0 flex flex-col items-center justify-center px-4 py-6 sm:px-6 lg:px-10';

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userPinnedToBottomRef.current = distFromBottom < 96;
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [chatActive, session?.id]);

  useEffect(() => {
    isStreamingRef.current = isStreaming;
    const el = scrollRef.current;
    if (!el || !session?.id) return;

    if (pendingScrollRestoreRef.current === session.id) {
      const savedTop = scrollPositionsRef.current.get(session.id);
      pendingScrollRestoreRef.current = null;
      requestAnimationFrame(() => {
        el.scrollTop = savedTop ?? el.scrollHeight;
        userPinnedToBottomRef.current = savedTop == null || savedTop >= el.scrollHeight - el.clientHeight - 96;
      });
      return;
    }

    scrollChatToBottom({ force: isStreaming || userPinnedToBottomRef.current });
  }, [messages, isStreaming, session?.id, scrollChatToBottom]);

  // Defensive sync: if the chat is in empty hub state but the canvas still
  // holds artifacts from a previous session (HMR / stale state), reset it.
  // This prevents the "chat empty, canvas showing old resume" desync.
  useEffect(() => {
    // Don't wipe canvas while a Jobs→Agent optimize handoff is opening the workspace.
    if (canvasJob || jobsHandoffStartedRef.current) return;
    if (
      showEmptyState &&
      (canvas.state.mode === 'CANVAS' ||
        canvas.state.sections?.length ||
        canvas.state.coverLetter ||
        canvas.state.applicationNote)
    ) {
      canvas.dispatch({ type: 'RESET_CANVAS' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmptyState, canvasJob, canvas.state.mode]);
  const hasJobPanel     = jobResults !== null || jobsLoading;
  const hasWebPanel     = webResults !== null || webLoading;
  const isCanvasMode    = canvas.state.mode === 'CANVAS';
  const hasLetterPanel  = workspaceLetter !== null || isGeneratingLetter;
  const hasRightPanel   = hasJobPanel || hasWebPanel || isCanvasMode || hasLetterPanel;
  const showRightPanel  = hasWebPanel || isCanvasMode || hasLetterPanel || (hasJobPanel && jobsPanelVisible);

  const agentActivity = useMemo<AgentActivity>(() => {
    const isOptimizing = agentic.phase === 'baseline' || agentic.phase === 'optimizing';
    if (jobsLoading) return { kind: 'job-search', phase: channelFetchPhase };
    if (isOptimizing) return {
      kind: 'resume-optimize',
      phase: agentic.phase,
      label: canvasJob?.title ? `${canvasJob.title} · ${canvasJob.company}` : undefined,
    };
    if (isGeneratingLetter) return {
      kind: generatingLetterType === 'application' ? 'application-letter' : 'cover-letter',
      label: canvasJob?.title ? `${canvasJob.title} · ${canvasJob.company}` : undefined,
    };
    if (webLoading) return { kind: 'web-search' };
    return { kind: 'idle' };
  }, [jobsLoading, channelFetchPhase, agentic.phase, canvasJob?.title, canvasJob?.company, isGeneratingLetter, generatingLetterType, webLoading]);

  /** Top suggested jobs for left-panel engagement (after optimization / letter). */
  const engagementSuggestedJobs = useMemo((): JobMiniCardData[] => {
    const currentJobId = canvas.state.job?.id ?? canvasJob?.id;
    const pool = (jobResults ?? []).filter((j) => {
      if (currentJobId && j.id === currentJobId) return false;
      const rec = jobRecs.get(j.id);
      if (rec && rec.score < 40) return false;
      
      // Exclude jobs that have already been optimized or applied
      const applied = appliedJobsLookup(j.id, j.title, j.company);
      if (applied) {
        const isApplied = applied.status === 'applied' || applied.status === 'interviewing' || 
                          applied.status === 'offered' || applied.status === 'accepted';
        const isOptimized = applied.hasResume || applied.hasCoverLetter;
        // Skip jobs that are already applied or optimized
        if (isApplied || isOptimized) return false;
      }
      
      return true;
    });
    return pool
      .sort((a, b) => (jobRecs.get(b.id)?.score ?? 0) - (jobRecs.get(a.id)?.score ?? 0))
      .slice(0, 3)
      .map((j) => ({
        id: j.id,
        title: j.title,
        company: j.company,
        company_logo_url: j.company_logo_url,
        location: j.location,
        score: jobRecs.get(j.id)?.score,
        source_channel: j.source_channel,
        workflowStatus: null, // Only showing non-optimized jobs now
      }));
  }, [jobResults, jobRecs, canvas.state.job?.id, canvasJob?.id, appliedJobsLookup]);

  const mobileWorkspaceSplit = !isDesktop && showRightPanel;
  const mobileWorkspaceTabLabel = isCanvasMode ? 'Resume' : 'Jobs';
  const showChatColumn = isDesktop
    ? (!hasRightPanel || chatVisible)
    : mobileWorkspaceSplit
      ? mobileSplitTab === 'chat'
      : (!hasRightPanel || chatVisible);
  const showJobsColumn = isDesktop
    ? showRightPanel
    : mobileWorkspaceSplit
      ? mobileSplitTab === 'jobs'
      : showRightPanel;
  const agentMsgCount = messages.filter((m) => m.role !== 'system').length;

  const prevCanvasModeRef = useRef(false);
  useEffect(() => {
    if (!isDesktop && isCanvasMode && !prevCanvasModeRef.current) {
      setMobileSplitTab('jobs');
    }
    prevCanvasModeRef.current = isCanvasMode;
  }, [isDesktop, isCanvasMode]);

  const handleJobOpen = useCallback((jobId: string | null) => {
    setFocusedJobId(jobId);
    scrollChatToBottom({ force: true, behavior: 'smooth' });
  }, [scrollChatToBottom]);

  // Listen for job link clicks from chat messages (job:// scheme)
  useEffect(() => {
    const handler = (e: Event) => {
      const jobId = (e as CustomEvent<{ jobId: string }>).detail?.jobId;
      if (!jobId) return;
      const agentJob = (jobResults ?? []).find((j) => j.id === jobId);
      if (!agentJob) return;
      const rec = jobRecs.get(jobId);
      const job: JobCardType = {
        id: agentJob.id,
        source: agentJob.source_channel === 'peerlist' ? 'peerlist' : 'direct',
        source_channel: agentJob.source_channel ?? undefined,
        title: agentJob.title,
        company: agentJob.company,
        company_logo_url: agentJob.company_logo_url ?? undefined,
        location: agentJob.location ?? '',
        url: agentJob.url ?? '#',
        created: agentJob.posted_at ?? new Date().toISOString(),
        description: agentJob.description_md ?? '',
        skills: agentJob.skills ?? undefined,
        must_have_skills: agentJob.must_have_skills ?? undefined,
        good_to_have_skills: agentJob.good_to_have_skills ?? undefined,
        salary_min: agentJob.salary_min ?? undefined,
        salary_max: agentJob.salary_max ?? undefined,
        salary_currency: agentJob.salary_currency ?? 'INR',
        job_type: agentJob.job_type ?? undefined,
        experience_level: agentJob.experience_level ?? undefined,
        remote: agentJob.remote ?? undefined,
        compensation_text: agentJob.compensation_text ?? undefined,
        posted_at: agentJob.posted_at ?? undefined,
        matchScore: rec?.score,
        matchedSkills: rec?.matched_skills,
        missingSkills: rec?.missing_skills,
      };
      setChatSelectedJob(job);
      setFocusedJobId(jobId);
    };
    window.addEventListener('agent:open-job', handler);
    return () => window.removeEventListener('agent:open-job', handler);
  }, [jobResults, jobRecs]);

  useLayoutEffect(() => {
    if (!chatActive) return;
    scrollChatToBottom({ force: true });
    const t = window.setTimeout(() => scrollChatToBottom({ force: true }), 320);
    return () => window.clearTimeout(t);
  }, [showRightPanel, chatWidthPct, canvas.state.mode, focusedJobId, chatActive, scrollChatToBottom]);

  const canvasJobAppliedInfo = useMemo(() => {
    const j = canvas.state.job;
    if (!j) return null;
    return appliedJobsLookup(j.id, j.title, j.company);
  }, [canvas.state.job, appliedJobsLookup]);

  // Collapse workspace nav when results/canvas open; do not force-expand on close.
  useEffect(() => {
    if (hasRightPanel) {
      window.dispatchEvent(
        new CustomEvent('ggj:workspace-sidebar', { detail: { collapsed: true } }),
      );
    }
    window.dispatchEvent(
      new CustomEvent('ggj:agent-right-panel', {
        detail: { active: hasRightPanel, canvas: isCanvasMode },
      }),
    );
  }, [hasRightPanel, isCanvasMode]);

  // Breadcrumb: static label — never use raw user message text
  const breadcrumbTitle = '';
  const displayChatTitle = sessionDisplayTitle === 'New search'
    ? t('agent.chrome.newChat', { defaultValue: 'New chat' })
    : sessionDisplayTitle;

  // Close results panel + reset (ensures chat is visible after close)
  const handleCloseResults = () => {
    const sid = sessionRef.current?.id;
    if (sid) persistSessionJobsPanelOpen(sid, false);
    setJobResults(null);
    setJobRecs(new Map());
    setShortlisted(new Set());
    setWebResults(null);
    setWebSearchQuery('');
    setWorkspaceLetter(null);
    setChatVisible(true);
    setJobsPanelVisible(false);
  };

  const sourceToolbar = (
    <SourceSelector
      value={searchSource}
      onChange={setSearchSource}
      credCount={0}
      deepSearch={deepSearch}
      onToggleDeepSearch={() => setDeepSearch((v) => !v)}
      onFileUpload={(file) => void handleResumeFileUpload(file)}
      showAdminControls={isOwnerAdmin}
      activeModel={activeModel}
      availableModels={availableModels}
      selectedModel={selectedModel}
      onModelChange={setSelectedModel}
      ollamaReachable={ollamaReachable}
      hideSourceDropdown
    />
  );

  return (
    <div className={`flex flex-col ${embedded ? 'flex-1 min-h-0' : 'h-screen'} w-full overflow-hidden`}
      style={{ background: 'var(--ds-bg-page)' }}>

      {/* ── Assisted Handoff return-nudge ─────────────────────────────────
          Triggered when the user clicks Apply, opens the portal in a new
          tab, spends ≥30s away, then returns. They confirm whether they
          actually submitted (flips application_status to 'applied' /
          'not_applied' / leaves as 'in_progress').
          ─────────────────────────────────────────────────────────────── */}
      {returnNudge.pending && (
        <div
          className="fixed z-[10000] inset-x-4 sm:inset-x-auto sm:right-6 sm:max-w-sm rounded-xl shadow-2xl border animate-in fade-in slide-in-from-bottom-3"
          role="dialog"
          aria-label="Confirm application status"
          style={{
            bottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            background: 'var(--ds-bg-card, #ffffff)',
            borderColor: 'var(--ds-border, #e5e7eb)',
            boxShadow: '0 20px 50px rgba(0,0,0,0.18), 0 4px 12px rgba(0,0,0,0.06)',
          }}
        >
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-start gap-3">
              <div
                className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}
                aria-hidden
              >
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold leading-snug" style={{ color: 'var(--ds-text-body)' }}>
                  Did you finish applying?
                </div>
                <div className="text-xs mt-0.5 leading-snug" style={{ color: 'var(--ds-text-muted)' }}>
                  <span className="font-medium" style={{ color: 'var(--ds-text-body)' }}>{returnNudge.pending.jobTitle}</span>
                  <span style={{ color: 'var(--ds-text-muted)' }}> @ {returnNudge.pending.company}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => returnNudge.dismiss()}
                className="text-base leading-none shrink-0 -mt-0.5 min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                style={{ color: 'var(--ds-text-muted)' }}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          </div>
          <div className="px-4 pb-4 pt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => { handleApplyNudgeResolve('applied'); }}
              className="flex-1 min-w-[110px] min-h-[44px] px-3 py-2 rounded-lg text-xs font-semibold text-white transition-colors touch-manipulation"
              style={{ background: '#15803d' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#166534'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#15803d'; }}
            >
              Yes, applied
            </button>
            <button
              type="button"
              onClick={() => { handleApplyNudgeResolve('later'); }}
              className="flex-1 min-w-[90px] min-h-[44px] px-3 py-2 rounded-lg text-xs font-semibold transition-colors touch-manipulation"
              style={{ background: 'rgba(99,102,241,0.10)', color: '#4f46e5' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(99,102,241,0.18)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(99,102,241,0.10)'; }}
            >
              Not yet
            </button>
            <button
              type="button"
              onClick={() => { handleApplyNudgeResolve('not_applied'); }}
              className="flex-1 min-w-[110px] min-h-[44px] px-3 py-2 rounded-lg text-xs font-semibold transition-colors touch-manipulation"
              style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.15)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(220,38,38,0.08)'; }}
            >
              Didn't apply
            </button>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          TOP BAR — minimal strip with history toggle only (standalone mode).
          Removed bulky logo/breadcrumb to maximize content space.
          ══════════════════════════════════════════════════════════════ */}
      {!embedded && (
        <div
          className="shrink-0 flex items-center gap-2 px-3 h-14 border-b z-10 bg-white shadow-sm"
          style={{ borderColor: 'var(--ds-border)' }}
        >
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            aria-pressed={historyOpen}
            title={historyOpen ? 'Hide chat history' : 'Show chat history'}
            className="ds-btn ds-btn-ghost w-8 h-8 p-0 shrink-0"
          >
            {historyOpen ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
          </button>

          {!showEmptyState && (
            <span className="ml-2 truncate text-sm font-semibold text-gray-800">
              AI Agent
              {breadcrumbTitle && (
                <span className="ml-1 font-normal text-gray-400">› {breadcrumbTitle}</span>
              )}
            </span>
          )}

          <div className="flex-1" />

          <UtilityCluster
            userName={profile?.displayName || null}
            userEmail={profile?.email || ''}
            userAvatarUrl={null}
            credits={creditSystem.credits ?? planStatus?.credits ?? null}
            creditsLoading={creditSystem.loading || (planStatus?.loading ?? false)}
            onUpgradeClick={() => { window.location.href = '/checkout'; }}
            onSignOutClick={() => onSignOut?.()}
            onNavChange={() => {}}
            isDesktop={isDesktop}
          />
        </div>
      )}

      {/* Mobile-only: history toggle (desktop toolbar removed — job count lives in right panel header) */}
      {embedded && !hubLayout && !isDesktop && (
        <div
          className="shrink-0 flex items-center gap-1 px-3 h-10 border-b"
          style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-tinted)' }}
        >
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            aria-pressed={historyOpen}
            title={historyOpen ? 'Hide chat history' : 'Show chat history'}
            className={`inline-flex items-center justify-center w-9 h-9 rounded-lg transition-colors shrink-0 touch-manipulation border
              ${historyOpen
                ? 'border-[var(--ds-brand-border,rgba(108,71,255,0.30))] bg-[var(--ds-brand-soft)]'
                : 'border-transparent hover:bg-[var(--ds-bg-tinted)]'}`}
            style={{ color: historyOpen ? 'var(--ds-brand)' : 'var(--ds-text-body)' }}
          >
            {historyOpen
              ? <PanelLeftClose className="w-5 h-5" />
              : <PanelLeftOpen  className="w-5 h-5" />}
          </button>
          <div className="flex-1" />
        </div>
      )}

      {/* ── Body ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden relative">

        {/* Desktop persistent sidebar — hidden when embedded (shell sidebar owns it) */}
        {isDesktop && !embedded && (
          <div
            className={`shrink-0 flex flex-col border-r overflow-hidden transition-[width] duration-200 ease-out
              ${historyOpen ? 'w-[min(240px,26vw)]' : 'w-0'}`}
            style={{ borderColor: 'var(--ds-border)' }}
            aria-hidden={!historyOpen}>
            {historyOpen && (
              <HistorySidebar
                activeSessionId={session?.id ?? null}
                onSelectSession={handleSelectSession}
                onNewSession={handleNewSession}
                refreshKey={historyKey}
                onSessionRenamed={handleSidebarRenamed}
                onSessionDeleted={handleSidebarDeletedSession}
                onBackToWorkspace={onBackToWorkspace}
                collections={collections}
                activeCollectionId={activeCollectionId}
                onSelectCollection={setActiveCollectionId}
              />
            )}
          </div>
        )}

        {/* Mobile overlay drawer — still available even when embedded */}
        {!isDesktop && historyOpen && (
          <>
            <button type="button" aria-label="Close chat history"
              className="absolute inset-0 z-40 bg-black/45"
              onClick={() => setHistoryOpen(false)} />
            <div className="absolute top-0 left-0 bottom-0 z-50 w-[min(272px,calc(100vw-40px))] shadow-xl" aria-modal="true">
              <HistorySidebar
                activeSessionId={session?.id ?? null}
                onSelectSession={handleSelectSession}
                onNewSession={handleNewSession}
                refreshKey={historyKey}
                onClose={() => setHistoryOpen(false)}
                onSessionRenamed={handleSidebarRenamed}
                onSessionDeleted={handleSidebarDeletedSession}
                onBackToWorkspace={onBackToWorkspace}
                collections={collections}
                activeCollectionId={activeCollectionId}
                onSelectCollection={setActiveCollectionId}
              />
            </div>
          </>
        )}

        {/* ── Main content: chat + optional job panel ── */}
        <div ref={splitRef} className={`flex-1 min-w-0 flex ${hasRightPanel && isDesktop ? 'flex-row' : 'flex-col'} overflow-hidden`}>

          {mobileWorkspaceSplit && (
            <div
              className="agent-mobile-split-tabs shrink-0 flex border-b"
              style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-card)' }}
              role="tablist"
              aria-label="Agent and workspace"
            >
              <button
                type="button"
                role="tab"
                aria-selected={mobileSplitTab === 'chat'}
                onClick={() => { setMobileSplitTab('chat'); setMobileAgentFollowUp(false); }}
                className={`agent-mobile-split-tabs__btn flex-1 relative ${mobileSplitTab === 'chat' ? 'is-active' : ''}`}
              >
                Agent
                <span className="agent-mobile-split-tabs__count">{agentMsgCount}</span>
                {mobileAgentFollowUp && mobileSplitTab !== 'chat' && (
                  <span className="agent-mobile-split-tabs__dot" aria-label="Follow-up available" />
                )}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mobileSplitTab === 'jobs'}
                onClick={() => setMobileSplitTab('jobs')}
                className={`agent-mobile-split-tabs__btn flex-1 ${mobileSplitTab === 'jobs' ? 'is-active' : ''}`}
              >
                {mobileWorkspaceTabLabel}
                {!isCanvasMode && (
                  <span className="agent-mobile-split-tabs__count">{filteredCount}</span>
                )}
              </button>
            </div>
          )}

          {/* Chat column — hidden when user collapses it via toolbar "Chat" button */}
          {showChatColumn && (
            <div
              className={`ds-agent-chat-panel flex flex-col overflow-hidden shrink-0 relative${hubLayout ? ' agent-hub-chat-panel' : ''}`}
              style={hasRightPanel && isDesktop
                ? {
                    width: showRightPanel ? `${chatWidthPct}%` : '100%',
                    borderRight: showRightPanel ? '1px solid var(--ds-border)' : undefined,
                    boxShadow: showRightPanel ? 'inset -1px 0 0 rgba(0,0,0,0.06)' : undefined,
                  }
                : { flex: 1, minHeight: 0 }}
            >
              {/* Unified chat column — landing hub morphs into bottom dock on first send */}
              <div className={`relative flex min-h-0 flex-1 flex-col overflow-hidden${mobileHubLanding ? ' agent-hub-landing-layout' : ''}`}>

                {/* ── Chat panel header bar ── */}
                {chatActive && (
                  <div className="agent-chat-panel-header">
                    {/* Agent Panel identity */}
                    <div className="flex items-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0" aria-hidden>
                        <rect x="1" y="1" width="12" height="12" rx="3" fill="var(--ds-brand)" opacity="0.15"/>
                        <path d="M4 7h6M4 4.5h6M4 9.5h4" stroke="var(--ds-brand)" strokeWidth="1.3" strokeLinecap="round"/>
                      </svg>
                      <span className="agent-chat-panel-header__title">Agent</span>
                      <span className="agent-chat-panel-header__count">
                        {messages.filter((m) => m.role !== 'system').length}
                      </span>
                    </div>

                    {/* Collapse agent panel — icon only */}
                    {hasRightPanel && isDesktop && showRightPanel && chatVisible && (
                      <button
                        type="button"
                        onClick={() => setChatVisible(false)}
                        title="Collapse Agent panel"
                        aria-label="Collapse Agent panel"
                        className="ds-agent-panel-toggle"
                      >
                        <PanelLeftClose className="w-4 h-4" />
                      </button>
                    )}

                    {/* Expand jobs panel when collapsed */}
                    {hasRightPanel && isDesktop && !showRightPanel && hasJobPanel && (
                      <button
                        type="button"
                        onClick={() => setJobsPanelVisible(true)}
                        title="Expand Jobs panel"
                        aria-label="Expand Jobs panel"
                        className="ds-agent-panel-toggle ds-agent-panel-toggle--brand"
                      >
                        <PanelRightOpen className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}

                {/* Message stream */}
                <div
                  ref={scrollRef}
                  className={`relative z-10 min-h-0 overflow-y-auto px-4 pb-4 pt-4 transition-all duration-300 sm:px-6 lg:px-10 agent-messages-scroll ${
                    chatActive ? 'opacity-100' : 'pointer-events-none opacity-0'
                  }`}
                  style={{
                    flex: chatCollapsed ? '0 0 0px' : '1 1 0',
                    maxHeight: chatCollapsed ? 0 : undefined,
                    overflow: chatCollapsed ? 'hidden' : undefined,
                    transition: 'flex 0.25s ease, max-height 0.25s ease',
                  }}
                >
                  <div className="mx-auto flex w-full max-w-[760px] flex-col">

                    {/* Load previous messages — sliding window */}
                    {olderMsgsExist && (
                      <div className="flex flex-col items-center gap-1 pt-4 pb-2">
                        <button
                          type="button"
                          onClick={() => void loadOlderMessages()}
                          disabled={isLoadingOlder}
                          className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors disabled:opacity-50"
                          style={{
                            borderColor: 'var(--ds-border)',
                            background: 'var(--ds-bg-recessed)',
                            color: 'var(--ds-text-muted)',
                          }}
                        >
                          {isLoadingOlder ? (
                            <span className="w-3 h-3 rounded-full border border-current border-t-transparent animate-spin shrink-0" />
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
                              <path d="M6 9V3M3 6l3-3 3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {isLoadingOlder ? 'Loading…' : 'Load previous messages'}
                        </button>
                        <span className="text-[11px]" style={{ color: 'var(--ds-text-muted)' }}>
                          Showing the most recent {messages.length} messages
                        </span>
                      </div>
                    )}

                    {/* Conversation start marker */}
                    {!olderMsgsExist && messages.length > 0 && (
                      <div className="flex items-center gap-2 py-3 pb-5">
                        <div className="h-px flex-1" style={{ background: 'var(--ds-divider)' }} />
                        <span className="text-[11px]" style={{ color: 'var(--ds-text-muted)' }}>Start of conversation</span>
                        <div className="h-px flex-1" style={{ background: 'var(--ds-divider)' }} />
                      </div>
                    )}

                    {chatActive && (
                      <div className="mb-5 flex items-center justify-between border-b border-slate-100 pb-3 dark:border-slate-800">
                        {titleEditing ? (
                          <input
                            autoFocus
                            value={draftTitle}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            onBlur={() => void persistHeaderTitle()}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void persistHeaderTitle();
                              }
                              if (e.key === 'Escape') {
                                setDraftTitle(session?.title?.trim() ?? '');
                                setTitleEditing(false);
                              }
                            }}
                            placeholder="Untitled chat"
                            className="min-w-0 flex-1 bg-transparent text-lg font-semibold tracking-tight text-slate-900 outline-none dark:text-slate-100"
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => setTitleEditing(true)}
                            className="min-w-0 truncate text-left text-lg font-semibold tracking-tight text-slate-900 transition-colors hover:text-indigo-600 dark:text-slate-100"
                            title="Click to rename chat"
                          >
                            {draftTitle.trim() || displayChatTitle}
                          </button>
                        )}
                        {!titleEditing && (
                          <button
                            type="button"
                            onClick={() => setTitleEditing(true)}
                            className="ml-3 shrink-0 rounded px-2 py-1 text-xs text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-600 dark:hover:bg-slate-800"
                          >
                            Rename
                          </button>
                        )}
                      </div>
                    )}
                    {messages.map((m, i) => {
                        const nextMessage = messages[i + 1];
                        const isTurnEnd = !nextMessage || nextMessage.role === 'user';
                        const isOptimizingMsg = optimizingMsgId === m.id && canvasJob;
                        const optimizeWidget = isOptimizingMsg ? (
                          <InlineOptimizeWidget
                            jobTitle={canvasJob.title}
                            company={canvasJob.company}
                            phase={
                              agentic.phase === 'baseline' ? 'baseline'
                              : agentic.phase === 'optimizing' ? 'optimizing'
                              : agentic.phase === 'done' ? 'done'
                              : agentic.phase === 'error' ? 'error'
                              : 'baseline'
                            }
                            baseline={agentic.baseline}
                            sections={agentic.sections.map(s => ({
                              title: s.title,
                              status: s.status,
                              originalContent: s.originalContent,
                              optimizedContent: s.optimizedContent,
                            }))}
                            result={
                              agentic.finalResult
                                ? {
                                    matchScoreBefore: agentic.finalResult.matchScoreBefore,
                                    matchScoreAfter: agentic.finalResult.matchScoreAfter,
                                    matchedSkills: agentic.finalResult.matchedSkills,
                                    missingSkills: agentic.finalResult.missingSkills,
                                    improvements: agentic.finalResult.improvements,
                                    optimizedResume: agentic.finalResult.fullText,
                                  }
                                : null
                            }
                            error={agentic.error}
                            onSave={handleCanvasSave}
                            onDownload={handleCanvasDownload}
                            onApply={handleCanvasApply}
                            onCoverLetter={() => {
                              if (!canvasJob || !session) return;
                              const replyId = crypto.randomUUID();
                              const reply: AgentMessageT = {
                                id: replyId, sessionId: session.id, role: 'assistant',
                                content: draftingCoverLetterMessage(canvasJob.title, canvasJob.company),
                                createdAt: new Date().toISOString(),
                              };
                              setMessages(prev => [...prev, reply]);
                              void handleLetterAction('cover', { jobTitle: canvasJob.title, company: canvasJob.company }, replyId);
                            }}
                            onSearchSimilar={handleCanvasSearchSimilar}
                            saving={canvasSaving}
                            saved={canvasSaved}
                            applied={canvasApplied}
                          />
                        ) : undefined;

                        const msgActions = messageActions[m.id];
                        const isLetterProcessing = processingMsgId === m.id;
                        const isStreamProcessing = streamProcessingMsgId === m.id && isStreaming;
                        const isJobSearchMsg =
                          jobsLoading &&
                          m.role === 'assistant' &&
                          /searching for matching jobs|results are loading|found.*matching jobs/i.test(m.content);
                        const isResumeOptimizing =
                          optimizingMsgId === m.id &&
                          agentic.phase !== 'done' &&
                          agentic.phase !== 'error';

                        let executionSteps = undefined as import('./components/AgentMessage').AgentExecutionStep[] | undefined;
                        if (isLetterProcessing) {
                          executionSteps = [
                            { id: 'intent', label: 'Defining task intent and required context', status: 'completed' },
                            { id: 'profile', label: 'Mapping profile data to the target role', status: 'completed' },
                            {
                              id: 'artifact',
                              label: isGeneratingLetter
                                ? 'Writing document artifact in Analysis Workspace'
                                : 'Running agent action',
                              status: 'active',
                            },
                          ];
                        } else if (isStreamProcessing) {
                          executionSteps = mergeStatusSteps(chatProcessingSteps, streamStatusLabels);
                        } else if (isJobSearchMsg) {
                          executionSteps = jobSearchProcessingSteps;
                        } else if (isResumeOptimizing) {
                          executionSteps = resumeOptimizeProcessingSteps;
                        }

                        return (
                          <React.Fragment key={m.id}>
                            <AgentMessage
                              role={m.role}
                              content={m.content}
                              messageId={m.id}
                              userName={profile?.displayName?.trim() || firstName}
                              isTurnEnd={isTurnEnd}
                              thinkingMs={thinkingMap[m.id]}
                              modelLabel={modelMap[m.id]}
                              thinking={thinkingContent[m.id]}
                              isStreaming={isStreaming && i === messages.length - 1 && m.role === 'assistant'}
                              isProcessing={
                                isResumeOptimizing ||
                                isLetterProcessing ||
                                isStreamProcessing ||
                                isJobSearchMsg
                              }
                              widget={optimizeWidget ?? messageWidgets[m.id]}
                              actions={msgActions}
                              executionSteps={executionSteps}
                              onEdit={m.role === 'user' ? (text) => {
                                // Trim the conversation back to just before this message,
                                // then prefill the composer so the user can revise and resend.
                                setMessages(prev => prev.slice(0, i));
                                setComposerValue(text);
                                requestAnimationFrame(() => composerRef.current?.focus?.());
                              } : undefined}
                            />
                          </React.Fragment>
                        );
                      })}
                    {/* ── Post-optimization engagement: videos + action cards ── */}
                    {canvas.state.job?.title &&
                      !isStreaming &&
                      agentic.phase === 'done' &&
                      ((canvas.state.sections?.length ?? 0) > 0 || canvas.state.coverLetter || canvas.state.applicationNote) && (
                      <div className="ds-canvas-engagement mx-3 mb-3 mt-1 rounded-xl" style={{ border: '1px solid var(--ds-border)' }}>
                        <VideoSuggestions
                          jobTitle={canvas.state.job.title}
                          company={canvas.state.job.company}
                          className="ds-canvas-engagement__videos"
                        />
                        {/* Quick toolbar actions — mirrors right panel toolbar */}
                        <div className="ds-canvas-engagement__section px-3 pt-3 pb-2">
                          <p className="ds-canvas-engagement__heading t-label mb-2">Quick actions</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }))}
                              className="ds-btn ds-btn-sm flex items-center gap-1.5"
                              style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                              PDF
                            </button>
                            <button
                              type="button"
                              onClick={() => window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'docx' } }))}
                              className="ds-btn ds-btn-sm flex items-center gap-1.5"
                              style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6', border: '1px solid rgba(59,130,246,0.25)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/></svg>
                              DOCX
                            </button>
                            <button
                              type="button"
                              onClick={() => window.dispatchEvent(new CustomEvent('ggj:agent-email'))}
                              className="ds-btn ds-btn-sm flex items-center gap-1.5"
                              style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6', border: '1px solid rgba(139,92,246,0.25)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                              Email
                            </button>
                            <button
                              type="button"
                              onClick={handleCanvasApply}
                              className="ds-btn ds-btn-sm flex items-center gap-1.5"
                              style={{ background: 'rgba(22,101,52,0.1)', color: '#16a34a', border: '1px solid rgba(22,101,52,0.25)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              Apply
                            </button>
                            <button
                              type="button"
                              onClick={() => window.dispatchEvent(new CustomEvent('ggj:agent-skill-gap'))}
                              className="ds-btn ds-btn-sm flex items-center gap-1.5"
                              style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.25)' }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>
                              Skill gap
                            </button>
                          </div>
                        </div>

                        <div className="ds-canvas-engagement__section">
                          <p className="ds-canvas-engagement__heading t-label">What's next?</p>
                          <div className="ds-canvas-engagement__actions">
                            {!canvas.state.coverLetter && (
                              <button
                                type="button"
                                onClick={() => {
                                  const job = canvas.state.job;
                                  if (!job || !session) return;
                                  const replyId = crypto.randomUUID();
                                  setMessages(prev => [...prev, {
                                    id: replyId, sessionId: session.id, role: 'assistant' as const,
                                    content: draftingCoverLetterMessage(job.title, job.company),
                                    createdAt: new Date().toISOString(),
                                  }]);
                                  void handleLetterAction('cover', { jobTitle: job.title, company: job.company }, replyId);
                                }}
                                className="ds-canvas-engagement__card"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ds-brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                                <span className="ds-canvas-engagement__card-title">Write cover letter</span>
                                <span className="ds-canvas-engagement__card-desc">Tailored to this role</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                const job = canvas.state.job;
                                const company = job?.company ?? 'the company';
                                const title = job?.title ?? 'this role';
                                void handleSubmit(`Help me draft a referral request message to someone at ${company} for the ${title} position`);
                              }}
                              className="ds-canvas-engagement__card ds-canvas-engagement__card--pulse"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ds-ok, #10b981)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                              <span className="ds-canvas-engagement__card-title">Get referral</span>
                              <span className="ds-canvas-engagement__card-desc">Draft referral request</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                window.dispatchEvent(new CustomEvent('ggj:agent-download', { detail: { format: 'pdf' } }));
                              }}
                              className="ds-canvas-engagement__card"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                              <span className="ds-canvas-engagement__card-title">Download resume</span>
                              <span className="ds-canvas-engagement__card-desc">Save as PDF</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                window.history.pushState({}, '', '/interview-prep');
                                window.dispatchEvent(new PopStateEvent('popstate'));
                              }}
                              className="ds-canvas-engagement__card"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
                              <span className="ds-canvas-engagement__card-title">Interview prep</span>
                              <span className="ds-canvas-engagement__card-desc">Practice for this role</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                window.history.pushState({}, '', '/my-activities');
                                window.dispatchEvent(new PopStateEvent('popstate'));
                              }}
                              className="ds-canvas-engagement__card"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="8" height="18" x="8" y="3" rx="1"/><path d="M4 11h2M18 11h2M12 3v2M12 19v2"/></svg>
                              <span className="ds-canvas-engagement__card-title">Track applications</span>
                              <span className="ds-canvas-engagement__card-desc">Manage your pipeline</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                void handleSubmit('Draft a formal application letter');
                              }}
                              className="ds-canvas-engagement__card"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--ds-brand, #6366f1)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                              <span className="ds-canvas-engagement__card-title">Application letter</span>
                              <span className="ds-canvas-engagement__card-desc">Formal direct application</span>
                            </button>
                          </div>
                        </div>
                        {engagementSuggestedJobs.length > 0 && (
                          <div className="ds-canvas-engagement__section px-3 pt-2 pb-3">
                            <p className="ds-canvas-engagement__heading t-label mb-1">Optimize for more roles</p>
                            <JobMiniCards
                              jobs={engagementSuggestedJobs}
                              onOptimize={(jobId) => {
                                const job = (jobResults ?? []).find((r) => r.id === jobId);
                                if (job) handleAgentOptimizeFromJobs(job);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Anchor for scroll-to-bottom */}
                    <div ref={messagesEndRef} style={{ height: 1 }} />
                  </div>
                </div>

                {mobileHubLanding && (
                  <div className="agent-hub-landing-scroll shrink-0 overflow-visible px-3 pt-1 pb-2 sm:px-6 sm:pt-4">
                    {bootstrapError && (
                      <div className="mb-3 flex w-full flex-col gap-2 rounded-xl border px-3 py-3 t-body sm:flex-row sm:items-center sm:gap-3 sm:px-4"
                        style={{ background: 'var(--ds-danger-bg)', borderColor: 'var(--ds-danger-border)', color: 'var(--ds-text-strong)' }}>
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: 'var(--ds-danger)' }} />
                        <span className="flex-1">Could not connect to the server. Check your internet connection or restore the Supabase project.</span>
                        <button
                          type="button"
                          onClick={() => void runBootstrap()}
                          className="ds-btn ds-btn-sm shrink-0 min-h-[44px] sm:min-h-0"
                          style={{ color: 'var(--ds-danger)', borderColor: 'var(--ds-danger)' }}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    <div data-tour="agent-welcome">
                      <EmptyState firstName={firstName} />
                    </div>
                    <div data-tour="agent-prompt-chips" className="pb-2">
                      {renderSuggestedChips()}
                    </div>
                  </div>
                )}

                {/* Composer — pinned below message stream (not inside scroll area) */}
                <div
                  className={`agent-composer-dock z-20 w-full shrink-0 transition-all duration-300 ease-out${chatActive ? ' agent-composer-dock--active' : ''} ${composerDockClass}`}
                  style={{
                    borderColor: 'var(--ds-border)',
                    background: chatActive || mobileHubLanding ? 'var(--ds-bg-card)' : 'var(--ds-bg-page)',
                  }}
                >
                  <div className={`w-full ${chatActive ? 'mx-auto max-w-[760px]' : 'max-w-[720px]'}`}>
                    {bootstrapError && !chatActive && !mobileHubLanding && (
                      <div className="mb-3 flex w-full items-center gap-3 rounded-xl border px-4 py-3 t-body"
                        style={{ background: 'var(--ds-danger-bg)', borderColor: 'var(--ds-danger-border)', color: 'var(--ds-text-strong)' }}>
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: 'var(--ds-danger)' }} />
                        <span className="flex-1">Could not connect to the server. Check your internet connection or restore the Supabase project.</span>
                        <button
                          type="button"
                          onClick={() => void runBootstrap()}
                          className="ds-btn ds-btn-sm shrink-0"
                          style={{ color: 'var(--ds-danger)', borderColor: 'var(--ds-danger)' }}
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* Landing chrome — fades out on first send (desktop / centered hub only) */}
                    {!mobileHubLanding && (
                      <div
                        data-tour="agent-welcome"
                        className={`overflow-hidden transition-all duration-300 ease-out ${
                          chatActive ? 'pointer-events-none max-h-0 opacity-0' : 'max-h-[480px] opacity-100'
                        }`}
                      >
                        <EmptyState firstName={firstName} />
                      </div>
                    )}

                    {false && activeCapability && chatActive && (
                      <div className="mb-2 ds-chip ds-chip-brand">
                        <Sparkles className="w-3 h-3" />
                        <span>{activeCapability.label}</span>
                        <button type="button" onClick={() => setActiveCapability(null)}
                          className="ml-1 rounded-full hover:bg-[var(--ds-brand-soft)]" aria-label="Clear capability">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}

                    {!mobileHubLanding && (
                      <div
                        data-tour="agent-prompt-chips"
                        className={`overflow-hidden transition-all duration-300 ease-out ${
                          chatActive ? 'pointer-events-none max-h-0 opacity-0' : 'max-h-36 opacity-100 pb-2.5'
                        }`}
                      >
                        {renderSuggestedChips()}
                      </div>
                    )}

                    <div data-tour="agent-composer">
                    <AgentComposer
                      ref={composerRef}
                      value={composerValue}
                      onChange={setComposerValue}
                      onSubmit={(t) => void handleSubmit(t)}
                      onStop={handleStop}
                      isStreaming={isStreaming}
                      disabled={!session}
                      compact={chatActive && (!isDesktop || hasJobPanel)}
                      usePlaceholderTypewriter={!chatActive && !bootstrapError}
                      placeholderPrompts={placeholderPrompts}
                      placeholder={
                        bootstrapError
                          ? t('agent.composer.placeholderConnectionFailed', 'Connection failed — click Retry above')
                          : chatActive
                            ? isDesktop
                              ? t('agent.composer.placeholderActive', 'Ask questions or add context with commands…')
                              : t('agent.composer.placeholderActiveMobile', 'Ask or add commands…')
                            : placeholderPrompts[0]
                      }
                      sourceSelector={sourceToolbar}
                    />
                    </div>

                    {isOwnerAdmin && (
                      <div
                        className={`agent-ollama-strip mt-2 overflow-hidden rounded-xl border transition-opacity duration-300 ${
                          mobileHubLanding || chatActive ? 'hidden sm:block' : 'opacity-100'
                        }${chatActive ? ' sm:opacity-90' : ''}`}
                        style={{
                          borderColor: 'var(--ds-border)',
                          background: 'var(--ds-bg-recessed)',
                        }}
                      >
                        <OllamaRoutingStrip
                          ollamaReachable={ollamaReachable}
                          selectedModel={selectedModel ?? ''}
                          availableModels={availableModels}
                          onRetest={(result) => {
                            setOllamaReachable(
                              result.status === 'ok' ? true :
                              result.status === 'cors_blocked' ? 'cors' : false
                            );
                            if (result.status === 'ok') {
                              const names = result.models;
                              setAvailableModels(names.length > 0 ? names : [...FALLBACK_OLLAMA_MODELS]);
                              if (names.length > 0) {
                                const preferred = import.meta.env.VITE_OLLAMA_MODEL ?? 'gpt-oss:20b';
                                const base = preferred.split(':')[0];
                                const matched = names.find(n => n.startsWith(base)) ?? names[0];
                                setSelectedModel((prev) => prev ?? matched);
                              }
                            }
                          }}
                        />
                      </div>
                    )}

                    {isGeneratingLetter && chatActive && (
                      <div className="mt-2 flex items-center gap-2 t-tag" style={{ color: 'var(--ds-text-body)' }}>
                        <span className="w-3 h-3 rounded-full border-2 border-[var(--ds-border)] border-t-[var(--ds-brand)] animate-spin" />
                        <span>{t('agent.composer.generatingLetter', 'Generating letter…')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Drag handle — invisible resize strip (border-right on chat provides the visual divider) ── */}
          {hasRightPanel && isDesktop && chatVisible && showRightPanel && (
            <div
              className="ds-agent-split-divider shrink-0"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panels"
            >
              <button
                type="button"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setChatVisible(false)}
                title="Collapse Agent panel"
                aria-label="Collapse Agent panel"
                className="ds-agent-split-divider__btn"
              >
                <ChevronsLeft className="w-3 h-3" />
              </button>
              <div
                onMouseDown={onDividerMouseDown}
                title="Drag to resize panels"
                className="ds-agent-split-divider__handle"
              />
              {!isCanvasMode && (
                <button
                  type="button"
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={collapseJobsPanel}
                  title="Collapse Jobs panel"
                  aria-label="Collapse Jobs panel"
                  className="ds-agent-split-divider__btn"
                >
                  <ChevronsRight className="w-3 h-3" />
                </button>
              )}
            </div>
          )}

          {/* ── Right panel: persistent Analysis Workspace ── */}
          {showJobsColumn && (
            <div
              className={`ds-agent-right-panel flex min-w-0 flex-col overflow-hidden ${isDesktop ? 'flex-1' : 'flex-1 min-h-0'}`}
            >

              {/* Expand Agent Panel strip — shown when the chat column is hidden */}
              {hasRightPanel && isDesktop && !chatVisible && (
                <div
                  className="shrink-0 flex items-center gap-2 px-3 border-b"
                  style={{
                    borderColor: 'var(--ds-border)',
                    background: 'var(--ds-bg-card)',
                    height: 36,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setChatVisible(true)}
                    title="Expand Agent panel"
                    aria-label="Expand Agent panel"
                    className="ds-agent-panel-toggle ds-agent-panel-toggle--brand"
                  >
                    <PanelLeftOpen className="w-4 h-4" />
                    <span className="text-[11px] font-semibold">Agent</span>
                  </button>
                </div>
              )}

              {hasWebPanel && !hasJobPanel && !hasLetterPanel && canvas.state.mode !== 'CANVAS' ? (
                <WebSearchPanel
                  query={webSearchQuery}
                  results={webResults ?? []}
                  isLoading={webLoading}
                  onClose={handleCloseResults}
                />
              ) : (
              <RightPanelContainer
                showJobList={hasJobPanel}
                jobs={jobResults ?? []}
                recommendations={jobRecs}
                jobsLoading={jobsLoading}
                agentActivity={agentActivity}
                onCloseJobs={handleCloseResults}
                onAgentOptimize={handleAgentOptimizeFromJobs}
                onJobOpen={handleJobOpen}
                quickStartView={
                  <SuggestedPromptChips
                    onSelectChat={(preset) => {
                      setActiveCapability(preset);
                      const starter = getLocalizedStarterPrompt(preset, t);
                      if (starter) {
                        setComposerValue(starter);
                      }
                      composerRef.current?.focus?.();
                    }}
                    onDeepLink={(preset) => {
                      if (preset.deepLinkPath) {
                        window.location.href = preset.deepLinkPath;
                      }
                    }}
                    onAction={handleChipAction}
                  />
                }
                jobListView={
                  <>
                    <AgentJobsPanel
                    query={jobQuery}
                    jobs={jobResults ?? []}
                    recommendations={jobRecs}
                    isLoading={jobsLoading}
                    searchSource={searchSource}
                    deepSearch={deepSearch}
                    onClose={handleCloseResults}
                    onCollapse={collapseJobsPanel}
                    onAgentOptimize={handleAgentOptimizeFromJobs}
                    collections={collections}
                    activeCollectionId={activeCollectionId}
                    onCollectionsChange={handleCollectionsChange}
                    onCollectionSelect={setActiveCollectionId}
                    viewMode={jobViewMode}
                    onViewModeChange={setJobViewMode}
                    shortlisted={shortlisted}
                    onShortlistedChange={setShortlisted}
                    onFilteredCount={setFilteredCount}
                    onJobOpen={handleJobOpen}
                  />
                  </>
                }
                letterArtifact={workspaceLetter}
                isGeneratingLetter={isGeneratingLetter}
                onClearLetter={() => setWorkspaceLetter(null)}
                onApply={handleCanvasApply}
                onDownload={handleCanvasDownload}
                onEmail={handleCanvasEmail}
                onSaveToProfile={handleSaveToProfile}
                jobAppliedInfo={canvasJobAppliedInfo}
                onStartOptimization={handleCanvasStartOptimization}
                onOpenConversation={!isDesktop ? handleCanvasOpenConversation : undefined}
                onFindSimilarJobs={() => {
                  const job = canvas.state.job;
                  const role = job?.title ?? profile?.currentRole ?? 'engineer';
                  const skills = (profile?.skills ?? []).slice(0, 3).join(' ');
                  const query = [role, skills].filter(Boolean).join(' ');
                  canvas.dispatch({ type: 'SET_MODE', mode: 'LIST' });
                  void fetchJobResults(query, undefined, { openPanel: true });
                }}
                onInterviewPrep={() => {
                  window.history.pushState({}, '', '/interview-prep');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                onTrackApplications={() => {
                  window.history.pushState({}, '', '/my-activities');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }}
                onCoverLetter={() => {
                  const job = canvas.state.job;
                  if (!job || !session) return;
                  const replyId = crypto.randomUUID();
                  const reply = {
                    id: replyId, sessionId: session.id, role: 'assistant' as const,
                    content: draftingCoverLetterMessage(job.title, job.company),
                    createdAt: new Date().toISOString(),
                  };
                  setMessages(prev => [...prev, reply]);
                  void handleLetterAction('cover', { jobTitle: job.title, company: job.company }, replyId);
                }}
                onApplicationNote={() => {
                  const job = canvas.state.job;
                  if (!job || !session) return;
                  const replyId = crypto.randomUUID();
                  const reply = {
                    id: replyId, sessionId: session.id, role: 'assistant' as const,
                    content: draftingApplicationNoteMessage(job.title, job.company),
                    createdAt: new Date().toISOString(),
                  };
                  setMessages(prev => [...prev, reply]);
                  void handleLetterAction('application', { jobTitle: job.title, company: job.company }, replyId);
                }}
              />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Job detail drawer opened from chat job links */}
      <JobDetailDrawer
        job={chatSelectedJob}
        onClose={() => setChatSelectedJob(null)}
        onApply={(j) => {
          setChatSelectedJob(null);
          const raw = (jobResults ?? []).find((r) => r.id === j.id);
          if (raw) handleAgentOptimizeFromJobs(raw);
        }}
        onView={() => {}}
        onToggleSave={() => {}}
        isSaved={false}
        allJobs={(jobResults ?? []).map((a) => {
          const rec = jobRecs.get(a.id);
          return {
            id: a.id,
            source: a.source_channel === 'peerlist' ? 'peerlist' : 'direct',
            source_channel: a.source_channel ?? undefined,
            title: a.title,
            company: a.company,
            company_logo_url: a.company_logo_url ?? undefined,
            location: a.location ?? '',
            url: a.url ?? '#',
            created: a.posted_at ?? new Date().toISOString(),
            description: a.description_md ?? '',
            skills: a.skills ?? undefined,
            salary_min: a.salary_min ?? undefined,
            salary_max: a.salary_max ?? undefined,
            salary_currency: a.salary_currency ?? 'INR',
            job_type: a.job_type ?? undefined,
            experience_level: a.experience_level ?? undefined,
            remote: a.remote ?? undefined,
            posted_at: a.posted_at ?? undefined,
            matchScore: rec?.score,
          } as JobCardType;
        })}
        onSelectSimilar={(j) => { if (j) { setChatSelectedJob(j); setFocusedJobId(j.id); } }}
      />

      {/* ── Credit gate paywall modal ── */}
      <CreditGateModal
        isOpen={creditGate.open}
        onClose={closeCreditGate}
        creditsRemaining={creditSystem.credits ?? 0}
        creditsRequired={creditGate.cost}
        actionLabel={creditGate.action}
        onGetCredits={handleGetCredits}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// JobMiniCards — compact job suggestion cards rendered inside chat messages
// ─────────────────────────────────────────────────────────────────────────────

interface JobMiniCardData {
  id: string;
  title: string;
  company: string;
  company_logo_url?: string | null;
  location?: string | null;
  score?: number;
  source_channel?: string | null;
  /** 'optimized' | 'applied' | null — from useAppliedJobsMap */
  workflowStatus?: 'optimized' | 'applied' | null;
}

const JobMiniCards: React.FC<{
  jobs: JobMiniCardData[];
  onOptimize?: (jobId: string) => void;
}> = ({ jobs, onOptimize }) => {
  return (
    <div className="mt-3 flex flex-col gap-2">
      {jobs.map((job) => {
        const scoreColor = job.score != null
          ? job.score >= 70 ? '#10b981' : job.score >= 50 ? '#3b82f6' : job.score >= 35 ? '#f59e0b' : '#ef4444'
          : null;
        const srcLabel = job.source_channel
          ? ({ peerlist: 'Peerlist', linkedin: 'LinkedIn', naukri: 'Naukri' }[job.source_channel] ?? null)
          : null;
        return (
          <button
            key={job.id}
            type="button"
            onClick={() => {
              window.dispatchEvent(new CustomEvent('agent:open-job', { detail: { jobId: job.id } }));
            }}
            className="group/card flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all hover:shadow-md cursor-pointer w-full"
            style={{
              background: 'var(--ds-bg-card, #fff)',
              borderColor: 'var(--ds-border, #e5e7eb)',
            }}
          >
            <div className="shrink-0">
              <CompanyLogo
                url={job.company_logo_url}
                company={job.company}
                imgClassName="h-9 w-9 rounded-lg object-contain"
                imgStyle={{ background: '#f8f9fa' }}
                fallbackClassName="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold"
                fallbackStyle={{
                  background: 'var(--ds-brand-soft-2, #eef2ff)',
                  color: 'var(--ds-brand, #4f46e5)',
                }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="text-[14px] font-semibold truncate"
                  style={{ color: 'var(--ds-text-strong)' }}
                >
                  {job.title}
                </span>
                {srcLabel && (
                  <span
                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
                    style={{
                      background: 'var(--ds-brand-soft-2, #eef2ff)',
                      color: 'var(--ds-brand, #4f46e5)',
                    }}
                  >
                    {srcLabel}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-[12px]" style={{ color: 'var(--ds-text-muted)' }}>
                <span className="truncate">{job.company}</span>
                {job.location && (
                  <>
                    <span className="shrink-0">·</span>
                    <span className="truncate">{job.location.split(',')[0]}</span>
                  </>
                )}
                {job.workflowStatus === 'applied' && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold"
                    style={{ background: 'rgba(16,185,129,0.12)', color: '#059669' }}
                  >
                    Applied
                  </span>
                )}
                {job.workflowStatus === 'optimized' && (
                  <span
                    className="shrink-0 inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-bold"
                    style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}
                  >
                    Optimized
                  </span>
                )}
              </div>
            </div>
            {job.score != null && (
              <div className="shrink-0 flex flex-col items-center">
                <span
                  className="text-[16px] font-bold leading-tight"
                  style={{ color: scoreColor ?? undefined }}
                >
                  {Math.round(job.score)}%
                </span>
                <span className="text-[10px]" style={{ color: 'var(--ds-text-muted)' }}>match</span>
              </div>
            )}
            {onOptimize && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onOptimize(job.id);
                }}
                className="shrink-0 rounded-lg px-2.5 py-1.5 text-[12px] font-semibold transition-colors opacity-0 group-hover/card:opacity-100"
                style={{
                  background: 'var(--ds-brand, #4f46e5)',
                  color: '#fff',
                }}
              >
                Optimize
              </button>
            )}
          </button>
        );
      })}
    </div>
  );
};

interface EmptyStateProps {
  firstName: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ firstName }) => {
  const { t } = useTranslation();
  return (
  <div className="relative mb-3 flex w-full flex-col items-center px-2 text-center sm:mb-6">
    <div
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-1/2 -z-10 -translate-x-1/2 -translate-y-1/2"
    >
      <div
        className="h-28 w-48 rounded-full blur-3xl sm:h-48 sm:w-80"
        style={{ background: 'radial-gradient(circle, var(--ds-brand-soft-2) 0%, transparent 70%)' }}
      />
    </div>
    <h1
      className="relative text-xl font-semibold tracking-tight sm:text-2xl lg:text-[1.65rem]"
      style={{ color: 'var(--ds-text-strong)' }}
    >
      {t('agent.empty.greeting', { name: firstName, defaultValue: `Hi ${firstName}, what can I help you with today?` })}
    </h1>
    <p className="relative mt-2 max-w-[480px] t-body" style={{ color: 'var(--ds-text-muted)' }}>
      {t('agent.empty.subtitle', { defaultValue: 'Search jobs, optimize your resume, or prep for interviews.' })}
    </p>
  </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
// WebSearchPanel — right panel showing web search links + optional DDG results
// ─────────────────────────────────────────────────────────────────────────────

interface WebSearchPanelProps {
  query:     string;
  results:   WebSearchResult[];
  isLoading: boolean;
  onClose:   () => void;
}

/** Generate targeted search engine links for the query. */
function buildSearchLinks(query: string) {
  const q = encodeURIComponent(query);
  const isJobQuery = /\b(job|jobs|role|roles|opening|position|developer|engineer|designer|manager|analyst|intern|hiring|salary|career)\b/i.test(query);

  const links = [
    {
      name:   'Google',
      icon:   '🔍',
      url:    `https://www.google.com/search?q=${q}`,
      color:  '#4285F4',
    },
    ...(isJobQuery ? [
      {
        name:  'LinkedIn Jobs',
        icon:  '💼',
        url:   `https://www.linkedin.com/jobs/search/?keywords=${q}`,
        color: '#0077B5',
      },
      {
        name:  'Naukri',
        icon:  '🇮🇳',
        url:   `https://www.naukri.com/${query.toLowerCase().replace(/\s+/g,'-')}-jobs`,
        color: '#d97706',
      },
      {
        name:  'Indeed',
        icon:  '📋',
        url:   `https://in.indeed.com/jobs?q=${q}`,
        color: '#2164F3',
      },
      {
        name:  'Peerlist Jobs',
        icon:  '🟣',
        url:   `https://peerlist.io/jobs?search=${q}`,
        color: 'var(--ds-brand)',
      },
    ] : [
      {
        name:  'DuckDuckGo',
        icon:  '🦆',
        url:   `https://duckduckgo.com/?q=${q}`,
        color: '#DE5833',
      },
      {
        name:  'Bing',
        icon:  '🔷',
        url:   `https://www.bing.com/search?q=${q}`,
        color: '#00809D',
      },
    ]),
  ];
  return links;
}

const WebSearchPanel: React.FC<WebSearchPanelProps> = ({ query, results, isLoading, onClose }) => {
  const [copied, setCopied] = React.useState<string | null>(null);
  const searchLinks = React.useMemo(() => buildSearchLinks(query), [query]);

  const handleCopy = async (text: string, key: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(null), 1_500); } catch { /* ignore */ }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--ds-bg-card)' }}>
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: 'var(--ds-border)', background: 'var(--ds-bg-tinted)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="w-4 h-4 shrink-0" style={{ color: 'var(--ds-brand)' }} />
          <span className="t-card-title shrink-0">
            Web Search
          </span>
          {query && (
            <span className="t-meta t-muted truncate">
              — {query}
            </span>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Close web search"
          className="ds-btn ds-btn-ghost p-1.5 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Content — always scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col gap-5 scrollbar-theme">

        {/* ── Search Engine Quick Links — always shown ── */}
        <div>
          <p className="t-label mb-2.5">
            Open in browser
          </p>
          <div className="flex flex-col gap-2">
            {searchLinks.map((link) => (
              <a
                key={link.name}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all hover:opacity-90 active:scale-[0.98]"
                style={{
                  background:     'var(--ds-bg-tinted)',
                  border:         '1px solid var(--ds-border)',
                  textDecoration: 'none',
                }}
              >
                <span className="text-lg shrink-0">{link.icon}</span>
                <div className="flex-1 min-w-0">
                  <span className="t-meta font-medium" style={{ color: link.color }}>
                    {link.name}
                  </span>
                  <p className="t-tag t-muted truncate mt-0.5">
                    {link.url.slice(0, 55)}…
                  </p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 shrink-0 t-muted" />
              </a>
            ))}
          </div>
        </div>

        {/* ── Loading skeleton ── */}
        {isLoading && (
          <div>
            <p className="t-label mb-2.5">
              Fetching results…
            </p>
            <div className="flex flex-col gap-3 animate-pulse">
              {[1,2,3].map((i) => (
                <div key={i} className="ds-card-sm p-4">
                  <div className="h-3 rounded w-3/4 mb-2" style={{ background: 'var(--ds-bg-tinted)' }} />
                  <div className="h-2 rounded w-full mb-1" style={{ background: 'var(--ds-bg-tinted)' }} />
                  <div className="h-2 rounded w-4/5" style={{ background: 'var(--ds-bg-tinted)' }} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── DuckDuckGo results (bonus — only when available) ── */}
        {!isLoading && results.length > 0 && (
          <div>
            <p className="t-label mb-2.5">
              Instant results
            </p>
            <div className="flex flex-col gap-3">
              {results.map((r, i) => (
                <div
                  key={i}
                  className="group ds-card-sm p-4 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2 mb-1.5">
                    <a
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="t-meta font-semibold leading-snug hover:underline"
                      style={{ color: 'var(--ds-brand)' }}
                    >
                      {r.title}
                    </a>
                    <button
                      type="button"
                      onClick={() => handleCopy(r.url, `url-${i}`)}
                      title="Copy link"
                      className="ds-btn ds-btn-ghost p-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    >
                      {copied === `url-${i}`
                        ? <Check className="w-3 h-3" style={{ color: 'var(--ds-ok)' }} />
                        : <Copy className="w-3 h-3" />}
                    </button>
                  </div>
                  <p className="t-tag t-muted mb-1.5 truncate">
                    {r.url}
                  </p>
                  <p className="t-meta leading-relaxed">
                    {r.snippet}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// SourceSelector — "Searching over: [Internal DB ▼]" Wrangle-style dropdown
// ─────────────────────────────────────────────────────────────────────────────

/** Shown when Ollama is offline — only models we actively support locally. */
const FALLBACK_OLLAMA_MODELS = ['qwen2.5:latest'] as const;

const USE_OLLAMA_MODELS =
  import.meta.env.DEV && (import.meta.env.VITE_USE_OLLAMA as string | undefined) === 'true';

const CLOUD_MODEL_OPTIONS = [
  {
    id:    `${GEMINI_MODEL_PREFIX}gemini-3.6-flash`,
    name:  'Gemini Flash',
    desc:  'Default · fast · Gemini cloud',
    color: '#4285f4',
  },
  {
    id:    `${WEB_MODEL_PREFIX}gemini-3.6-flash`,
    name:  'Gemini + web',
    desc:  'Live web context + citations',
    color: 'var(--ds-brand)',
  },
] as const;

function formatLocalModelLabel(model: string): { title: string; subtitle: string } {
  const [shortName, tag = 'latest'] = model.split(':');
  const title = tag === 'latest' ? shortName : `${shortName} (${tag})`;
  return { title, subtitle: `${tag} · local only` };
}

function sortLocalModels(models: string[]): string[] {
  return [...models].sort((a, b) => {
    const [aName, aTag = ''] = a.split(':');
    const [bName, bTag = ''] = b.split(':');
    const byName = aName.localeCompare(bName);
    return byName !== 0 ? byName : aTag.localeCompare(bTag);
  });
}

/** Low-profile inline pill — plain text + chevron when idle, no chip background. */
function inlinePill(active = false): string {
  return ['agent-composer-toolbar__pill', active ? 'is-active' : ''].filter(Boolean).join(' ');
}

interface SourceSelectorProps {
  value:    'internal' | 'external';
  onChange: (v: 'internal' | 'external') => void;
  credCount: number;
  deepSearch: boolean;
  onToggleDeepSearch: () => void;
  /** Resume file upload handler — renders an attach pill when provided */
  onFileUpload?: (file: File) => void;
  /** Model picker + backend status — owner admin only */
  showAdminControls?: boolean;
  activeModel?: { label: string; backend: 'ollama' | 'gemini' | 'groq' } | null;
  availableModels?: string[];
  selectedModel?: string | null;
  onModelChange?: (model: string) => void;
  /** null = probe still in progress; true = Ollama running; false = using cloud */
  ollamaReachable?: boolean | null;
  /** When true, hides the Internal/External source dropdown (unified search) */
  hideSourceDropdown?: boolean;
}

const ATTACH_ACCEPTED = '.pdf,.doc,.docx';
const ATTACH_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];

const SourceSelector: React.FC<SourceSelectorProps> = ({
  value, onChange, credCount, deepSearch, onToggleDeepSearch,
  onFileUpload,
  showAdminControls = false,
  activeModel,
  availableModels = [], selectedModel, onModelChange, ollamaReachable,
  hideSourceDropdown = false,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [modelOpen, setModelOpen] = React.useState(false);
  const sourceBtnRef = React.useRef<HTMLButtonElement>(null);
  const sourceMenuRef = React.useRef<HTMLDivElement>(null);
  const modelBtnRef = React.useRef<HTMLButtonElement>(null);
  const modelMenuRef = React.useRef<HTMLDivElement>(null);
  const [sourceMenuPos, setSourceMenuPos] = React.useState<{
    bottom: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [modelMenuPos, setModelMenuPos] = React.useState<{
    bottom: number;
    right: number;
    width: number;
    maxHeight: number;
  } | null>(null);

  const updateSourceMenuPos = React.useCallback(() => {
    const btn = sourceBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = Math.min(224, window.innerWidth - 32);
    const left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16));
    setSourceMenuPos({
      bottom: window.innerHeight - rect.top + 8,
      left,
      width,
      maxHeight: Math.max(120, rect.top - 16),
    });
  }, []);

  const updateModelMenuPos = React.useCallback(() => {
    const btn = modelBtnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setModelMenuPos({
      bottom: window.innerHeight - rect.top + 8,
      right:  window.innerWidth - rect.right,
      width:  Math.min(280, window.innerWidth - 32),
      maxHeight: Math.max(120, rect.top - 16),
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setSourceMenuPos(null);
      return;
    }
    updateSourceMenuPos();
    window.addEventListener('resize', updateSourceMenuPos);
    window.addEventListener('scroll', updateSourceMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateSourceMenuPos);
      window.removeEventListener('scroll', updateSourceMenuPos, true);
    };
  }, [open, updateSourceMenuPos]);

  React.useLayoutEffect(() => {
    if (!modelOpen) {
      setModelMenuPos(null);
      return;
    }
    updateModelMenuPos();
    window.addEventListener('resize', updateModelMenuPos);
    window.addEventListener('scroll', updateModelMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateModelMenuPos);
      window.removeEventListener('scroll', updateModelMenuPos, true);
    };
  }, [modelOpen, updateModelMenuPos]);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (sourceBtnRef.current?.contains(t)) return;
      if (sourceMenuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  React.useEffect(() => {
    if (!modelOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (modelBtnRef.current?.contains(t)) return;
      if (modelMenuRef.current?.contains(t)) return;
      setModelOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [modelOpen]);

  React.useEffect(() => {
    if (!open && !modelOpen) return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open, modelOpen]);

  const OPTIONS = React.useMemo(() => [
    {
      value: 'internal' as const,
      icon: Database,
      label: t('agent.composer.sourceInternal', 'Internal DB'),
      desc: t('agent.composer.sourceInternalDesc', 'AI-ranked from our curated job database'),
    },
    {
      value: 'external' as const,
      icon: Globe,
      label: t('agent.composer.sourceExternal', 'External'),
      desc: credCount > 0
        ? t('agent.composer.sourceExternalDescCredentials', {
            defaultValue: '{{count}} credentials active · Refresh saves to DB',
            count: credCount,
          })
        : t('agent.composer.sourceExternalDescLive', 'Live LinkedIn/Naukri search · Refresh saves to DB'),
    },
  ], [credCount, t]);

  const current = OPTIONS.find((o) => o.value === value)!;
  const Icon = current.icon;
  const localModels = USE_OLLAMA_MODELS
    ? sortLocalModels(availableModels.length > 0 ? availableModels : [...FALLBACK_OLLAMA_MODELS])
    : [];
  const activeModelId = selectedModel ?? DEFAULT_AGENT_CLOUD_MODEL;

  return (
    <div className="agent-source-toolbar flex w-full min-w-0 flex-nowrap items-center gap-1.5 overflow-x-auto overscroll-x-contain pb-0.5 sm:flex-wrap sm:items-center sm:justify-between sm:overflow-visible sm:gap-x-2 sm:gap-y-1.5">
        {/* Deep Search toggle */}
        <button
          type="button"
          onClick={onToggleDeepSearch}
          title={t('agent.composer.deepSearchTitle', 'Deep Search: Triggers a live Peerlist ingest + broader multi-field search')}
          className={inlinePill(deepSearch)}
        >
          <Globe className="agent-composer-toolbar__icon h-3.5 w-3.5 shrink-0" />
          <span className="hidden min-[400px]:inline">{t('agent.composer.deepSearch', 'Deep Search')}</span>
          <span className="min-[400px]:hidden">{t('agent.composer.deepSearchShort', 'Deep')}</span>
          {!deepSearch && (
            <span className="agent-composer-toolbar__badge">{t('agent.composer.deepSearchNew', 'new')}</span>
          )}
        </button>

        {/* Attach resume */}
        {onFileUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ATTACH_ACCEPTED}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (e.target) e.target.value = '';
                if (!file) return;
                if (!ATTACH_MIME_TYPES.includes(file.type)) return;
                if (file.size > 10 * 1024 * 1024) { alert('File must be under 10 MB.'); return; }
                onFileUpload(file);
              }}
              className="hidden"
              aria-hidden
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              title="Upload resume (PDF, DOCX)"
              className={inlinePill(false)}
            >
              <Paperclip className="agent-composer-toolbar__icon h-3.5 w-3.5 shrink-0" />
              <span className="hidden min-[400px]:inline">Attach Resume</span>
              <span className="min-[400px]:hidden">Attach</span>
            </button>
          </>
        )}

      {/* Non-admin: branded label only (no provider / model list) */}
      {!showAdminControls && (
        <div
          className="relative flex shrink-0 items-center sm:ml-auto"
          title="Responses use GoGrabJob secure cloud AI optimized for careers"
        >
          <span className={`${inlinePill(false)} cursor-default`} aria-label="AI model">
            <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-blue-400" />
            <Sparkles className="agent-composer-toolbar__icon h-3.5 w-3.5 shrink-0" />
            <span>{formatPublicAgentModelLabel(activeModelId)}</span>
          </span>
        </div>
      )}

      {/* Model picker — owner admin only */}
      {showAdminControls && (
      <div className="relative shrink-0 sm:ml-auto">
        <button
          ref={modelBtnRef}
          type="button"
          onClick={() => { setOpen(false); setModelOpen((v) => !v); }}
          title="Switch AI model — click to see status"
          aria-expanded={modelOpen}
          aria-haspopup="listbox"
          className={inlinePill(modelOpen)}
        >
          {/* Live backend status dot */}
          {(() => {
            const isCloud = isCloudGeminiModel(activeModelId) || activeModelId.startsWith(WEB_MODEL_PREFIX);
            if (isCloud) {
              return <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-blue-400" title="Using Gemini cloud" />;
            }
            if (ollamaReachable === null) {
              return <span className="w-1.5 h-1.5 rounded-full shrink-0 animate-pulse bg-slate-400" title="Checking Ollama…" />;
            }
            if (ollamaReachable === 'cors') {
              return <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-orange-500" title="Ollama running but CORS not configured — set OLLAMA_ORIGINS=* and restart ollama serve" />;
            }
            const isPulled = ollamaReachable === true && (
              availableModels.includes(activeModelId) ||
              availableModels.some((n) => n.startsWith(activeModelId.split(':')[0]))
            );
            if (isPulled) {
              return <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-emerald-500" title={`Ollama running · ${activeModelId} is loaded`} />;
            }
            if (ollamaReachable === true) {
              return <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-amber-400" title={`Ollama running but ${activeModelId} is not pulled — will use best available`} />;
            }
            return <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-red-400" title="Ollama offline — routing to Gemini cloud" />;
          })()}
          {(() => {
            const m = activeModelId;
            const isWebModel   = m.startsWith(WEB_MODEL_PREFIX);
            const isCloudModel = isCloudGeminiModel(m);
            const modelName = isWebModel
              ? `${formatAdminCloudModelShort(m)} + web`
              : isCloudModel
                ? formatAdminCloudModelShort(m)
                : formatLocalModelLabel(m).title;
            return (
                <span className="agent-composer-toolbar__mono max-w-[120px] truncate">{modelName}</span>
            );
          })()}
          <ChevronDown className="agent-composer-toolbar__chevron h-3 w-3 shrink-0" />
        </button>

        {modelOpen && modelMenuPos && createPortal(
          <>
            <button
              type="button"
              className="agent-composer-menu__backdrop fixed inset-0 z-[9998] cursor-pointer border-0 bg-black/25 p-0 touch-manipulation sm:bg-transparent sm:pointer-events-none"
              aria-label="Close menu"
              onClick={() => setModelOpen(false)}
            />
            <div
              ref={modelMenuRef}
              className="agent-composer-menu ds-card-pop fixed z-[9999] overflow-y-auto !p-1.5 shadow-lg"
              style={{
                bottom: modelMenuPos.bottom,
                right:  modelMenuPos.right,
                width:  modelMenuPos.width,
                maxHeight: modelMenuPos.maxHeight,
              }}
              role="listbox"
            >
            {/* Live backend status banner */}
            {(() => {
              const isCloudSelected = isCloudGeminiModel(activeModelId) || activeModelId.startsWith(WEB_MODEL_PREFIX);
              const isPulled = ollamaReachable === true && (
                availableModels.includes(activeModelId) ||
                availableModels.some((n) => n.startsWith(activeModelId.split(':')[0]))
              );
              let bg = 'rgba(251,191,36,0.12)';
              let dot = <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />;
              let title = <><span className="font-semibold text-amber-700 dark:text-amber-400">Ollama offline</span><span className="text-slate-500 dark:text-slate-400 ml-1">· routing to Gemini (ai-companion)</span></>;

              if (isCloudSelected) {
                bg = 'rgba(96,165,250,0.1)';
                dot = <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />;
                title = <><span className="font-semibold text-blue-600 dark:text-blue-400">Gemini</span><span className="text-slate-500 dark:text-slate-400 ml-1">· ai-companion edge</span></>;
              } else if (ollamaReachable === 'cors') {
                bg = 'rgba(249,115,22,0.1)';
                dot = <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />;
                title = (
                  <div>
                    <span className="font-semibold text-orange-700 dark:text-orange-400">CORS not configured</span>
                    <span className="text-slate-500 dark:text-slate-400 ml-1">· Ollama is running but browser is blocked</span>
                    <div className="mt-1 font-mono text-[10px] text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded px-2 py-1">
                      $env:OLLAMA_ORIGINS="*"<br/>ollama serve
                    </div>
                  </div>
                );
              } else if (ollamaReachable === null) {
                bg = 'var(--ds-bg-tinted)';
                dot = <span className="w-2 h-2 rounded-full bg-slate-400 animate-pulse shrink-0" />;
                title = <span className="font-medium" style={{ color: 'var(--ds-text-body)' }}>Checking Ollama…</span>;
              } else if (isPulled) {
                bg = 'var(--ds-ok-soft, #d1fae5)';
                dot = <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />;
                title = <><span className="font-semibold text-emerald-700 dark:text-emerald-400">Ollama running</span><span className="text-slate-500 dark:text-slate-400 ml-1">· <span className="font-mono">{activeModelId}</span> is loaded &amp; active</span></>;
              } else if (ollamaReachable === true) {
                bg = 'rgba(251,191,36,0.12)';
                dot = <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />;
                title = <><span className="font-semibold text-amber-700 dark:text-amber-400">Model not pulled</span><span className="text-slate-500 dark:text-slate-400 ml-1">· Ollama will use best available</span></>;
              }

              return (
                <div className="mx-1 mb-1.5 rounded-lg px-3 py-2 text-[11px] flex items-center gap-2" style={{ background: bg }}>
                  {dot}
                  <div>{title}</div>
                </div>
              );
            })()}
            {localModels.length > 0 && (
            <>
            <div className="sticky top-0 z-10 border-b bg-[var(--ds-bg-card)] px-3 py-1.5" style={{ borderColor: 'var(--ds-border)' }}>
              <span className="t-label">Local models (Ollama)</span>
            </div>
            {localModels.map((model) => {
              const isActive = activeModelId === model;
              const { title, subtitle } = formatLocalModelLabel(model);
              const isRoutingHere = isActive && ollamaReachable === true;
              return (
                <button
                  key={model}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onModelChange?.(model); setModelOpen(false); }}
                  className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-[var(--ds-bg-tinted)]"
                  style={{ background: isActive ? 'var(--ds-brand-soft)' : 'transparent' }}
                >
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: isRoutingHere ? 'var(--ds-ok)' : isActive ? 'var(--ds-border)' : 'var(--ds-border)' }}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="t-meta font-semibold block">{title}</span>
                    <span className="t-tag t-muted">
                      {subtitle}
                      {isActive && ollamaReachable === false && (
                        <span className="ml-1 text-amber-500 font-medium">· offline, using Google AI instead</span>
                      )}
                    </span>
                  </span>
                  {isRoutingHere && (
                    <span className="ds-chip ds-chip-ok t-tag shrink-0">running</span>
                  )}
                  {isActive && !isRoutingHere && ollamaReachable !== null && (
                    <span className="ds-chip t-tag shrink-0" style={{ background: 'var(--ds-warning-soft, rgba(251,191,36,0.15))', color: 'var(--ds-warning, #b45309)' }}>selected</span>
                  )}
                </button>
              );
            })}
            </>)}

            <div className="mx-3 my-1 border-t" style={{ borderColor: 'var(--ds-border)' }} />
            <div className="sticky top-0 bg-[var(--ds-bg-card)] px-3 py-1.5 t-label">
              Cloud
            </div>
            {CLOUD_MODEL_OPTIONS.map((opt) => {
              const isActive = activeModelId === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { onModelChange?.(opt.id); setModelOpen(false); }}
                  className="w-full flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-[var(--ds-bg-tinted)]"
                  style={{ background: isActive ? 'var(--ds-brand-soft)' : 'transparent' }}
                >
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: isActive ? opt.color : 'var(--ds-border)' }} />
                  <span className="flex-1 min-w-0">
                    <span className="t-meta font-semibold block">{opt.name}</span>
                    <span className="t-tag t-muted">{opt.desc}</span>
                  </span>
                  {isActive && (
                    <span className="ds-chip ds-chip-brand t-tag shrink-0">active</span>
                  )}
                </button>
              );
            })}
          </div>
          </>,
          document.body,
        )}
      </div>
      )}

    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// RoleTemplateChips — Wrangle image 1 row of quick-start role chips
// ─────────────────────────────────────────────────────────────────────────────
const ROLE_TEMPLATES = [
  { label: 'Software Engineer',   prompt: 'Find Software Engineer jobs matching my profile' },
  { label: 'Product Designer',    prompt: 'Find Product Designer roles for me' },
  { label: 'Product Manager',     prompt: 'Show me Product Manager openings' },
  { label: 'Data Scientist',      prompt: 'Find Data Scientist jobs in my field' },
  { label: 'Marketing Lead',      prompt: 'Search for Marketing Lead roles' },
  { label: 'Full Stack Dev',      prompt: 'Find Full Stack Developer jobs' },
  { label: 'DevOps / SRE',        prompt: 'Show me DevOps and SRE openings' },
];

interface RoleTemplateChipsProps {
  onSelect: (prompt: string) => void;
}

const RoleTemplateChips: React.FC<RoleTemplateChipsProps> = ({ onSelect }) => (
  <div className="flex flex-wrap justify-center gap-2 mt-2">
    {ROLE_TEMPLATES.map((t) => (
      <button
        key={t.label}
        type="button"
        onClick={() => onSelect(t.prompt)}
        className="ds-chip ds-chip-button ds-chip-brand transition-all active:scale-[0.97]"
      >
        <Sparkles className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
        {t.label}
      </button>
    ))}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// OllamaRoutingStrip — shows backend status + live "Test" button
// ─────────────────────────────────────────────────────────────────────────────

interface OllamaRoutingStripProps {
  ollamaReachable: boolean | 'cors' | null;
  selectedModel:   string;
  availableModels: string[];
  onRetest: (result: import('./agentSession').OllamaProbeResult) => void;
}

function OllamaRoutingStrip({ ollamaReachable, selectedModel, availableModels, onRetest }: OllamaRoutingStripProps) {
  const { t } = useTranslation();
  const [testing, setTesting] = React.useState(false);
  const [testMsg, setTestMsg] = React.useState<string | null>(null);

  const handleTest = async () => {
    setTesting(true);
    setTestMsg(null);
    try {
      const result = await probeOllama();
      onRetest(result);
      markOllamaAvailable(result.status === 'ok');
      if (result.status === 'ok') {
        setTestMsg(`✓ Connected · ${result.models.length} model${result.models.length !== 1 ? 's' : ''} available`);
      } else if (result.status === 'cors_blocked') {
        setTestMsg('✗ CORS blocked · run: [System.Environment]::SetEnvironmentVariable("OLLAMA_ORIGINS","*","User") then restart ollama serve');
      } else {
        setTestMsg('✗ Offline · run: ollama serve');
      }
    } finally {
      setTesting(false);
    }
  };

  const m = selectedModel;
  const isCloud = isCloudGeminiModel(m) || m.startsWith(WEB_MODEL_PREFIX);
  const isWebSearch = m.startsWith(WEB_MODEL_PREFIX);

  let dotColor = '#ef4444';
  let label = 'Ollama offline · routing to GoGrabJob AI';
  let sublabel = 'Gemini · ai-companion edge';
  let isWarning = false;

  if (isCloud) {
    dotColor = '#60a5fa';
    const modelName = m.startsWith(GEMINI_MODEL_PREFIX)
      ? m.slice(GEMINI_MODEL_PREFIX.length)
      : m.startsWith(CLOUD_MODEL_PREFIX)
        ? m.slice(CLOUD_MODEL_PREFIX.length)
        : m.slice(WEB_MODEL_PREFIX.length);
    label = isWebSearch
      ? t('agent.composer.groqWebSearch', { defaultValue: 'Google AI · live web search', model: modelName })
      : t('agent.composer.groqCloudModel', { defaultValue: 'Google AI · {{model}}', model: modelName });
    sublabel = isWebSearch
      ? t('agent.composer.realtimeWebContext', 'real-time web context')
      : t('agent.composer.noInternetFaster', 'no internet — faster');
  } else if (ollamaReachable === null) {
    dotColor = '#94a3b8';
    label = 'Checking Ollama…';
    sublabel = '';
  } else if (ollamaReachable === 'cors') {
    dotColor = '#f97316';
    isWarning = true;
    label = 'Ollama running but CORS not set — calls going to GoGrabJob AI';
    sublabel = 'Fix: [System.Environment]::SetEnvironmentVariable("OLLAMA_ORIGINS","*","User") then restart ollama serve';
  } else if (ollamaReachable === true) {
    const pulled = availableModels.includes(m) || availableModels.some(n => n.startsWith(m.split(':')[0]));
    const resolvedModel = pulled ? m : (availableModels[0] ?? m);
    dotColor = '#10b981';
    label = `Ollama · ${resolvedModel}`;
    sublabel = pulled
      ? `${import.meta.env.VITE_OLLAMA_URL ?? 'localhost:11434'} · calls go to your local GPU`
      : `${m} not pulled — using ${resolvedModel} instead`;
  }

  return (
    <div className="agent-ollama-strip__inner flex flex-col gap-0.5 px-3 py-2">
      <div
        className={`flex flex-wrap items-center gap-1.5 ${isWarning ? 'rounded-md px-2 py-1' : ''}`}
        style={isWarning ? { background: 'var(--ds-warn-bg)' } : undefined}
        aria-label="Active AI backend"
      >
        {/* animated pulse when checking */}
        <span
          className={`w-1.5 h-1.5 rounded-full shrink-0 ${ollamaReachable === null || testing ? 'animate-pulse' : ''}`}
          style={{ background: testing ? '#94a3b8' : dotColor }}
        />
        <span className="agent-ollama-strip__label font-mono" style={{ color: isWarning ? '#c2410c' : undefined }}>
          {testing ? 'Testing Ollama connection…' : label}
        </span>
        {sublabel && !testing && (
          <span
            className="agent-ollama-strip__sublabel"
            style={{ color: isWarning ? '#ea580c' : undefined, fontFamily: isWarning ? 'monospace' : undefined }}
          >
            · {sublabel}
          </span>
        )}

        {/* Test / Retry button — only show when not cloud and not currently checking */}
        {!isCloud && ollamaReachable !== null && (
          <button
            type="button"
            onClick={() => void handleTest()}
            disabled={testing}
            className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-40"
            style={{
              background: ollamaReachable === true ? 'var(--ds-bg-tinted)' : 'rgba(249,115,22,0.12)',
              color: ollamaReachable === true ? 'var(--ds-text-muted)' : '#c2410c',
              border: '1px solid var(--ds-border)',
            }}
            title="Re-test Ollama connection"
          >
            {testing ? '…' : ollamaReachable === true ? 'Re-test' : 'Test connection'}
          </button>
        )}
      </div>

      {/* One-time result message after test */}
      {testMsg && (
        <p
          className="font-mono text-[10px] leading-tight px-0.5"
          style={{ color: testMsg.startsWith('✓') ? '#059669' : '#c2410c' }}
        >
          {testMsg}
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Wrapped export with CanvasProvider
// ─────────────────────────────────────────────────────────────────────────────

function AIAgentPanelWithCanvas(props: AIAgentPanelProps) {
  return (
    <CanvasProvider>
      <AIAgentPanel {...props} />
    </CanvasProvider>
  );
}

export default AIAgentPanelWithCanvas;
